import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import WhatsappAutomation from '../components/WhatsappAutomation';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from '../components/ui/Select';
import { Switch } from '../components/ui/Switch';
import { Separator } from '../components/ui/Separator';
import { Badge } from '../components/ui/Badge';
import { 
  Settings as SettingsIcon, 
  User, 
  Palette, 
  Bell, 
  Key, 
  Save, 
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Check,
  CreditCard,
  Shield,
  AlertCircle,
  CheckCircle,
  Trash2,
  Clock,
  Play,
  Server,
  Zap,
  Plus,
  X,
  TestTube,
  CheckCircle2,
  XCircle,
  Ticket,
  Link,
  MessageCircle,
  ShieldAlert,
  Smartphone,
  Trophy,
  ExternalLink,
  Database,
  Coins
} from 'lucide-react';
import ColorPicker from '../components/ui/ColorPicker';
import { simpleApiService } from '../services/simpleApi';
import { apiService } from '../services/api';
import { useFormValidation, cmsUserValidationRules } from '../hooks/useFormValidation';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../hooks/useAuth';
import { redirectToLogin } from '../lib/utils';
import {
  normalizeMercadoPagoPlan,
  type MercadoPagoPlanRow
} from '../utils/mercadoPagoPlans';
import { joinUrlList, splitUrlListInput } from '../utils/streamingUrlList';
import {
  DEFAULT_EDGE_API_PRIMARY
} from '../config/streamingEdgeDefaults';
// import { useColors } from '../contexts/ColorContext';

export type ApkPlaybackMode = 'iptv' | 'edge' | 'p2p';

export function resolveApkPlaybackMode(se?: {
  playbackMode?: string;
  enabled?: boolean;
  p2pEnabled?: boolean;
}): ApkPlaybackMode {
  const m = String(se?.playbackMode || '').toLowerCase();
  if (m === 'iptv' || m === 'edge' || m === 'p2p') return m;
  if (!se?.enabled) return 'iptv';
  if (se?.p2pEnabled) return 'p2p';
  return 'edge';
}

/** Slug CMS: modo + URLs da API Edge. Broker/CDN vêm do painel P2P. */
export function streamingEdgePayloadFromMode(mode: ApkPlaybackMode, edgeUrlsText: string) {
  const edge = splitUrlListInput(edgeUrlsText);
  return {
    playbackMode: mode,
    enabled: mode !== 'iptv',
    edgeApiUrl: mode !== 'iptv' ? edge.primary : '',
    edgeApiUrlFallbacks: mode !== 'iptv' ? edge.fallbacks : '',
    p2pEnabled: mode === 'p2p',
    p2pBrokerUrl: '',
    p2pBrokerUrlFallbacks: ''
  };
}

export function streamingEdgeForForm(se?: {
  playbackMode?: string;
  enabled?: boolean;
  p2pEnabled?: boolean;
  edgeApiUrl?: string;
  edgeApiUrlFallbacks?: string;
}) {
  const base = streamingEdgePayloadFromMode(
    resolveApkPlaybackMode(se),
    joinUrlList(se?.edgeApiUrl, se?.edgeApiUrlFallbacks)
  );
  return {
    ...base,
    edgeApiUrlsList: joinUrlList(se?.edgeApiUrl, se?.edgeApiUrlFallbacks)
  };
}

interface CmsUserData {
  _id: string;
  username: string;
  email: string;
  name: string;
  cmsName: string;
  logoUrl?: string;
  cmsSlug: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  settings: {
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
    theme: string;
    language: string;
  };
  cmsSettings?: {
    theme?: string;
    language?: string;
    customDomain?: string;
    /** API BinStream/U2P (ex.: https://api1.hojevai.com). Vazio = default do APK. */
    binstreamApiUrl?: string;
    bannerUrl?: string;
    /** Imagem de fundo full-screen na home do app Android (opcional). */
    homeBackgroundUrl?: string;
    /** Exposto no JSON público do CMS para o APK (renovação in-app). */
    inAppPaymentEnabled?: boolean;
  };
  appUpdate?: {
    enabled: boolean;
    latestVersionCode: number;
    latestVersionName: string;
    apkDownloadUrl: string;
    updateMessage: string;
    forceUpdate: boolean;
    minSupportedVersion: number;
  };
  inAppNotice?: {
    enabled: boolean;
    id?: string;
    title: string;
    message: string;
    severity: 'info' | 'warning' | 'danger';
    updatedAt?: string;
  };
  gamesOfDay?: {
    enabled: boolean;
    apiKey: string;
    filteredCountries?: string[];
    filteredLeagues?: string[];
    countryId: string;
    countryName: string;
    pageUrl: string;
    /** JSON HTTPS com dicas "onde assistir" (merge no painel → campo broadcastHint no APK) */
    broadcastHintsUrl?: string;
    /** Quando true, a cron diária mapeia automaticamente os canais Xtream para os jogos */
    autoBroadcastChannels?: boolean;
  };
  dpiBypass?: {
    enabled: boolean;
    proxyPort: number;
  };
  streamingEdge?: {
    playbackMode?: ApkPlaybackMode;
    enabled: boolean;
    edgeApiUrl: string;
    edgeApiUrlFallbacks?: string;
    p2pEnabled: boolean;
    p2pBrokerUrl: string;
    p2pBrokerUrlFallbacks?: string;
  };
  appPromotionSettings?: {
    enabled: boolean;
    promotionBox?: {
      title: string;
      price: string;
      priceLabel: string;
      description: string;
      highlightText: string;
      benefits: string[];
      buttonText: string;
      buttonLink: string;
      qrCodeUrl?: string;
      contactTitle?: string;
      phoneNumber?: string;
      whatsappLink?: string;
    };
    supportBox?: {
      enabled: boolean;
      title: string;
      phoneNumber: string;
      whatsappLink: string;
      imageUrl: string;
    };
    startDate?: string;
    endDate?: string;
  };
  tokens?: {
    apiKey?: string;
  };
  mercadoPago?: {
    enabled: boolean;
    publicKey: string;
    accessToken: string;
    webhookUrl: string;
    environment: 'sandbox' | 'production';
    renewalMonthPriceBrl?: number;
    plans?: Array<{ id: string; label: string; months: number; priceBrl: number; active: boolean }>;
    createdAt?: string;
    updatedAt?: string;
  };
  updePix?: {
    enabled: boolean;
    apiKey: string;
    createDepositUrl: string;
    webhookSecret?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  lynx?: {
    enabled: boolean;
    apiKey: string;
    createPixUrl: string;
    webhookSecret?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  creditSales?: {
    enabled: boolean;
    /** priceBrl em cada pacote = preço por crédito (R$); total cobrado = créditos × priceBrl */
    packages: Array<{ id: string; label: string; credits: number; priceBrl: number; active: boolean }>;
  };
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** Resposta da API mascara segredos (toSafeJSON); não preencher o formulário com isso. */
function clearIfMaskedCredential(val: string | undefined): string {
  const s = String(val || '').trim();
  if (!s) return '';
  if (s.includes('…') || s.includes('***')) return '';
  if (/^whsec_\.\.[a-z0-9]{4}$/i.test(s)) return '';
  return s;
}

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cmsUser, setCmsUser] = useState<CmsUserData | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugDebounceTimer, setSlugDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'appearance' | 'notifications' | 'security' | 'payments' | 'trial' | 'whatsapp' | 'appUpdate' | 'appPromotion' | 'inAppNotice' | 'gamesOfDay' | 'system'>('profile');
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isSocio, isMaster } = useAuth();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'payments') {
      setActiveTab('payments');
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [revenuePerUser, setRevenuePerUser] = useState<string>('');
  
  // Estados para inputs de país e liga (Jogos do Dia)
  const [newCountryInput, setNewCountryInput] = useState('');
  const [newLeagueInput, setNewLeagueInput] = useState('');
  const [showCountryInput, setShowCountryInput] = useState(false);
  const [showLeagueInput, setShowLeagueInput] = useState(false);

  /** Curadoria de transmissões (API /api/games-broadcast-map) — apenas admin. */
  type BroadcastGameRow = {
    fixtureId: number | null;
    fixtureDate?: string | null;
    league?: { name?: string } | null;
    teams?: { home?: { name?: string }; away?: { name?: string } } | null;
    broadcastHint?: string;
    streamIds?: number[];
    streamLabels?: string[];
    /** IDs do catálogo P2P/BinStream (opcional; apps antigos ignoram) */
    p2pStreamIds?: number[];
    p2pStreamLabels?: string[];
    notes?: string;
  };
  const [broadcastGames, setBroadcastGames] = useState<BroadcastGameRow[]>([]);
  const [broadcastCacheHint, setBroadcastCacheHint] = useState<string | null>(null);
  const [xtreamServer, setXtreamServer] = useState('');
  const [xtreamUsername, setXtreamUsername] = useState('');
  const [xtreamPassword, setXtreamPassword] = useState('');
  /** Linha BinStream (não use channelsday — isso é só Xtream). */
  const [p2pUsername, setP2pUsername] = useState('');
  const [p2pPassword, setP2pPassword] = useState('');
  const [broadcastBusy, setBroadcastBusy] = useState<
    null | 'today' | 'streams' | 'hints' | 'hintsApply' | 'epg' | 'p2pStreams' | 'p2pHints' | 'p2pHintsApply'
  >(null);
  const [broadcastError, setBroadcastError] = useState<string | null>(null);
  const [xtreamStreamsCount, setXtreamStreamsCount] = useState<number | null>(null);
  const [p2pStreamsCount, setP2pStreamsCount] = useState<number | null>(null);
  /** Rascunhos locais por fixtureId (string) — stream_id IPTV, P2P, rótulos, notas. */
  const [broadcastDrafts, setBroadcastDrafts] = useState<
    Record<string, { ids: string; labels: string; p2pIds: string; p2pLabels: string; notes: string }>
  >({});
  /** Manual: gravar no mapa global (todos os CMS), alinhado ao «Aplicar hints». */
  const [broadcastSaveTargetGlobal, setBroadcastSaveTargetGlobal] = useState(true);
  const [broadcastFixtureBusy, setBroadcastFixtureBusy] = useState<number | null>(null);
  
  useEffect(() => {
    const next: Record<string, { ids: string; labels: string; p2pIds: string; p2pLabels: string; notes: string }> = {};
    for (const row of broadcastGames) {
      if (row.fixtureId == null) continue;
      const k = String(row.fixtureId);
      next[k] = {
        ids: (row.streamIds || []).join(', '),
        labels: (row.streamLabels || []).join(', '),
        p2pIds: (row.p2pStreamIds || []).join(', '),
        p2pLabels: (row.p2pStreamLabels || []).join(', '),
        notes: (row.notes || '').trim()
      };
    }
    setBroadcastDrafts(next);
  }, [broadcastGames]);

  // Estados para configurações de limpeza de linhas
  const [lineCleanupSettings, setLineCleanupSettings] = useState({
    enabled: false,
    cron_expression: '0 3 * * *',
    is_running: false,
    last_run: null as Date | null,
    last_run_result: null as any,
    stats: {
      total_runs: 0,
      total_removed: 0,
      last_error: null
    }
  });

  const [lineCleanupStats, setLineCleanupStats] = useState({
    total_empty_lines: 0,
    empty_username: 0,
    empty_password: 0,
    empty_both: 0
  });

  const [, setStreamingPlatform] = useState({
    edgeApiUrlsList: '',
    p2pEnabledDefault: true,
    adminNotes: ''
  });
  const [, setLoadingStreamingPlatform] = useState(false);

  const [loadingLineCleanup, setLoadingLineCleanup] = useState(false);
  const [executingCleanup, setExecutingCleanup] = useState(false);
  const [nextExecutionCountdown, setNextExecutionCountdown] = useState<string>('--:--:--');
  
  // Estados para gerador de testes
  const [trialSettings, setTrialSettings] = useState({
    xuiApiUrl: '',
    xuiApiToken: '',
    xuiTrialPackageId: 194,
    xuiIndependentEnabled: false, // Usar XUI próprio (independente do painel pai/union)
    appDownloadUrl: '',
    trialCooldownHours: 720, // 30 dias padrão
    hasToken: false,
    publicTrialToken: '',
    publicTrialUrlApi: '',
    publicTrialUrlPage: '',
    trialGeneratorEnabled: true // Controle de ativação no app
  });
  
  const [validatingToken, setValidatingToken] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [generatingTrial, setGeneratingTrial] = useState(false);
  const [lastGeneratedTrial, setLastGeneratedTrial] = useState<any>(null);
  const [showTrialToken, setShowTrialToken] = useState(false);
  const [publicUrlApiCopied, setPublicUrlApiCopied] = useState(false);
  const [publicUrlPageCopied, setPublicUrlPageCopied] = useState(false);
  
  // Estado simplificado apenas para verificar conexão (usado pelo componente WhatsappAutomation)
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  
  // Preview removido - não é mais necessário já que temos a página completa
  
  // Estados para configurações do Mercado Pago
  const [showMercadoPagoKeys, setShowMercadoPagoKeys] = useState({
    publicKey: false,
    accessToken: false
  });
  const [mercadoPagoKeysCopied, setMercadoPagoKeysCopied] = useState({
    publicKey: false,
    accessToken: false
  });
  const [mercadoPagoConfig, setMercadoPagoConfig] = useState({
    enabled: false,
    publicKey: '',
    accessToken: '',
    webhookUrl: '',
    environment: 'sandbox' as 'sandbox' | 'production',
    renewalMonthPriceBrl: 29.9,
    plans: [
      {
        id: 'mensal',
        label: 'Mensal',
        months: 1,
        priceBrl: 29.9,
        active: true,
        iptvPackageId: null
      }
    ] as MercadoPagoPlanRow[]
  });

  // Estados para configurações do UpdePix (PIX)
  const [showUpdePixKey, setShowUpdePixKey] = useState(false);
  const [updePixKeyCopied, setUpdePixKeyCopied] = useState(false);
  const [showUpdePixWebhookSecret, setShowUpdePixWebhookSecret] = useState(false);
  const [updePixWebhookSecretCopied, setUpdePixWebhookSecretCopied] = useState(false);
  const [updePixConfig, setUpdePixConfig] = useState({
    enabled: false,
    apiKey: '',
    createDepositUrl: '',
    webhookSecret: ''
  });

  const [showLynxKey, setShowLynxKey] = useState(false);
  const [lynxKeyCopied, setLynxKeyCopied] = useState(false);
  const [showLynxWebhookSecret, setShowLynxWebhookSecret] = useState(false);
  const [lynxWebhookSecretCopied, setLynxWebhookSecretCopied] = useState(false);
  const [lynxConfig, setLynxConfig] = useState({
    enabled: false,
    apiKey: '',
    createPixUrl: '',
    webhookSecret: ''
  });

  const [creditSalesConfig, setCreditSalesConfig] = useState({
    enabled: false,
    packages: [] as Array<{ id: string; label: string; credits: number; priceBrl: number; active: boolean }>
  });

  /** Pacotes IPTV oficiais (excl. trial), mesma API da renovação manual — para planos da loja. */
  const [storeRenewalIptvPackages, setStoreRenewalIptvPackages] = useState<
    Array<{
      id: number;
      display_name: string;
      is_trial?: number;
      official_duration?: number;
    }>
  >([]);
  const [storeRenewalIptvPackagesLoading, setStoreRenewalIptvPackagesLoading] =
    useState(false);
  /** Pacotes do XUI remoto (modo independente) — inclui testes. */
  const [xuiRemotePackages, setXuiRemotePackages] = useState<
    Array<{ id: number; display_name?: string; package_name?: string; name?: string; is_trial?: number }>
  >([]);

  // Form validation
  const { validateForm, validateSingleField, clearError } = useFormValidation(cmsUserValidationRules);
  
  const { token, isAuthenticated } = useAuthStore();

  const getPanelApiBase = useCallback(
    () =>
      process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api-cms.cms20.online'),
    []
  );

  const resolveAuthToken = useCallback((): string | null => {
    let authToken = token || '';
    if (!authToken) {
      try {
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
          const authData = JSON.parse(authStorage);
          authToken = authData.state?.token || authData.token || '';
        }
        if (!authToken) {
          const legacyStorage = localStorage.getItem('_pv001');
          if (legacyStorage) {
            const legacyData = JSON.parse(legacyStorage);
            authToken = legacyData.t || '';
          }
        }
        if (!authToken) {
          authToken = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
        }
      } catch {
        /* ignore */
      }
    }
    return authToken ? String(authToken) : null;
  }, [token]);

  // Credenciais Xtream dos jogos do dia: domínio do slug + channelsday/daychannels
  useEffect(() => {
    if (activeTab !== 'gamesOfDay') return;
    if (!isAdmin()) return;

    let cancelled = false;

    setXtreamUsername((prev) => (prev && prev.trim() ? prev : 'channelsday'));
    setXtreamPassword((prev) => (prev ? prev : 'daychannels'));

    (async () => {
      const auth = resolveAuthToken();
      if (!auth) return;
      try {
        const base = getPanelApiBase();
        const res = await fetch(`${base}/api/games-broadcast-map/xtream-defaults`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${auth}`,
            ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
          }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        const payload = (data as { data?: { server?: string; username?: string; password?: string } }).data;
        if (!payload) return;
        if (payload.server) {
          setXtreamServer((prev) => (prev && prev.trim() ? prev : String(payload.server).replace(/\/+$/, '')));
        }
        if (payload.username) {
          setXtreamUsername((prev) => (prev && prev.trim() ? prev : payload.username!));
        }
        if (payload.password) {
          setXtreamPassword((prev) => (prev ? prev : payload.password!));
        }
      } catch {
        /* user/senha já preenchidos; URL vem da API quando disponível */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, cmsUser?.cmsSlug, getPanelApiBase, resolveAuthToken, isAdmin]);

  const parseBroadcastStreamIds = (raw: string): number[] =>
    String(raw || '')
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n > 0);

  const loadStoreRenewalIptvPackages = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    setStoreRenewalIptvPackagesLoading(true);
    try {
      const res = (await apiService.getPackages(1, 500)) as {
        data?: Array<{
          id: number;
          display_name: string;
          package_name?: string;
          name?: string;
          is_trial?: number;
          official_duration?: number;
        }>;
        xui_independent?: boolean;
      };
      const raw = res.data;
      const rows: Array<{
        id: number;
        display_name: string;
        package_name?: string;
        name?: string;
        is_trial?: number;
        is_official?: number;
        official_duration?: number;
      }> = Array.isArray(raw) ? raw : [];
      setXuiRemotePackages(rows);
      // Pacotes oficiais (não-trial) para vincular aos planos da loja; XUI remoto usa is_trial_package
      setStoreRenewalIptvPackages(
        rows.filter(
          (row) =>
            Number(row.is_trial || 0) < 1 ||
            Number(row.is_official || 0) === 1 ||
            Number(row.official_duration || 0) > 0
        )
      );
    } catch {
      setStoreRenewalIptvPackages([]);
      setXuiRemotePackages([]);
    } finally {
      setStoreRenewalIptvPackagesLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    void loadStoreRenewalIptvPackages();
  }, [loadStoreRenewalIptvPackages]);

  /** Alinhar `months` ao pacote quando a lista de pacotes carregar (ex.: official_duration alterado no painel). */
  useEffect(() => {
    if (storeRenewalIptvPackagesLoading || storeRenewalIptvPackages.length === 0) return;
    setMercadoPagoConfig((prev) => {
      const plans = prev.plans || [];
      let changed = false;
      const next = plans.map((row) => {
        if (row.iptvPackageId == null || row.iptvPackageId < 1) return row;
        const pkg = storeRenewalIptvPackages.find((x) => x.id === row.iptvPackageId);
        if (!pkg) return row;
        const m = Math.min(
          60,
          Math.max(1, parseInt(String(pkg.official_duration ?? 1), 10) || 1)
        );
        if (row.months !== m) {
          changed = true;
          return { ...row, months: m };
        }
        return row;
      });
      return changed ? { ...prev, plans: next } : prev;
    });
  }, [storeRenewalIptvPackages, storeRenewalIptvPackagesLoading]);

  // Colors context - removido para simplificar
  // const { colors } = useColors();

  // Comunicado no app: marca quando título/mensagem mudaram desde a última publicação.
  // Ao salvar, texto alterado gera um ID novo → o aviso reaparece para todos os clientes.
  const [inAppNoticeDirty, setInAppNoticeDirty] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    cmsName: '',
    logoUrl: '',
    cmsSlug: '',
    colors: {
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#F59E0B',
      background: '#F8FAFC',
      text: '#1F2937'
    },
    settings: {
      notifications: {
        email: true,
        push: false,
        sms: false
      },
      theme: 'light',
      language: 'pt-BR',
      customDomain: '',
      binstreamApiUrl: '',
      bannerUrl: '',
      homeBackgroundUrl: ''
    },
    appUpdate: {
      enabled: false,
      latestVersionCode: 1,
      latestVersionName: '1.0.0',
      apkDownloadUrl: '',
      updateMessage: 'Nova versão disponível! Atualize para ter acesso às últimas funcionalidades.',
      forceUpdate: false,
      minSupportedVersion: 1
    },
    inAppNotice: {
      enabled: false,
      id: '',
      title: '',
      message: '',
      severity: 'info' as const,
      updatedAt: ''
    },
    gamesOfDay: {
      enabled: false,
      apiKey: '',
      countryId: 'BR',
      countryName: 'Brasil',
      pageUrl: '',
      broadcastHintsUrl: '',
      autoBroadcastChannels: true
    },
    dpiBypass: {
      enabled: false,
      proxyPort: 1080
    },
    streamingEdge: streamingEdgeForForm({
      playbackMode: 'iptv',
      edgeApiUrl: DEFAULT_EDGE_API_PRIMARY
    }),
    inAppPaymentEnabled: false,
    appPromotionSettings: {
      enabled: false,
      promotionBox: {
        title: 'ATENÇÃO!',
        price: 'R$29,90/MÊS',
        priceLabel: 'Nosso app custa apenas:',
        description: 'O melhores conteúdos',
        highlightText: 'Sempre Atualizados!',
        benefits: ['Canais, Filmes e Séries', 'Dê 5 estrelas', 'Deixe um comentário'],
        buttonText: 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
        buttonLink: '',
              contactTitle: '',
        phoneNumber: '',
        whatsappLink: ''
      },
      supportBox: {
        enabled: true,
        title: 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP',
        phoneNumber: '',
        whatsappLink: '',
        imageUrl: ''
      },
      startDate: '',
      endDate: ''
    }
  } as any);


  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (slugDebounceTimer) {
        clearTimeout(slugDebounceTimer);
      }
    };
  }, [slugDebounceTimer]);

  const fetchCmsUser = async () => {
    try {
      setLoading(true);
      const data: any = await simpleApiService.getCmsUserCurrent();
      
      if (data?.user) {
        const u = data.user;
        const cmsData: CmsUserData = {
          _id: u._id,
          username: u.username,
          email: u.email,
          name: u.name || u.username,
          cmsName: u.cmsName || u.username,
          logoUrl: u.logoUrl || '',
          cmsSlug: u.cmsSlug || u.username,
          colors: u.colors || {
            primary: '#3B82F6',
            secondary: '#1E40AF',
            accent: '#F59E0B',
            background: '#F8FAFC',
            text: '#1F2937'
          },
          settings: (u.settings ? {
            ...u.settings,
            customDomain: u.cmsSettings?.customDomain || u.cmsSettings?.customDomain || '',
            binstreamApiUrl: u.cmsSettings?.binstreamApiUrl || '',
            bannerUrl: u.cmsSettings?.bannerUrl || '',
            homeBackgroundUrl: u.cmsSettings?.homeBackgroundUrl || ''
          } : {
            notifications: { email: true, push: false, sms: false },
            theme: 'light',
            language: 'pt-BR',
            customDomain: '',
            binstreamApiUrl: '',
            bannerUrl: '',
            homeBackgroundUrl: ''
          }),
          appUpdate: u.appUpdate || {
            enabled: false,
            latestVersionCode: 1,
            latestVersionName: '1.0.0',
            apkDownloadUrl: '',
            updateMessage: 'Nova versão disponível! Atualize para ter acesso às últimas funcionalidades.',
            forceUpdate: false,
            minSupportedVersion: 1
          },
          inAppNotice: u.inAppNotice ? {
            enabled: u.inAppNotice.enabled || false,
            id: u.inAppNotice.id || '',
            title: u.inAppNotice.title || '',
            message: u.inAppNotice.message || '',
            severity: ['info', 'warning', 'danger'].includes(u.inAppNotice.severity) ? u.inAppNotice.severity : 'info',
            updatedAt: u.inAppNotice.updatedAt
              ? (typeof u.inAppNotice.updatedAt === 'string'
                ? u.inAppNotice.updatedAt
                : new Date(u.inAppNotice.updatedAt).toISOString())
              : ''
          } : {
            enabled: false,
            id: '',
            title: '',
            message: '',
            severity: 'info',
            updatedAt: ''
          },
          gamesOfDay: u.gamesOfDay || {
            enabled: false,
            apiKey: '',
            countryId: 'BR',
            countryName: 'Brasil',
            pageUrl: '',
            broadcastHintsUrl: ''
          },
          dpiBypass: u.dpiBypass || {
            enabled: false,
            proxyPort: 1080
          },
          streamingEdge: streamingEdgeForForm({
            ...u.streamingEdge,
            edgeApiUrl: u.streamingEdge?.edgeApiUrl || DEFAULT_EDGE_API_PRIMARY
          }),
          appPromotionSettings: u.appPromotionSettings || {
            enabled: false,
            title: '',
            message: '',
            buttonText: 'Ver Promoção',
            buttonLink: '',
            imageUrl: '',
            startDate: '',
            endDate: ''
          },
          mercadoPago: u.mercadoPago || {
            enabled: false,
            publicKey: '',
            accessToken: '',
            webhookUrl: '',
            environment: 'sandbox',
            renewalMonthPriceBrl: 29.9
          },
          updePix: u.updePix || {
            enabled: false,
            apiKey: '',
            createDepositUrl: ''
          },
          lynx: u.lynx || {
            enabled: false,
            apiKey: '',
            createPixUrl: ''
          },
          creditSales: u.creditSales || { enabled: false, packages: [] },
          role: u.role,
          status: u.status,
          createdAt: u.createdAt,
          updatedAt: u.updatedAt
        };

        setCmsUser(cmsData);
        setFormData({
          username: cmsData.username,
          email: cmsData.email,
          name: cmsData.name,
          cmsName: cmsData.cmsName,
          logoUrl: cmsData.logoUrl || '',
          colors: cmsData.colors,
          settings: {
            ...cmsData.settings,
            customDomain: u.cmsSettings?.customDomain || cmsData.cmsSettings?.customDomain || '',
            binstreamApiUrl: u.cmsSettings?.binstreamApiUrl || cmsData.cmsSettings?.binstreamApiUrl || '',
            bannerUrl: u.cmsSettings?.bannerUrl || cmsData.cmsSettings?.bannerUrl || '',
            homeBackgroundUrl: u.cmsSettings?.homeBackgroundUrl || cmsData.cmsSettings?.homeBackgroundUrl || ''
          },
          cmsSlug: cmsData.cmsSlug || '',
          appUpdate: cmsData.appUpdate ? {
            enabled: cmsData.appUpdate.enabled || false,
            latestVersionCode: cmsData.appUpdate.latestVersionCode || 1,
            latestVersionName: cmsData.appUpdate.latestVersionName || '1.0.0',
            apkDownloadUrl: cmsData.appUpdate.apkDownloadUrl || '',
            updateMessage: cmsData.appUpdate.updateMessage || 'Nova versão disponível! Atualize para ter acesso às últimas funcionalidades.',
            forceUpdate: cmsData.appUpdate.forceUpdate || false,
            minSupportedVersion: cmsData.appUpdate.minSupportedVersion || 1
          } : {
            enabled: false,
            latestVersionCode: 1,
            latestVersionName: '1.0.0',
            apkDownloadUrl: '',
            updateMessage: 'Nova versão disponível! Atualize para ter acesso às últimas funcionalidades.',
            forceUpdate: false,
            minSupportedVersion: 1
          },
          inAppNotice: cmsData.inAppNotice ? {
            enabled: cmsData.inAppNotice.enabled || false,
            id: cmsData.inAppNotice.id || '',
            title: cmsData.inAppNotice.title || '',
            message: cmsData.inAppNotice.message || '',
            severity: ['info', 'warning', 'danger'].includes(cmsData.inAppNotice.severity)
              ? cmsData.inAppNotice.severity
              : 'info',
            updatedAt: cmsData.inAppNotice.updatedAt || ''
          } : {
            enabled: false,
            id: '',
            title: '',
            message: '',
            severity: 'info',
            updatedAt: ''
          },
          gamesOfDay: cmsData.gamesOfDay ? {
            enabled: cmsData.gamesOfDay.enabled || false,
            apiKey: cmsData.gamesOfDay.apiKey || '',
            filteredCountries: cmsData.gamesOfDay.filteredCountries || [],
            filteredLeagues: cmsData.gamesOfDay.filteredLeagues || [],
            countryId: cmsData.gamesOfDay.countryId || '27',
            countryName: cmsData.gamesOfDay.countryName || 'Brasil',
            pageUrl: cmsData.gamesOfDay.pageUrl || '',
            broadcastHintsUrl: cmsData.gamesOfDay.broadcastHintsUrl || '',
            autoBroadcastChannels: cmsData.gamesOfDay.autoBroadcastChannels !== false
          } : {
            enabled: false,
            apiKey: '',
            filteredCountries: [],
            filteredLeagues: [],
            countryId: 'BR',
            countryName: 'Brasil',
            pageUrl: '',
            broadcastHintsUrl: '',
            autoBroadcastChannels: true
          },
          dpiBypass: cmsData.dpiBypass ? {
            enabled: cmsData.dpiBypass.enabled || false,
            proxyPort: cmsData.dpiBypass.proxyPort || 1080
          } : {
            enabled: false,
            proxyPort: 1080
          },
          streamingEdge: streamingEdgeForForm({
            ...cmsData.streamingEdge,
            edgeApiUrl: cmsData.streamingEdge?.edgeApiUrl || DEFAULT_EDGE_API_PRIMARY
          }),
          appPromotionSettings: cmsData.appPromotionSettings ? {
            enabled: cmsData.appPromotionSettings.enabled || false,
            promotionBox: cmsData.appPromotionSettings.promotionBox ? {
              title: cmsData.appPromotionSettings.promotionBox.title || 'ATENÇÃO!',
              price: cmsData.appPromotionSettings.promotionBox.price || 'R$19,90/MÊS',
              priceLabel: cmsData.appPromotionSettings.promotionBox.priceLabel || 'Nosso app custa apenas:',
              description: cmsData.appPromotionSettings.promotionBox.description || '',
              highlightText: cmsData.appPromotionSettings.promotionBox.highlightText || '',
              benefits: Array.isArray(cmsData.appPromotionSettings.promotionBox.benefits) ? cmsData.appPromotionSettings.promotionBox.benefits : ['Canais, Filmes e Séries', 'Dê 5 estrelas', 'Deixe um comentário'],
              buttonText: cmsData.appPromotionSettings.promotionBox.buttonText || 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
              buttonLink: cmsData.appPromotionSettings.promotionBox.buttonLink || '',
              // Migrar dados de contato do supportBox para promotionBox se não existirem
              contactTitle: cmsData.appPromotionSettings.promotionBox.contactTitle ?? cmsData.appPromotionSettings.supportBox?.title ?? '',
              phoneNumber: cmsData.appPromotionSettings.promotionBox.phoneNumber ?? cmsData.appPromotionSettings.supportBox?.phoneNumber ?? '',
              whatsappLink: cmsData.appPromotionSettings.promotionBox.whatsappLink ?? cmsData.appPromotionSettings.supportBox?.whatsappLink ?? ''
            } : {
              title: 'ATENÇÃO!',
              price: 'R$19,90/MÊS',
              priceLabel: 'Nosso app custa apenas:',
              description: 'O melhores conteúdos',
              highlightText: 'Sempre Atualizados!',
              benefits: ['Canais, Filmes e Séries', 'Dê 5 estrelas', 'Deixe um comentário'],
              buttonText: 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
              buttonLink: '',
              // Migrar dados de contato do supportBox para promotionBox
              contactTitle: cmsData.appPromotionSettings.supportBox?.title ?? '',
              phoneNumber: cmsData.appPromotionSettings.supportBox?.phoneNumber ?? '',
              whatsappLink: cmsData.appPromotionSettings.supportBox?.whatsappLink ?? ''
            },
            supportBox: cmsData.appPromotionSettings.supportBox ? {
              enabled: cmsData.appPromotionSettings.supportBox.enabled !== undefined ? cmsData.appPromotionSettings.supportBox.enabled : true,
              title: cmsData.appPromotionSettings.supportBox.title || 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP',
              phoneNumber: cmsData.appPromotionSettings.supportBox.phoneNumber || '',
              whatsappLink: cmsData.appPromotionSettings.supportBox.whatsappLink || '',
              imageUrl: cmsData.appPromotionSettings.supportBox.imageUrl || ''
            } : {
              enabled: true,
              title: 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NO WHATSAPP',
              phoneNumber: '',
              whatsappLink: '',
              imageUrl: ''
            },
            startDate: cmsData.appPromotionSettings.startDate ? (typeof cmsData.appPromotionSettings.startDate === 'string' ? cmsData.appPromotionSettings.startDate : new Date(cmsData.appPromotionSettings.startDate).toISOString().split('T')[0]) : '',
            endDate: cmsData.appPromotionSettings.endDate ? (typeof cmsData.appPromotionSettings.endDate === 'string' ? cmsData.appPromotionSettings.endDate : new Date(cmsData.appPromotionSettings.endDate).toISOString().split('T')[0]) : ''
          } : {
            enabled: false,
            promotionBox: {
              title: 'ATENÇÃO!',
              price: 'R$19,90/MÊS',
              priceLabel: 'Nosso app custa apenas:',
              description: 'O melhores conteúdos',
              highlightText: 'Sempre Atualizados!',
              benefits: ['Canais, Filmes e Séries', 'Dê 5 estrelas', 'Deixe um comentário'],
              buttonText: 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
              buttonLink: '',
              contactTitle: '',
              phoneNumber: '',
              whatsappLink: ''
            },
            supportBox: {
              enabled: true,
              title: 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP',
              phoneNumber: '',
              whatsappLink: '',
              imageUrl: ''
            },
            startDate: '',
            endDate: ''
          },
          inAppPaymentEnabled: Boolean(u.cmsSettings?.inAppPaymentEnabled)
        });

        setMercadoPagoConfig({
          enabled: cmsData.mercadoPago?.enabled || false,
          publicKey: cmsData.mercadoPago?.publicKey || '',
          accessToken: clearIfMaskedCredential(cmsData.mercadoPago?.accessToken),
          webhookUrl: cmsData.mercadoPago?.webhookUrl || '',
          environment: cmsData.mercadoPago?.environment || 'sandbox',
          renewalMonthPriceBrl:
            cmsData.mercadoPago?.renewalMonthPriceBrl != null &&
            Number.isFinite(Number(cmsData.mercadoPago?.renewalMonthPriceBrl))
              ? Number(cmsData.mercadoPago?.renewalMonthPriceBrl)
              : 29.9,
          plans:
            Array.isArray(cmsData.mercadoPago?.plans) && cmsData.mercadoPago?.plans.length
              ? cmsData.mercadoPago.plans.map((p: Record<string, unknown>) =>
                  normalizeMercadoPagoPlan(p)
                )
              : [
                  normalizeMercadoPagoPlan({
                    id: 'mensal',
                    label: 'Mensal',
                    months: 1,
                    priceBrl: 29.9,
                    active: true
                  })
                ]
        });

        setUpdePixConfig({
          enabled: cmsData.updePix?.enabled || false,
          apiKey: clearIfMaskedCredential(cmsData.updePix?.apiKey),
          createDepositUrl: cmsData.updePix?.createDepositUrl || '',
          webhookSecret: clearIfMaskedCredential(cmsData.updePix?.webhookSecret)
        });
        setLynxConfig({
          enabled: cmsData.lynx?.enabled || false,
          apiKey: clearIfMaskedCredential(cmsData.lynx?.apiKey),
          createPixUrl: cmsData.lynx?.createPixUrl || '',
          webhookSecret: clearIfMaskedCredential(cmsData.lynx?.webhookSecret)
        });

        const creditPkgs = cmsData.creditSales?.packages;
        setCreditSalesConfig({
          enabled: Boolean(cmsData.creditSales?.enabled),
          packages: Array.isArray(creditPkgs) ? creditPkgs : []
        });

        const rev = (u.settings && (u.settings.revenue_per_user || u.settings.revenuePerUser)) || '';
        setRevenuePerUser(rev ? String(rev) : '');
      }
    } catch (error) {
      // Erro silenciado
    } finally {
      setLoading(false);
    }
  };

  
      // Load user data
      useEffect(() => {
        fetchCmsUser();
      }, [  ]);

  // Carregar configurações de limpeza de linhas (apenas para admins)
  const fetchLineCleanupSettings = useCallback(async () => {
    if (cmsUser?.role !== 'admin') return;
    
    try {
      setLoadingLineCleanup(true);
      const data = await apiService.getLineCleanupSettings();
      
      if (data.success) {
        setLineCleanupSettings({
          enabled: data.data.enabled,
          cron_expression: data.data.cron_expression,
          is_running: data.data.is_running,
          last_run: data.data.last_run ? new Date(data.data.last_run) : null,
          last_run_result: data.data.last_run_result,
          stats: data.data.stats
        });
        setLineCleanupStats(data.data.current_stats);
      }
    } catch (error) {
      // Erro silenciado
    } finally {
      setLoadingLineCleanup(false);
    }
  }, [cmsUser?.role]);

  useEffect(() => {
    if (cmsUser?.role === 'admin') {
      fetchLineCleanupSettings();
    }
  }, [cmsUser, fetchLineCleanupSettings]);

  const fetchStreamingPlatformSettings = useCallback(async () => {
    if (cmsUser?.role !== 'admin') return;
    try {
      setLoadingStreamingPlatform(true);
      const data = await apiService.getStreamingPlatformSettings();
      if (data.success && data.data) {
        setStreamingPlatform({
          edgeApiUrlsList: joinUrlList(data.data.defaultEdgeApiUrl, data.data.edgeApiUrlFallbacks),
          p2pEnabledDefault: data.data.p2pEnabledDefault !== false,
          adminNotes: data.data.adminNotes || ''
        });
      }
    } catch {
      // silencioso
    } finally {
      setLoadingStreamingPlatform(false);
    }
  }, [cmsUser?.role]);

  useEffect(() => {
    if (cmsUser?.role === 'admin') {
      fetchStreamingPlatformSettings();
    }
  }, [cmsUser, fetchStreamingPlatformSettings]);

  // Calcular próxima execução e atualizar countdown
  useEffect(() => {
    if (!lineCleanupSettings.enabled || !lineCleanupSettings.is_running) {
      setNextExecutionCountdown('--:--:--');
      return;
    }

    const calculateNextExecution = () => {
      const now = new Date();
      let nextExec = new Date(now);

      // Calcular baseado na expressão cron
      const cron = lineCleanupSettings.cron_expression;
      
      if (cron === '*/1 * * * *') {
        // A cada 1 minuto - próxima execução no próximo minuto
        nextExec.setMinutes(nextExec.getMinutes() + 1);
        nextExec.setSeconds(0);
        nextExec.setMilliseconds(0);
      } else if (cron === '*/10 * * * *') {
        // A cada 10 minutos
        const currentMinute = nextExec.getMinutes();
        const nextMinute = Math.ceil((currentMinute + 1) / 10) * 10;
        nextExec.setMinutes(nextMinute);
        nextExec.setSeconds(0);
        nextExec.setMilliseconds(0);
      } else if (cron === '*/30 * * * *') {
        // A cada 30 minutos
        const currentMinute = nextExec.getMinutes();
        const nextMinute = currentMinute < 30 ? 30 : 60;
        if (nextMinute === 60) {
          nextExec.setHours(nextExec.getHours() + 1);
          nextExec.setMinutes(0);
        } else {
          nextExec.setMinutes(nextMinute);
        }
        nextExec.setSeconds(0);
        nextExec.setMilliseconds(0);
      } else if (cron === '0 */1 * * *') {
        // A cada 1 hora
        nextExec.setHours(nextExec.getHours() + 1);
        nextExec.setMinutes(0);
        nextExec.setSeconds(0);
        nextExec.setMilliseconds(0);
      }

      return nextExec;
    };

    const updateCountdown = () => {
      const now = new Date();
      const next = calculateNextExecution();
      const diff = next.getTime() - now.getTime();

      if (diff <= 0) {
        setNextExecutionCountdown('Executando...');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setNextExecutionCountdown(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };

    // Atualizar imediatamente
    updateCountdown();

    // Atualizar a cada segundo
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [lineCleanupSettings.enabled, lineCleanupSettings.is_running, lineCleanupSettings.cron_expression]);

  const handleLineCleanupToggle = async (enabled: boolean) => {
    try {
      setSaving(true);
      const data = await apiService.updateLineCleanupSettings({
        enabled,
        cron_expression: lineCleanupSettings.cron_expression
      });
      
      if (data.success) {
        alert(data.message);
        await fetchLineCleanupSettings();
      }
    } catch (error) {
      alert('Erro ao atualizar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleCronExpressionChange = async (cronExpression: string) => {
    try {
      setSaving(true);
      const data = await apiService.updateLineCleanupSettings({
        enabled: lineCleanupSettings.enabled,
        cron_expression: cronExpression
      });
      
      if (data.success) {
        alert('Horário atualizado com sucesso!');
        await fetchLineCleanupSettings();
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao atualizar horário');
    } finally {
      setSaving(false);
    }
  };

  const handleManualCleanup = async () => {
    if (!window.confirm('Deseja executar a limpeza manual de linhas vazias agora?')) {
      return;
    }
    
    try {
      setExecutingCleanup(true);
      const data = await apiService.executeManualLineCleanup();
      
      if (data.success) {
        alert(`Limpeza concluída!\n${data.removed} linhas removidas.`);
        await fetchLineCleanupSettings();
      } else {
        alert(`Erro: ${data.message}`);
      }
    } catch (error) {
      alert('Erro ao executar limpeza');
    } finally {
      setExecutingCleanup(false);
    }
  };   

  // Funções do Gerador de Testes
  const fetchTrialSettings = useCallback(async () => {
    try {
      const data = await apiService.getTrialGeneratorSettings();
      
      if (data.success) {
        setTrialSettings({
          ...data.data,
          trialGeneratorEnabled: data.data.trialGeneratorEnabled !== false // Default true se não existir
        });
      }
    } catch (error) {
      // Erro silenciado
    }
  }, []);

  // Função simplificada para verificar apenas se está conectado
  const checkWhatsappConnection = useCallback(async () => {
    try {
      if (cmsUser?.role === 'client') return;
      const data = await apiService.getWhatsappAutomationStatus();
      if (data?.success && data.data) {
        setWhatsappConnected(data.data.status === 'connected');
      } else {
        setWhatsappConnected(false);
      }
    } catch (error) {
      setWhatsappConnected(false);
    }
  }, [cmsUser?.role]);

  useEffect(() => {
    fetchTrialSettings();
  }, [fetchTrialSettings]);

  useEffect(() => {
    if (!cmsUser || cmsUser.role === 'client') return;
    checkWhatsappConnection();
    // Verificar conexão periodicamente
    const interval = setInterval(() => {
      checkWhatsappConnection();
    }, 30000); // A cada 30 segundos
    return () => clearInterval(interval);
  }, [cmsUser, checkWhatsappConnection]);

  const handleTrialSettingsChange = (field: string, value: any) => {
    setTrialSettings(prev => ({
      ...prev,
      [field]: value
    }));
    // Resetar validação ao mudar token
    if (field === 'xuiApiToken' || field === 'xuiApiUrl') {
      setTokenValid(null);
    }
  };

  const handleValidateToken = async () => {
    try {
      setValidatingToken(true);
      const data = await apiService.validateXuiToken(
        trialSettings.xuiApiUrl,
        trialSettings.xuiApiToken
      );
      
      setTokenValid(data.valid);
      alert(data.message);
    } catch (error) {
      setTokenValid(false);
      alert('Erro ao validar token');
    } finally {
      setValidatingToken(false);
    }
  };

  // Preview removido - não é mais necessário já que temos a página completa

  const handleSaveTrialSettings = async () => {
    try {
      setSaving(true);
      
      // Preparar payload - enviar apenas campos preenchidos
      const payload: any = {};
      
      if (trialSettings.xuiApiUrl && trialSettings.xuiApiUrl.trim() !== '') {
        payload.xuiApiUrl = trialSettings.xuiApiUrl;
      }
      
      if (trialSettings.xuiApiToken && trialSettings.xuiApiToken.trim() !== '') {
        payload.xuiApiToken = trialSettings.xuiApiToken;
      }
      
      if (trialSettings.appDownloadUrl && trialSettings.appDownloadUrl.trim() !== '') {
        payload.appDownloadUrl = trialSettings.appDownloadUrl;
      }
      
      // Package ID e cooldown sempre enviam
      payload.xuiTrialPackageId = trialSettings.xuiTrialPackageId;
      payload.trialCooldownHours = trialSettings.trialCooldownHours;
      payload.trialGeneratorEnabled = trialSettings.trialGeneratorEnabled;
      payload.xuiIndependentEnabled = trialSettings.xuiIndependentEnabled;
      
      const data = await apiService.updateTrialGeneratorSettings(payload);
      
      if (data.success) {
        alert(data.message);
        await fetchTrialSettings();
      }
    } catch (error) {
      alert('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateTrial = async () => {
    try {
      setGeneratingTrial(true);
      const data = await apiService.generateTrial(
        trialSettings.xuiTrialPackageId,
        24, // 24 horas
        1 // 1 conexão
      );
      
      if (data.success) {
        setLastGeneratedTrial(data.data);
        alert('Teste gerado com sucesso!');
      } else if (data.needsSetup) {
        alert('Configure seu token XUI primeiro!');
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao gerar trial');
    } finally {
      setGeneratingTrial(false);
    }
  };

  const copyTrialCredentials = (text: string) => {
    // Tentar clipboard API primeiro (HTTPS)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => alert('Copiado!'))
        .catch(() => fallbackCopy(text));
    } else {
      // Fallback para HTTP/navegadores antigos
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        alert('Copiado!');
      } else {
        promptCopy(text);
      }
    } catch (err) {
      promptCopy(text);
    }
    
    // Evita NotFoundError intermitente (o node pode já não estar no DOM)
    try {
      textArea.remove();
    } catch (_) {
      if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
    }
  };

  const promptCopy = (text: string) => {
    prompt('Copie o texto abaixo (Ctrl+C):', text);
  };

  const handleRegeneratePublicToken = async () => {
    if (!window.confirm('Deseja gerar um novo link público? O link anterior será invalidado.')) {
      return;
    }

    try {
      setSaving(true);
      const data = await apiService.regeneratePublicTrialToken();
      
      if (data.success) {
        alert(data.message);
        await fetchTrialSettings();
      }
    } catch (error) {
      alert('Erro ao regenerar token');
    } finally {
      setSaving(false);
    }
  };

  const copyPublicUrlApi = () => {
    if (!trialSettings.publicTrialUrlApi) return;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(trialSettings.publicTrialUrlApi)
        .then(() => {
          setPublicUrlApiCopied(true);
          setTimeout(() => setPublicUrlApiCopied(false), 2000);
        })
        .catch(() => fallbackCopyUrl(trialSettings.publicTrialUrlApi, setPublicUrlApiCopied));
    } else {
      fallbackCopyUrl(trialSettings.publicTrialUrlApi, setPublicUrlApiCopied);
    }
  };

  const fallbackCopyUrl = (text: string, setCopied?: (value: boolean) => void) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful && setCopied) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else if (!successful) {
        prompt('Copie o link abaixo (Ctrl+C):', text);
      }
    } catch (err) {
      prompt('Copie o link abaixo (Ctrl+C):', text);
    }
    
    // Evita NotFoundError intermitente (o node pode já não estar no DOM)
    try {
      textArea.remove();
    } catch (_) {
      if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
    }
  };

  const copyPublicUrlPage = () => {
    if (!trialSettings.publicTrialUrlPage) return;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(trialSettings.publicTrialUrlPage)
        .then(() => {
          setPublicUrlPageCopied(true);
          setTimeout(() => setPublicUrlPageCopied(false), 2000);
        })
        .catch(() => fallbackCopyUrl(trialSettings.publicTrialUrlPage, setPublicUrlPageCopied));
    } else {
      fallbackCopyUrl(trialSettings.publicTrialUrlPage, setPublicUrlPageCopied);
    }
  };


  const handleInputChange = (field: string, value: any) => {
    // Clear error when user starts typing
    clearError(field);
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData((prev: any) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as any),
          [child]: value
        }
      }));
    } else if (field.includes('notifications.')) {
      const [, notificationType] = field.split('.');
      setFormData((prev: any) => ({
        ...prev,
        settings: {
          ...prev.settings,
          notifications: {
            ...prev.settings.notifications,
            [notificationType]: value
          }
        }
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleInputBlur = (field: string, value: any) => {
    validateSingleField(field, value);
  };

  const handleColorChange = (colorType: string, value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorType]: value
      }
    }));
  };

  const canManageCreditSalesUi =
    isAdmin() ||
    isSocio() ||
    isMaster() ||
    ['admin', 'socio', 'master'].includes(String(cmsUser?.role || ''));

  const handleSave = async () => {
    // Verificar se o usuário está autenticado
    if (!isAuthenticated || !token) {
      // Tentar recuperar token do localStorage como fallback
      const authStorage = localStorage.getItem('auth-storage');
      const legacyStorage = localStorage.getItem('_pv001');
      const directToken = localStorage.getItem('token') || localStorage.getItem('authToken');
      
      if (!authStorage && !legacyStorage && !directToken) {
        alert('Você precisa estar logado para salvar as configurações. Por favor, faça login novamente.');
        // Redirecionar para login
        redirectToLogin();
        return;
      } else {
        // Token existe mas não está sincronizado - recarregar página para sincronizar
        window.location.reload();
        return;
      }
    }
    
    try {
      setSaving(true);
      
      // Verificar novamente se o token está disponível antes de salvar
      let finalToken: string | null = token;
      if (!finalToken) {
        // Tentar recuperar do localStorage como fallback
        try {
          const authStorage = localStorage.getItem('auth-storage');
          if (authStorage) {
            const authData = JSON.parse(authStorage);
            finalToken = authData.state?.token || authData.token || null;
          }
          if (!finalToken) {
            const legacyStorage = localStorage.getItem('_pv001');
            if (legacyStorage) {
              const legacyData = JSON.parse(legacyStorage);
              finalToken = legacyData.t || null;
            }
          }
          if (!finalToken) {
            finalToken = localStorage.getItem('token') || localStorage.getItem('authToken') || null;
          }
        } catch (e) {
        }
      }
      
      if (!finalToken) {
        alert('Token de autenticação não encontrado. Por favor, faça login novamente.');
        redirectToLogin();
        return;
      }
      
      // Validate form before saving
      if (!validateForm(formData)) {
        alert('Por favor, corrija os erros no formulário antes de salvar.');
        return;
      }
      
      // Se há um slug novo, verificar se está disponível
      if (cmsUser?.cmsSlug && cmsUser.cmsSlug !== formData.cmsName?.toLowerCase().replace(/[^a-z0-9-]/g, '-')) {
        await checkSlugAvailability(cmsUser.cmsSlug);
        if (slugAvailable === false) {
          alert('O slug escolhido não está disponível. Escolha outro.');
          return;
        }
      }
      
      // Separar settings do usuário de cmsSettings
      const { bannerUrl, homeBackgroundUrl, customDomain, binstreamApiUrl, ...userSettings } = formData.settings;
      
      const payloadToSend: any = {
        ...formData,
        cmsSlug: cmsUser?.cmsSlug,
        mercadoPago: mercadoPagoConfig,
        updePix: updePixConfig,
        lynx: lynxConfig,
        settings: {
          ...userSettings,
          revenue_per_user: Number(revenuePerUser) || undefined
        },
        cmsSettings: {
          theme: formData.settings.theme,
          language: formData.settings.language,
          customDomain: customDomain || '',
          binstreamApiUrl: binstreamApiUrl || '',
          bannerUrl: bannerUrl || '',
          homeBackgroundUrl: homeBackgroundUrl || '',
          inAppPaymentEnabled: Boolean(formData.inAppPaymentEnabled)
        }
      };

      if (canManageCreditSalesUi) {
        payloadToSend.creditSales = creditSalesConfig;
      }

      // Incluir appUpdate se não for client (admin ou reseller/sócio)
      // IMPORTANTE: Sempre incluir appUpdate se não for client, mesmo que não tenha mudado
      if (cmsUser?.role !== 'client') {
        // Garantir que appUpdate existe no formData
        if (!formData.appUpdate) {
          // Se não existe, criar com valores padrão
          formData.appUpdate = {
            enabled: false,
            latestVersionCode: 1,
            latestVersionName: '1.0.0',
            apkDownloadUrl: '',
            updateMessage: 'Nova versão disponível! Atualize para ter acesso às últimas funcionalidades.',
            forceUpdate: false,
            minSupportedVersion: 1
          };
        }
        payloadToSend.appUpdate = formData.appUpdate;
      }

      if (cmsUser?.role !== 'client') {
        const notice = {
          enabled: false,
          id: '',
          title: '',
          message: '',
          severity: 'info',
          updatedAt: '',
          ...(formData.inAppNotice || {})
        };
        // Comunicado novo OU texto alterado desde a última publicação → gera ID novo,
        // fazendo o app reexibir o aviso mesmo para quem já tinha visto o anterior.
        if (notice.enabled && (inAppNoticeDirty || !notice.id)) {
          notice.id = `notice_${Date.now()}`;
          notice.updatedAt = new Date().toISOString();
        }
        payloadToSend.inAppNotice = notice;
        // Reflete o ID gerado no formulário e limpa o "dirty" (já publicado).
        setFormData((prev: any) => ({ ...prev, inAppNotice: notice }));
        setInAppNoticeDirty(false);
      }

      // Incluir gamesOfDay para admin e reseller
      if (cmsUser?.role !== 'client') {
        if (!formData.gamesOfDay) {
          formData.gamesOfDay = {
            enabled: false,
            apiKey: '',
            countryId: 'BR',
            countryName: 'Brasil',
            pageUrl: '',
            broadcastHintsUrl: '',
            autoBroadcastChannels: true
          };
        }
        payloadToSend.gamesOfDay = formData.gamesOfDay;
      }

      // Incluir dpiBypass para admin e reseller (sempre porta 1080 - padrão SOCKS5)
      if (cmsUser?.role !== 'client') {
        payloadToSend.dpiBypass = {
          enabled: formData.dpiBypass?.enabled || false,
          proxyPort: 1080
        };
        // Híbrido = só IPTV Xtream + BinStream no app. Edge/mesh legado desligado no save.
        payloadToSend.streamingEdge = streamingEdgePayloadFromMode('iptv', '');
      }

      // Incluir appPromotionSettings para admin e reseller
      if (cmsUser?.role !== 'client') {
        if (!formData.appPromotionSettings) {
          formData.appPromotionSettings = {
            enabled: false,
            promotionBox: {
              title: 'ATENÇÃO!',
              price: 'R$19,90/MÊS',
              priceLabel: 'Nosso app custa apenas:',
              description: 'O melhores conteúdos',
              highlightText: 'Sempre Atualizados!',
              benefits: ['Canais, Filmes e Séries', 'Dê 5 estrelas', 'Deixe um comentário'],
              buttonText: 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
              buttonLink: '',
              contactTitle: '',
              phoneNumber: '',
              whatsappLink: ''
            },
            supportBox: {
              enabled: true,
              title: 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP',
              phoneNumber: '',
              whatsappLink: '',
              imageUrl: ''
            },
            startDate: '',
            endDate: ''
          };
        }
        
        // IMPORTANTE: Garantir que os dados do promotionBox sejam enviados corretamente
        // NÃO sobrescrever depois de ajustar os valores
        const promotionSettings = { ...formData.appPromotionSettings };
        
        // Garantir que campos vazios sejam salvos como string vazia, não null ou undefined
        // Usar ?? para preservar strings vazias explicitas
        if (promotionSettings.promotionBox) {
          promotionSettings.promotionBox = {
            ...promotionSettings.promotionBox,
            phoneNumber: promotionSettings.promotionBox.phoneNumber ?? '',
            whatsappLink: promotionSettings.promotionBox.whatsappLink ?? '',
            contactTitle: promotionSettings.promotionBox.contactTitle ?? ''
          };
        }
        
        // Manter supportBox apenas para compatibilidade, mas não usar para novos dados
        if (promotionSettings.supportBox) {
          promotionSettings.supportBox = {
            ...promotionSettings.supportBox,
            phoneNumber: promotionSettings.supportBox.phoneNumber ?? '',
            whatsappLink: promotionSettings.supportBox.whatsappLink ?? ''
          };
        }
        
        payloadToSend.appPromotionSettings = promotionSettings;
      }

      const saveResult = await simpleApiService.updateCmsUserProfile(payloadToSend);
      
      // Se a resposta contém os dados do usuário atualizado, usar diretamente
      // Caso contrário, invalidar cache e recarregar
      if (saveResult?.user) {
        // Usar os dados retornados diretamente do salvamento
        const u = saveResult.user;
        const cmsData: CmsUserData = {
          _id: u._id,
          username: u.username,
          email: u.email,
          name: u.name || u.username,
          cmsName: u.cmsName || u.username,
          logoUrl: u.logoUrl || '',
          cmsSlug: u.cmsSlug || u.username,
          colors: u.colors || {
            primary: '#3B82F6',
            secondary: '#1E40AF',
            accent: '#F59E0B',
            background: '#F8FAFC',
            text: '#1F2937'
          },
          settings: u.settings || {
            notifications: { email: true, push: false, sms: false },
            theme: 'light',
            language: 'pt-BR',
            customDomain: u.cmsSettings?.customDomain || '',
            binstreamApiUrl: u.cmsSettings?.binstreamApiUrl || '',
            bannerUrl: u.cmsSettings?.bannerUrl || '',
            homeBackgroundUrl: u.cmsSettings?.homeBackgroundUrl || ''
          },
          cmsSettings: u.cmsSettings || {
            theme: 'light',
            language: 'pt-BR',
            customDomain: '',
            binstreamApiUrl: '',
            bannerUrl: '',
            homeBackgroundUrl: ''
          },
          appUpdate: u.appUpdate || {
            enabled: false,
            latestVersionCode: 1,
            latestVersionName: '1.0.0',
            apkDownloadUrl: '',
            updateMessage: 'Nova versão disponível! Atualize para ter acesso às últimas funcionalidades.',
            forceUpdate: false,
            minSupportedVersion: 1
          },
          inAppNotice: u.inAppNotice ? {
            enabled: u.inAppNotice.enabled || false,
            id: u.inAppNotice.id || '',
            title: u.inAppNotice.title || '',
            message: u.inAppNotice.message || '',
            severity: ['info', 'warning', 'danger'].includes(u.inAppNotice.severity) ? u.inAppNotice.severity : 'info',
            updatedAt: u.inAppNotice.updatedAt
              ? (typeof u.inAppNotice.updatedAt === 'string'
                ? u.inAppNotice.updatedAt
                : new Date(u.inAppNotice.updatedAt).toISOString())
              : ''
          } : {
            enabled: false,
            id: '',
            title: '',
            message: '',
            severity: 'info',
            updatedAt: ''
          },
          gamesOfDay: u.gamesOfDay || {
            enabled: false,
            apiKey: '',
            countryId: 'BR',
            countryName: 'Brasil',
            pageUrl: '',
            broadcastHintsUrl: ''
          },
          dpiBypass: u.dpiBypass || {
            enabled: false,
            proxyPort: 1080
          },
          streamingEdge: streamingEdgeForForm({
            ...u.streamingEdge,
            edgeApiUrl: u.streamingEdge?.edgeApiUrl || DEFAULT_EDGE_API_PRIMARY
          }),
          appPromotionSettings: u.appPromotionSettings || {
            enabled: false,
            promotionBox: {
              title: 'ATENÇÃO!',
              price: 'R$19,90/MÊS',
              priceLabel: 'Nosso app custa apenas:',
              description: '',
              highlightText: '',
              benefits: [],
              buttonText: 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
              buttonLink: '',
              contactTitle: '',
              phoneNumber: '',
              whatsappLink: ''
            },
            supportBox: {
              enabled: true,
              title: 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP',
              phoneNumber: '',
              whatsappLink: '',
              imageUrl: ''
            },
            startDate: '',
            endDate: ''
          },
          mercadoPago: u.mercadoPago || {
            enabled: false,
            publicKey: '',
            accessToken: '',
            webhookUrl: '',
            environment: 'sandbox',
            renewalMonthPriceBrl: 29.9
          },
          role: u.role,
          status: u.status,
          createdAt: u.createdAt,
          updatedAt: u.updatedAt
        };
        
        setCmsUser(cmsData);
        // Atualizar formData com os dados retornados
        setFormData({
          username: cmsData.username,
          email: cmsData.email,
          name: cmsData.name,
          cmsName: cmsData.cmsName,
          logoUrl: cmsData.logoUrl || '',
          colors: cmsData.colors,
          settings: {
            ...cmsData.settings,
            customDomain: u.cmsSettings?.customDomain || cmsData.cmsSettings?.customDomain || '',
            binstreamApiUrl: u.cmsSettings?.binstreamApiUrl || cmsData.cmsSettings?.binstreamApiUrl || '',
            bannerUrl: u.cmsSettings?.bannerUrl || cmsData.cmsSettings?.bannerUrl || '',
            homeBackgroundUrl: u.cmsSettings?.homeBackgroundUrl || cmsData.cmsSettings?.homeBackgroundUrl || ''
          },
          cmsSlug: cmsData.cmsSlug || '',
          appUpdate: cmsData.appUpdate ? {
            enabled: cmsData.appUpdate.enabled || false,
            latestVersionCode: cmsData.appUpdate.latestVersionCode || 1,
            latestVersionName: cmsData.appUpdate.latestVersionName || '1.0.0',
            apkDownloadUrl: cmsData.appUpdate.apkDownloadUrl || '',
            updateMessage: cmsData.appUpdate.updateMessage || 'Nova versão disponível! Atualize para ter acesso às últimas funcionalidades.',
            forceUpdate: cmsData.appUpdate.forceUpdate || false,
            minSupportedVersion: cmsData.appUpdate.minSupportedVersion || 1
          } : {
            enabled: false,
            latestVersionCode: 1,
            latestVersionName: '1.0.0',
            apkDownloadUrl: '',
            updateMessage: 'Nova versão disponível! Atualize para ter acesso às últimas funcionalidades.',
            forceUpdate: false,
            minSupportedVersion: 1
          },
          inAppNotice: cmsData.inAppNotice ? {
            enabled: cmsData.inAppNotice.enabled || false,
            id: cmsData.inAppNotice.id || '',
            title: cmsData.inAppNotice.title || '',
            message: cmsData.inAppNotice.message || '',
            severity: ['info', 'warning', 'danger'].includes(cmsData.inAppNotice.severity)
              ? cmsData.inAppNotice.severity
              : 'info',
            updatedAt: cmsData.inAppNotice.updatedAt || ''
          } : {
            enabled: false,
            id: '',
            title: '',
            message: '',
            severity: 'info',
            updatedAt: ''
          },
          gamesOfDay: cmsData.gamesOfDay ? {
            enabled: cmsData.gamesOfDay.enabled || false,
            apiKey: cmsData.gamesOfDay.apiKey || '',
            filteredCountries: cmsData.gamesOfDay.filteredCountries || [],
            filteredLeagues: cmsData.gamesOfDay.filteredLeagues || [],
            countryId: cmsData.gamesOfDay.countryId || 'BR',
            countryName: cmsData.gamesOfDay.countryName || 'Brasil',
            pageUrl: cmsData.gamesOfDay.pageUrl || '',
            broadcastHintsUrl: cmsData.gamesOfDay.broadcastHintsUrl || '',
            autoBroadcastChannels: cmsData.gamesOfDay.autoBroadcastChannels !== false
          } : {
            enabled: false,
            apiKey: '',
            filteredCountries: [],
            filteredLeagues: [],
            countryId: 'BR',
            countryName: 'Brasil',
            pageUrl: '',
            broadcastHintsUrl: '',
            autoBroadcastChannels: true
          },
          dpiBypass: cmsData.dpiBypass ? {
            enabled: cmsData.dpiBypass.enabled || false,
            proxyPort: cmsData.dpiBypass.proxyPort || 1080
          } : {
            enabled: false,
            proxyPort: 1080
          },
          streamingEdge: streamingEdgeForForm({
            ...cmsData.streamingEdge,
            edgeApiUrl: cmsData.streamingEdge?.edgeApiUrl || DEFAULT_EDGE_API_PRIMARY
          }),
          appPromotionSettings: cmsData.appPromotionSettings ? {
            enabled: cmsData.appPromotionSettings.enabled || false,
            promotionBox: cmsData.appPromotionSettings.promotionBox ? {
              title: cmsData.appPromotionSettings.promotionBox.title ?? 'ATENÇÃO!',
              price: cmsData.appPromotionSettings.promotionBox.price ?? 'R$19,90/MÊS',
              priceLabel: cmsData.appPromotionSettings.promotionBox.priceLabel ?? 'Nosso app custa apenas:',
              description: cmsData.appPromotionSettings.promotionBox.description ?? '',
              highlightText: cmsData.appPromotionSettings.promotionBox.highlightText ?? '',
              benefits: Array.isArray(cmsData.appPromotionSettings.promotionBox.benefits) ? cmsData.appPromotionSettings.promotionBox.benefits : ['Canais, Filmes e Séries', 'Dê 5 estrelas', 'Deixe um comentário'],
              buttonText: cmsData.appPromotionSettings.promotionBox.buttonText ?? 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
              buttonLink: cmsData.appPromotionSettings.promotionBox.buttonLink ?? '',
              contactTitle: cmsData.appPromotionSettings.promotionBox.contactTitle ?? '',
              phoneNumber: cmsData.appPromotionSettings.promotionBox.phoneNumber ?? '',
              whatsappLink: cmsData.appPromotionSettings.promotionBox.whatsappLink ?? ''
            } : {
              title: 'ATENÇÃO!',
              price: 'R$19,90/MÊS',
              priceLabel: 'Nosso app custa apenas:',
              description: '',
              highlightText: '',
              benefits: ['Canais, Filmes e Séries', 'Dê 5 estrelas', 'Deixe um comentário'],
              buttonText: 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
              buttonLink: '',
              contactTitle: '',
              phoneNumber: '',
              whatsappLink: ''
            },
            supportBox: cmsData.appPromotionSettings.supportBox ? {
              enabled: cmsData.appPromotionSettings.supportBox.enabled !== undefined ? cmsData.appPromotionSettings.supportBox.enabled : true,
              title: cmsData.appPromotionSettings.supportBox.title ?? 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP',
              phoneNumber: cmsData.appPromotionSettings.supportBox.phoneNumber ?? '',
              whatsappLink: cmsData.appPromotionSettings.supportBox.whatsappLink ?? '',
              imageUrl: cmsData.appPromotionSettings.supportBox.imageUrl ?? ''
            } : {
              enabled: true,
              title: 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP',
              phoneNumber: '',
              whatsappLink: '',
              imageUrl: ''
            },
            startDate: cmsData.appPromotionSettings.startDate ? (typeof cmsData.appPromotionSettings.startDate === 'string' ? cmsData.appPromotionSettings.startDate : new Date(cmsData.appPromotionSettings.startDate).toISOString().split('T')[0]) : '',
            endDate: cmsData.appPromotionSettings.endDate ? (typeof cmsData.appPromotionSettings.endDate === 'string' ? cmsData.appPromotionSettings.endDate : new Date(cmsData.appPromotionSettings.endDate).toISOString().split('T')[0]) : ''
          } : {
            enabled: false,
            promotionBox: {
              title: 'ATENÇÃO!',
              price: 'R$19,90/MÊS',
              priceLabel: 'Nosso app custa apenas:',
              description: '',
              highlightText: '',
              benefits: ['Canais, Filmes e Séries', 'Dê 5 estrelas', 'Deixe um comentário'],
              buttonText: 'INDIQUE 2 AMIGOS E GANHE 1 MÊS',
              buttonLink: '',
              contactTitle: '',
              phoneNumber: '',
              whatsappLink: ''
            },
            supportBox: {
              enabled: true,
              title: 'PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP',
              phoneNumber: '',
              whatsappLink: '',
              imageUrl: ''
            },
            startDate: '',
            endDate: ''
          },
          inAppPaymentEnabled: Boolean(u.cmsSettings?.inAppPaymentEnabled)
        });
        
        // Atualizar configurações do Mercado Pago se existirem
        if (u.mercadoPago) {
          setMercadoPagoConfig({
            enabled: u.mercadoPago.enabled || false,
            publicKey: u.mercadoPago.publicKey || '',
            accessToken: u.mercadoPago.accessToken || '',
            webhookUrl: u.mercadoPago.webhookUrl || '',
            environment: u.mercadoPago.environment || 'sandbox',
            renewalMonthPriceBrl:
              u.mercadoPago.renewalMonthPriceBrl != null &&
              Number.isFinite(Number(u.mercadoPago.renewalMonthPriceBrl))
                ? Number(u.mercadoPago.renewalMonthPriceBrl)
                : 29.9,
            plans:
              Array.isArray(u.mercadoPago.plans) && u.mercadoPago.plans.length
                ? u.mercadoPago.plans.map((p: Record<string, unknown>) =>
                    normalizeMercadoPagoPlan(p)
                  )
                : [
                    normalizeMercadoPagoPlan({
                      id: 'mensal',
                      label: 'Mensal',
                      months: 1,
                      priceBrl: 29.9,
                      active: true
                    })
                  ]
          });
        }
      } else {
        // Fallback: Invalidar cache e recarregar dados
        simpleApiService.invalidateCmsUser();
        await fetchCmsUser();
      }
      
      // Show success message (you can add a toast notification here)
      alert('Configurações salvas com sucesso!');
    } catch (error) {
      alert('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const generateNewApiKey = async () => {
    try {
      if (!cmsUser?._id) return;
      
      setSaving(true);
      await simpleApiService.generateCmsUserApiKey(cmsUser._id);
      
      // Refresh user data to get new API key
      await fetchCmsUser();
      
      alert('Nova API key gerada com sucesso!');
    } catch (error) {
      alert('Erro ao gerar API key. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const copyApiKey = () => {
    if (cmsUser?.tokens?.apiKey) {
      navigator.clipboard.writeText(cmsUser.tokens.apiKey);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    }
  };

  // Funções para configurações do Mercado Pago
  const handleMercadoPagoConfigChange = (field: string, value: any) => {
    setMercadoPagoConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const copyMercadoPagoKey = (keyType: 'publicKey' | 'accessToken') => {
    const keyValue = mercadoPagoConfig[keyType];
    if (keyValue) {
      navigator.clipboard.writeText(keyValue);
      setMercadoPagoKeysCopied(prev => ({
        ...prev,
        [keyType]: true
      }));
      setTimeout(() => {
        setMercadoPagoKeysCopied(prev => ({
          ...prev,
          [keyType]: false
        }));
      }, 2000);
    }
  };

  const toggleMercadoPagoKeyVisibility = (keyType: 'publicKey' | 'accessToken') => {
    setShowMercadoPagoKeys(prev => ({
      ...prev,
      [keyType]: !prev[keyType]
    }));
  };

  const generateWebhookUrl = () => {
    const baseUrl = window.location.origin;
    // Não usar valor padrão - usar slug real ou não gerar URL se não tiver slug
    const slug = cmsUser?.cmsSlug || (window.location.pathname.split('/')[1] || '');
    if (!slug) {
      alert('Slug do CMS não encontrado. Configure o slug antes de gerar a URL do webhook.');
      return;
    }
    const webhookUrl = `${baseUrl}/api/webhooks/mercado-pago/${slug}`;
    setMercadoPagoConfig(prev => ({
      ...prev,
      webhookUrl
    }));
  };

  const testMercadoPagoConnection = async () => {
    try {
      setSaving(true);
      // Aqui implementaríamos a chamada para testar a conexão com o Mercado Pago
      // Por enquanto, apenas simular
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Conexão com Mercado Pago testada com sucesso!');
    } catch (error) {
      alert('Erro ao testar conexão com Mercado Pago');
    } finally {
      setSaving(false);
    }
  };

  // Funções para configurações do UpdePix
  const handleUpdePixConfigChange = (field: string, value: any) => {
    setUpdePixConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreditSalesChange = (field: 'enabled' | 'packages', value: any) => {
    setCreditSalesConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const copyUpdePixKey = () => {
    const keyValue = updePixConfig.apiKey;
    if (!keyValue) return;
    navigator.clipboard.writeText(keyValue);
    setUpdePixKeyCopied(true);
    setTimeout(() => setUpdePixKeyCopied(false), 2000);
  };

  const copyUpdePixWebhookSecret = () => {
    const keyValue = updePixConfig.webhookSecret;
    if (!keyValue) return;
    navigator.clipboard.writeText(keyValue);
    setUpdePixWebhookSecretCopied(true);
    setTimeout(() => setUpdePixWebhookSecretCopied(false), 2000);
  };

  const handleLynxConfigChange = (field: string, value: any) => {
    setLynxConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const copyLynxKey = () => {
    const keyValue = lynxConfig.apiKey;
    if (!keyValue) return;
    navigator.clipboard.writeText(keyValue);
    setLynxKeyCopied(true);
    setTimeout(() => setLynxKeyCopied(false), 2000);
  };

  const copyLynxWebhookSecret = () => {
    const keyValue = lynxConfig.webhookSecret;
    if (!keyValue) return;
    navigator.clipboard.writeText(keyValue);
    setLynxWebhookSecretCopied(true);
    setTimeout(() => setLynxWebhookSecretCopied(false), 2000);
  };


  const resetToDefaults = () => {
    setFormData((prev: any) => ({
      ...prev,
      colors: {
        primary: '#3B82F6',
        secondary: '#1E40AF',
        accent: '#F59E0B',
        background: '#F8FAFC',
        text: '#1F2937'
      }
    }));
  };

  // Função de validação com debounce
  const debouncedCheckSlug = (slug: string) => {
    // Limpar timer anterior se existir
    if (slugDebounceTimer) {
      clearTimeout(slugDebounceTimer);
    }

    // Se slug vazio ou igual ao atual, limpar estado
    if (!slug || slug === cmsUser?.cmsSlug) {
      setSlugAvailable(null);
      setSlugChecking(false);
      return;
    }

    // Se slug muito curto, não validar
    if (slug.length < 3) {
      setSlugAvailable(null);
      setSlugChecking(false);
      return;
    }

    // Configurar novo timer com debounce de 500ms
    const timer = setTimeout(async () => {
      await checkSlugAvailability(slug);
    }, 500);

    setSlugDebounceTimer(timer);
  };

  const checkSlugAvailability = async (slug: string) => {
    setSlugChecking(true);
    try {
      const client: any = (apiService as any).api;
      const response = await client.get(`/api/users/cms/slug/check/${slug}`);
      setSlugAvailable(Boolean(response?.data?.available));
    } catch (error) {
      setSlugAvailable(false);
    } finally {
      setSlugChecking(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Carregando configurações...</span>
      </div>
    );
  }

  if (!cmsUser) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Erro ao carregar dados do usuário.</p>
        <Button onClick={fetchCmsUser} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Perfil', icon: User },
    { id: 'appearance', label: 'Aparência', icon: Palette },
    { id: 'notifications', label: 'Notificações', icon: Bell },
    { id: 'security', label: 'Segurança', icon: Key },
    { id: 'payments', label: 'Pagamentos', icon: CreditCard },
    { id: 'trial', label: 'Gerador de Testes', icon: TestTube },
    ...(cmsUser.role !== 'client' ? [{ id: 'whatsapp', label: 'Automação WhatsApp', icon: MessageCircle }] : []),
    ...(cmsUser.role !== 'client' ? [{ id: 'appUpdate', label: 'Atualização do App', icon: Smartphone }] : []),
    ...(cmsUser.role !== 'client' ? [{ id: 'appPromotion', label: 'Promoção do App', icon: Smartphone }] : []),
    ...(cmsUser.role !== 'client' ? [{ id: 'inAppNotice', label: 'Comunicado no App', icon: Bell }] : []),
    ...(cmsUser.role !== 'client' ? [{ id: 'gamesOfDay', label: 'Jogos do Dia', icon: Trophy }] : []),
    ...(cmsUser.role === 'admin' ? [{ id: 'system', label: 'Manutenção', icon: Server }] : [])
  ];

  return (
    <div className="space-y-4 sm:space-y-6" >
      {/* Header - Responsivo */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Configurações</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Gerencie suas configurações pessoais e preferências do CMS
          </p>
        </div>
        <Badge variant="outline" className="flex items-center gap-2 w-fit">
          <SettingsIcon className="h-3 w-3 sm:h-4 sm:w-4" />
          <span className="text-xs sm:text-sm">{cmsUser.role}</span>
        </Badge>
      </div>

      {/* Tabs - Responsivo */}
      <div className="border-b overflow-x-auto scrollbar-thin">
        <nav className="-mb-px flex space-x-2 sm:space-x-4 md:space-x-8 min-w-max sm:min-w-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-1 sm:gap-2 py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-secondary text-secondary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                <Icon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden xs:inline sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card>
            <CardHeader>
              <CardTitle>Informações do Perfil</CardTitle>
              <CardDescription>
                Atualize suas informações pessoais e dados de contato
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Nome de usuário</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    onBlur={(e) => handleInputBlur('username', e.target.value)}
                    placeholder="Digite seu nome de usuário"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={(e) => handleInputBlur('email', e.target.value)}
                    placeholder="Digite seu e-mail"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome completo</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onBlur={(e) => handleInputBlur('name', e.target.value)}
                    placeholder="Digite seu nome completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cmsName">Nome do CMS</Label>
                  <Input
                    id="cmsName"
                    value={formData.cmsName}
                    onChange={(e) => handleInputChange('cmsName', e.target.value)}
                    onBlur={(e) => handleInputBlur('cmsName', e.target.value)}
                    placeholder="Nome personalizado para seu CMS"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Appearance Tab */}
        {activeTab === 'appearance' && (
          <div className="space-y-6">
            {/* Receita por usuário (para cálculo do dashboard) */}
            <Card>
              <CardHeader>
                <CardTitle>Receita por Usuário</CardTitle>
                <CardDescription>
                  Defina o valor em R$ por usuário ativo para calcular a receita no dashboard
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 max-w-sm">
                  <Label htmlFor="revenuePerUser">Valor por usuário (R$)</Label>
                  <Input
                    id="revenuePerUser"
                    type="number"
                    step="0.01"
                    min="0"
                    value={revenuePerUser}
                    onChange={(e) => setRevenuePerUser(e.target.value)}
                    placeholder="Ex: 20.00"
                  />
                  <p className="text-sm text-muted-foreground">
                    Este valor será usado no cálculo de Receita Total no Dashboard.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customDomain">Domínio Personalizado do Servidor (IPTV / XUI)</Label>
                  <Input
                    id="customDomain"
                    type="url"
                    value={formData.settings.customDomain || ''}
                    onChange={(e) => handleInputChange('settings.customDomain', e.target.value)}
                    placeholder="https://meuservidor.com ou servidor.meudominio.com"
                  />
                  <p className="text-sm text-muted-foreground">
                    Domínio Xtream/XUI usado pelo login IPTV do APK. Não misturar com a API BinStream.
                  </p>
                  <p className="text-xs text-amber-600">
                    ⚠️ Certifique-se de que o domínio está configurado corretamente e acessível.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="binstreamApiUrl">API BinStream / P2P (U2P)</Label>
                  <Input
                    id="binstreamApiUrl"
                    type="url"
                    value={formData.settings.binstreamApiUrl || ''}
                    onChange={(e) => handleInputChange('settings.binstreamApiUrl', e.target.value)}
                    placeholder="https://api1.hojevai.com"
                  />
                  <p className="text-sm text-muted-foreground">
                    Base da API de autenticação BinStream (padrão: https://api1.hojevai.com). O APK tenta este
                    endpoint em paralelo ao IPTV; se o XUI falhar e o P2P aceitar as credenciais, entra na grade P2P.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CMS Slug Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>URL Personalizada do CMS</CardTitle>
                <CardDescription>
                  Configure uma URL personalizada para acessar seu CMS
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cmsSlug">Slug do CMS</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="cmsSlug"
                      placeholder="meu-cms"
                      value={cmsUser?.cmsSlug || ''}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setCmsUser(prev => prev ? { ...prev, cmsSlug: value } : null);
                        debouncedCheckSlug(value);
                      }}
                      className="flex-1"
                    />
                  </div>
                  
                  {slugChecking && (
                    <p className="text-sm text-blue-600">Verificando disponibilidade...</p>
                  )}
                  
                  {slugAvailable === true && (
                    <p className="text-sm text-green-600">✅ Slug disponível!</p>
                  )}
                  
                  {slugAvailable === false && (
                    <p className="text-sm text-red-600">❌ Slug já está em uso</p>
                  )}
                  
                  <p className="text-sm text-muted-foreground">
                    Apenas letras minúsculas, números e hífens são permitidos
                  </p>
                </div>
                
                {cmsUser?.cmsSlug && (
                  <div className="space-y-2">
                    <Label>URL do seu CMS</Label>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-mono">
                        {window.location.origin}/{cmsUser.cmsSlug}/login
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Esta será a URL personalizada para acessar seu CMS
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Logo Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Logo do CMS</CardTitle>
                <CardDescription>
                  Configure a logo personalizada do seu CMS
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="logoUrl">URL da Logo</Label>
                  <Input
                    id="logoUrl"
                    type="url"
                    placeholder="https://exemplo.com/logo.png"
                    value={formData.logoUrl || ''}
                    onChange={(e) => handleInputChange('logoUrl', e.target.value)}
                    className="input-dynamic"
                  />
                  <p className="text-sm text-muted-foreground">
                    Insira a URL completa da imagem da sua logo
                  </p>
                </div>
                
                {formData.logoUrl && (
                  <div className="space-y-2">
                    <Label>Preview da Logo</Label>
                    <div className="flex items-center space-x-4 p-4 border rounded-lg">
                      <img 
                        src={formData.logoUrl} 
                        alt="Preview da logo"
                        className="h-12 w-12 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = 'block';
                          }
                        }}
                      />
                      <div className="hidden text-sm text-muted-foreground">
                        Erro ao carregar a imagem. Verifique se a URL está correta.
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Banner Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Banner da Home</CardTitle>
                <CardDescription>
                  Configure o banner que será exibido na página Home do aplicativo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bannerUrl">URL do Banner</Label>
                  <Input
                    id="bannerUrl"
                    type="url"
                    placeholder="https://exemplo.com/banner.jpg"
                    value={formData.settings.bannerUrl || ''}
                    onChange={(e) => handleInputChange('settings.bannerUrl', e.target.value)}
                    className="input-dynamic"
                  />
                  <p className="text-sm text-muted-foreground">
                    Insira a URL completa da imagem do banner que será exibido na Home do app
                  </p>
                </div>
                
                {formData.settings.bannerUrl && (
                  <div className="space-y-2">
                    <Label>Preview do Banner</Label>
                    <div className="p-4 border rounded-lg">
                      <img 
                        src={formData.settings.bannerUrl} 
                        alt="Preview do banner"
                        className="w-full h-auto max-h-64 object-contain rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = 'block';
                          }
                        }}
                      />
                      <div className="hidden text-sm text-muted-foreground">
                        Erro ao carregar a imagem. Verifique se a URL está correta.
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Fundo da home (app)</CardTitle>
                <CardDescription>
                  Imagem opcional em tela cheia atrás do conteúdo na home do aplicativo; a cor de fundo do tema
                  aplica um véu semitransparente por cima.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="homeBackgroundUrl">URL da imagem de fundo</Label>
                  <Input
                    id="homeBackgroundUrl"
                    type="url"
                    placeholder="https://exemplo.com/fundo.jpg"
                    value={formData.settings.homeBackgroundUrl || ''}
                    onChange={(e) => handleInputChange('settings.homeBackgroundUrl', e.target.value)}
                    className="input-dynamic"
                  />
                  <p className="text-sm text-muted-foreground">
                    Deixe em branco para usar apenas o gradiente de cores. Recomendado: imagem em paisagem, boa
                    resolução.
                  </p>
                </div>
                {formData.settings.homeBackgroundUrl && (
                  <div className="space-y-2">
                    <Label>Preview</Label>
                    <div className="p-4 border rounded-lg overflow-hidden max-h-64">
                      <img
                        src={formData.settings.homeBackgroundUrl}
                        alt="Preview do fundo"
                        className="w-full h-48 object-cover rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = 'block';
                          }
                        }}
                      />
                      <div className="hidden text-sm text-muted-foreground">
                        Erro ao carregar a imagem. Verifique se a URL está correta.
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Personalização de Cores</CardTitle>
                <CardDescription>
                  Personalize as cores do seu CMS para integração com aplicativos futuros
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(formData.colors).map(([colorType, colorValue]) => (
                    <ColorPicker
                      key={colorType}
                      label={colorType}
                      value={colorValue as string}
                      onChange={(value) => handleColorChange(colorType, value)}
                    />
                  ))}
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Preview das Cores</h4>
                    <p className="text-sm text-muted-foreground">
                      Visualize como ficará a combinação de cores
                    </p>
                  </div>
                  <Button variant="outline" onClick={resetToDefaults}>
                    Restaurar Padrão
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {/* Preview Simples das Cores */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-700">Preview das Cores</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        className="p-3 rounded border"
                        style={{ backgroundColor: formData.colors.primary, color: 'white' }}
                      >
                        <h5 className="font-semibold text-sm">Primária</h5>
                        <p className="text-xs opacity-90">{formData.colors.primary}</p>
                      </div>
                      <div 
                        className="p-3 rounded border"
                        style={{ backgroundColor: formData.colors.secondary, color: 'white' }}
                      >
                        <h5 className="font-semibold text-sm">Secundária</h5>
                        <p className="text-xs opacity-90">{formData.colors.secondary}</p>
                      </div>
                      <div 
                        className="p-3 rounded border"
                        style={{ backgroundColor: formData.colors.accent, color: 'white' }}
                      >
                        <h5 className="font-semibold text-sm">Destaque</h5>
                        <p className="text-xs opacity-90">{formData.colors.accent}</p>
                      </div>
                      <div 
                        className="p-3 rounded border"
                        style={{ backgroundColor: formData.colors.text, color: 'white' }}
                      >
                        <h5 className="font-semibold text-sm">Texto</h5>
                        <p className="text-xs opacity-90">{formData.colors.text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Preferências de Interface</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="theme">Tema</Label>
                    <select
                      id="theme"
                      value={formData.settings.theme}
                      onChange={(e) => handleInputChange('settings.theme', e.target.value)}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="light">Claro</option>
                      <option value="dark">Escuro</option>
                      <option value="auto">Automático</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="language">Idioma</Label>
                    <select
                      id="language"
                      value={formData.settings.language}
                      onChange={(e) => handleInputChange('settings.language', e.target.value)}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="pt-BR">Português (Brasil)</option>
                      <option value="en-US">English (US)</option>
                      <option value="es-ES">Español</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card>
            <CardHeader>
              <CardTitle>Configurações de Notificações</CardTitle>
              <CardDescription>
                Escolha como e quando você deseja receber notificações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {Object.entries(formData.settings.notifications).map(([type, enabled]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="capitalize">
                        {type === 'email' ? 'E-mail' :
                         type === 'push' ? 'Push' :
                         type === 'sms' ? 'SMS' : type}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {type === 'email' ? 'Receber notificações por e-mail' :
                         type === 'push' ? 'Receber notificações push no navegador' :
                         type === 'sms' ? 'Receber notificações por SMS' : ''}
                      </p>
                    </div>
                    <Switch
                      checked={enabled as boolean}
                      onCheckedChange={(checked) => handleInputChange(`notifications.${type}`, checked)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>API Key</CardTitle>
                <CardDescription>
                  Sua chave de API para integrações com o CMS e aplicativos futuros
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Chave de API</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type={showApiKey ? 'text' : 'password'}
                      value={cmsUser.tokens?.apiKey || 'Nenhuma chave gerada'}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyApiKey}
                      disabled={!cmsUser.tokens?.apiKey}
                    >
                      {apiKeyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {apiKeyCopied ? 'Chave copiada para a área de transferência!' : 
                     'Clique no ícone de cópia para copiar a chave'}
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={generateNewApiKey}
                    disabled={saving}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    {saving ? 'Gerando...' : 'Gerar Nova Chave'}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    ⚠️ Gerar uma nova chave invalidará a chave atual
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Informações da Conta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Função</Label>
                    <p className="text-sm">{cmsUser.role}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                    <Badge variant={cmsUser.status === 'active' ? 'default' : 'secondary'}>
                      {cmsUser.status === 'active' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Criado em</Label>
                    <p className="text-sm">{new Date(cmsUser.createdAt).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Última atualização</Label>
                    <p className="text-sm">{new Date(cmsUser.updatedAt).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <Card className="border-emerald-200/80 bg-emerald-50/40 dark:border-emerald-900/50 dark:bg-emerald-950/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Smartphone className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
                  Renovação no aplicativo (APK)
                </CardTitle>
                <CardDescription>
                  Quando ativado, o app Android pode exibir o fluxo de renovação automática (Mercado Pago e/ou PIX),
                  desde que as chaves estejam configuradas abaixo. O APK lê esta opção em{' '}
                  <code className="rounded bg-muted px-1 text-xs">cmsSettings.inAppPaymentEnabled</code> na API
                  pública.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between rounded-lg border bg-background/80 p-4">
                  <div className="space-y-0.5 pr-4">
                    <Label className="text-base font-medium">Ativar pagamento / renovação no app</Label>
                    <p className="text-sm text-muted-foreground">
                      Desligado por defeito. Ligue só depois de configurar Mercado Pago ou UpdePix nesta aba.
                    </p>
                  </div>
                  <Switch
                    checked={!!formData.inAppPaymentEnabled}
                    onCheckedChange={(checked) =>
                      setFormData((prev: any) => ({ ...prev, inAppPaymentEnabled: !!checked }))
                    }
                  />
                </div>
              </CardContent>
            </Card>
            {canManageCreditSalesUi && (
              <Card className="border-amber-200 bg-amber-50/80 dark:bg-amber-950/30 dark:border-amber-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Coins className="h-5 w-5 text-amber-600" />
                    Onde configurar preços e pacotes de créditos
                  </CardTitle>
                  <CardDescription className="text-foreground/90">
                    Role a página até a seção <strong>Venda automática de créditos (revendedores)</strong>. Ligue a opção,
                    adicione os pacotes (quantidade de créditos + preço em R$) e salve. Os revendedores vêem isso na
                    página <strong>Comprar créditos</strong> e no ícone ao lado de &quot;Créditos&quot; na barra lateral.
                    Os pagamentos usam o Mercado Pago e o UpdePix configurados nesta mesma aba.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}
            {/* Mercado Pago Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Integração Mercado Pago
                </CardTitle>
                <CardDescription>
                  Configure sua integração com o Mercado Pago para receber pagamentos automaticamente
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Switch */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Ativar Mercado Pago</Label>
                    <p className="text-sm text-muted-foreground">
                      Habilite a integração para receber pagamentos automaticamente
                    </p>
                  </div>
                  <Switch
                    checked={mercadoPagoConfig.enabled}
                    onCheckedChange={(checked) => handleMercadoPagoConfigChange('enabled', checked)}
                  />
                </div>

                {mercadoPagoConfig.enabled && (
                  <>
                    {/* Environment Selection */}
                    <div className="space-y-2">
                      <Label htmlFor="environment">Ambiente</Label>
                      <select
                        id="environment"
                        value={mercadoPagoConfig.environment}
                        onChange={(e) => handleMercadoPagoConfigChange('environment', e.target.value)}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="sandbox">Sandbox (Teste)</option>
                        <option value="production">Produção</option>
                      </select>
                      <p className="text-sm text-muted-foreground">
                        {mercadoPagoConfig.environment === 'sandbox' 
                          ? 'Use o ambiente de teste para desenvolvimento'
                          : 'Use o ambiente de produção para receber pagamentos reais'
                        }
                      </p>
                    </div>

                    {/* Public Key */}
                    <div className="space-y-2">
                      <Label htmlFor="publicKey">Chave Pública</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="publicKey"
                          type={showMercadoPagoKeys.publicKey ? 'text' : 'password'}
                          value={mercadoPagoConfig.publicKey}
                          onChange={(e) => handleMercadoPagoConfigChange('publicKey', e.target.value)}
                          placeholder="APP_USR_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleMercadoPagoKeyVisibility('publicKey')}
                        >
                          {showMercadoPagoKeys.publicKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyMercadoPagoKey('publicKey')}
                          disabled={!mercadoPagoConfig.publicKey}
                        >
                          {mercadoPagoKeysCopied.publicKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Chave pública do Mercado Pago (usada no frontend)
                      </p>
                    </div>

                    {/* Access Token */}
                    <div className="space-y-2">
                      <Label htmlFor="accessToken">Token de Acesso</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="accessToken"
                          type={showMercadoPagoKeys.accessToken ? 'text' : 'password'}
                          value={mercadoPagoConfig.accessToken}
                          onChange={(e) => handleMercadoPagoConfigChange('accessToken', e.target.value)}
                          placeholder="APP_USR_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleMercadoPagoKeyVisibility('accessToken')}
                        >
                          {showMercadoPagoKeys.accessToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyMercadoPagoKey('accessToken')}
                          disabled={!mercadoPagoConfig.accessToken}
                        >
                          {mercadoPagoKeysCopied.accessToken ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Token de acesso do Mercado Pago (usado no backend)
                      </p>
                    </div>

                    {/* Webhook URL */}
                    <div className="space-y-2">
                      <Label htmlFor="webhookUrl">URL do Webhook</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="webhookUrl"
                          value={mercadoPagoConfig.webhookUrl}
                          onChange={(e) => handleMercadoPagoConfigChange('webhookUrl', e.target.value)}
                          placeholder="https://seudominio.com/api/webhooks/mercado-pago"
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          onClick={generateWebhookUrl}
                        >
                          Gerar
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        URL para receber notificações de pagamento do Mercado Pago
                      </p>
                    </div>

                    {/* Test Connection */}
                    <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900">Testar Conexão</h4>
                        <p className="text-sm text-blue-700">
                          Verifique se suas credenciais estão corretas
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        onClick={testMercadoPagoConnection}
                        disabled={saving || !mercadoPagoConfig.publicKey || !mercadoPagoConfig.accessToken}
                      >
                        {saving ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Testando...
                          </>
                        ) : (
                          <>
                            <Shield className="h-4 w-4 mr-2" />
                            Testar Conexão
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Status Information */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        {mercadoPagoConfig.publicKey && mercadoPagoConfig.accessToken ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-yellow-600" />
                        )}
                        <span className="text-sm font-medium">
                          {mercadoPagoConfig.publicKey && mercadoPagoConfig.accessToken 
                            ? 'Configuração completa' 
                            : 'Configuração incompleta'
                          }
                        </span>
                      </div>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>• Configure suas credenciais no painel do Mercado Pago</p>
                        <p>• Use o ambiente Sandbox para testes</p>
                        <p>• Configure o webhook para receber notificações automáticas</p>
                        <p>• Cada revendedor terá sua própria integração</p>
                      </div>
                    </div>
                  </>
                )}

                {/* Preço/planos da loja pública (independente do provedor de pagamento) */}
                <div className="space-y-2">
                  <Label htmlFor="renewalMonthPriceBrl">Preço por mês (R$) — loja pública / app</Label>
                  <Input
                    id="renewalMonthPriceBrl"
                    type="number"
                    min={0}
                    step={0.01}
                    value={mercadoPagoConfig.renewalMonthPriceBrl}
                    onChange={(e) =>
                      handleMercadoPagoConfigChange(
                        'renewalMonthPriceBrl',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                  <p className="text-sm text-muted-foreground">
                    Valor usado como fallback quando não houver planos ativos. Vale para Mercado Pago e UpdePix.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Planos (até 10)</Label>
                  <p className="text-sm text-muted-foreground">
                    Defina os planos e valores em R$ que o cliente verá no site/app. Com um pacote IPTV, créditos e duração
                    na linha seguem o pacote (official_credits / official_duration). Sem pacote, a renovação usa 1 mês ×
                    conexões da linha; o campo meses no banco é atualizado automaticamente (1 ou duração do pacote).
                  </p>
                  <div className="space-y-3">
                    {(mercadoPagoConfig.plans || []).slice(0, 10).map((p: MercadoPagoPlanRow, idx: number) => (
                      <div key={`mp-plan-${idx}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 border rounded-md">
                        <div className="md:col-span-2 space-y-1">
                          <Label className="text-xs">ID</Label>
                          <Input
                            value={p.id}
                            onChange={(e) => {
                              const next = [...(mercadoPagoConfig.plans || [])];
                              next[idx] = { ...next[idx], id: e.target.value };
                              handleMercadoPagoConfigChange('plans', next);
                            }}
                            placeholder="mensal"
                          />
                        </div>
                        <div className="md:col-span-4 space-y-1">
                          <Label className="text-xs">Nome</Label>
                          <Input
                            value={p.label}
                            onChange={(e) => {
                              const next = [...(mercadoPagoConfig.plans || [])];
                              next[idx] = { ...next[idx], label: e.target.value };
                              handleMercadoPagoConfigChange('plans', next);
                            }}
                            placeholder="Mensal"
                          />
                        </div>
                        <div className="md:col-span-3 space-y-1">
                          <Label className="text-xs">Pacote IPTV</Label>
                          <Select
                            value={
                              p.iptvPackageId != null && p.iptvPackageId > 0
                                ? String(p.iptvPackageId)
                                : '__none__'
                            }
                            onValueChange={(v) => {
                              const next = [...(mercadoPagoConfig.plans || [])];
                              if (v === '__none__') {
                                next[idx] = {
                                  ...next[idx],
                                  iptvPackageId: null,
                                  months: 1
                                };
                              } else {
                                const pkgId = parseInt(v, 10) || 0;
                                const pkg = storeRenewalIptvPackages.find((x) => x.id === pkgId);
                                const dur = pkg?.official_duration;
                                const monthsFromPkg = Math.min(
                                  60,
                                  Math.max(1, parseInt(String(dur ?? 1), 10) || 1)
                                );
                                next[idx] = {
                                  ...next[idx],
                                  iptvPackageId: pkgId > 0 ? pkgId : null,
                                  months: monthsFromPkg
                                };
                              }
                              handleMercadoPagoConfigChange('plans', next);
                            }}
                            placeholder="Sem pacote"
                            className="w-full"
                          >
                            <SelectTrigger className="h-9 w-full text-sm">
                              {storeRenewalIptvPackagesLoading
                                ? 'Carregando…'
                                : p.iptvPackageId != null && p.iptvPackageId > 0
                                  ? storeRenewalIptvPackages.find(
                                      (x) => x.id === p.iptvPackageId
                                    )?.display_name || `Pacote #${p.iptvPackageId}`
                                  : 'Sem pacote'}
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Sem pacote (1 mês × conexões)</SelectItem>
                              {storeRenewalIptvPackages.map((pkg) => (
                                <SelectItem key={pkg.id} value={String(pkg.id)}>
                                  {pkg.display_name || `#${pkg.id}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2 space-y-1">
                          <Label className="text-xs">Preço (R$)</Label>
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={p.priceBrl}
                            onChange={(e) => {
                              const next = [...(mercadoPagoConfig.plans || [])];
                              next[idx] = { ...next[idx], priceBrl: parseFloat(e.target.value) || 0 };
                              handleMercadoPagoConfigChange('plans', next);
                            }}
                          />
                        </div>
                        <div className="md:col-span-1 flex items-end">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={p.active !== false}
                              onCheckedChange={(checked) => {
                                const next = [...(mercadoPagoConfig.plans || [])];
                                next[idx] = { ...next[idx], active: checked };
                                handleMercadoPagoConfigChange('plans', next);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!mercadoPagoConfig.plans || mercadoPagoConfig.plans.length < 10) && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          const next = [...(mercadoPagoConfig.plans || [])];
                          next.push({
                            id: `plano_${next.length + 1}`,
                            label: 'Novo plano',
                            months: 1,
                            priceBrl: 0,
                            active: true,
                            iptvPackageId: null
                          });
                          handleMercadoPagoConfigChange('plans', next);
                        }}
                      >
                        Adicionar plano
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods Info */}
            <Card>
              <CardHeader>
                <CardTitle>Métodos de Pagamento</CardTitle>
                <CardDescription>
                  Informações sobre os métodos de pagamento disponíveis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Cartão de Crédito</h4>
                    <p className="text-sm text-muted-foreground">
                      Aceita Visa, Mastercard, Elo e outros
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">PIX</h4>
                    <p className="text-sm text-muted-foreground">
                      Pagamento instantâneo via PIX
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Boleto Bancário</h4>
                    <p className="text-sm text-muted-foreground">
                      Pagamento via boleto bancário
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Parcelamento</h4>
                    <p className="text-sm text-muted-foreground">
                      Parcelamento em até 12x no cartão
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* UpdePix Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Integração UpdePix (PIX)
                </CardTitle>
                <CardDescription>
                  Configure o UpdePix para gerar PIX (copia e cola) na loja pública de renovação
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Ativar UpdePix</Label>
                    <p className="text-sm text-muted-foreground">
                      Habilite o PIX na loja pública (webvendas) para este slug
                    </p>
                  </div>
                  <Switch
                    checked={updePixConfig.enabled}
                    onCheckedChange={(checked) => handleUpdePixConfigChange('enabled', checked)}
                  />
                </div>

                {updePixConfig.enabled && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="updepixApiKey">API Key</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="updepixApiKey"
                          type={showUpdePixKey ? 'text' : 'password'}
                          value={updePixConfig.apiKey}
                          onChange={(e) => handleUpdePixConfigChange('apiKey', e.target.value)}
                          placeholder="sua-chave-updepix"
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUpdePixKey((v) => !v)}
                        >
                          {showUpdePixKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyUpdePixKey}
                          disabled={!updePixConfig.apiKey}
                        >
                          {updePixKeyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Chave usada no backend para criar depósitos PIX na UpdePix.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="updepixCreateDepositUrl">URL de criação do depósito (opcional)</Label>
                      <Input
                        id="updepixCreateDepositUrl"
                        value={updePixConfig.createDepositUrl}
                        onChange={(e) => handleUpdePixConfigChange('createDepositUrl', e.target.value)}
                        placeholder="https://updepix.cc/api/v1/depositos"
                        className="font-mono text-sm"
                      />
                      <p className="text-sm text-muted-foreground">
                        Se preenchida, sobrescreve a URL padrão do servidor (`UPDEPIX_CREATE_DEPOSIT_URL`) apenas para esta loja.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="updepixWebhookSecret">Webhook Secret (HMAC)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="updepixWebhookSecret"
                          type={showUpdePixWebhookSecret ? 'text' : 'password'}
                          value={updePixConfig.webhookSecret}
                          onChange={(e) => handleUpdePixConfigChange('webhookSecret', e.target.value)}
                          placeholder="whsec_..."
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUpdePixWebhookSecret((v) => !v)}
                        >
                          {showUpdePixWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyUpdePixWebhookSecret}
                          disabled={!updePixConfig.webhookSecret}
                        >
                          {updePixWebhookSecretCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Secret usado para validar a assinatura do webhook (`x-updepix-signature`). Se você usa uma conta UpdePix por loja, configure aqui.
                        Por segurança, após gravar o valor completo não voltamos a mostrá-lo aqui — só cole de novo se precisar alterar.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Lynx Pix Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Integração Lynx (PIX)
                </CardTitle>
                <CardDescription>
                  Configure a Lynx para gerar QR Code PIX na loja pública e na compra de créditos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Ativar Lynx</Label>
                    <p className="text-sm text-muted-foreground">
                      Habilite o PIX Lynx para este slug (loja web e CMS)
                    </p>
                  </div>
                  <Switch
                    checked={lynxConfig.enabled}
                    onCheckedChange={(checked) => handleLynxConfigChange('enabled', checked)}
                  />
                </div>

                {lynxConfig.enabled && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="lynxApiKey">Bearer Token (API Key)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="lynxApiKey"
                          type={showLynxKey ? 'text' : 'password'}
                          value={lynxConfig.apiKey}
                          onChange={(e) => handleLynxConfigChange('apiKey', e.target.value)}
                          placeholder="lynx_live_..."
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowLynxKey((v) => !v)}
                        >
                          {showLynxKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyLynxKey}
                          disabled={!lynxConfig.apiKey}
                        >
                          {lynxKeyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Token Bearer da conta Lynx — usado apenas no servidor.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lynxCreatePixUrl">URL de criação PIX (opcional)</Label>
                      <Input
                        id="lynxCreatePixUrl"
                        value={lynxConfig.createPixUrl}
                        onChange={(e) => handleLynxConfigChange('createPixUrl', e.target.value)}
                        placeholder="https://lynxpix.cc/api/v1/pixQrCode/create"
                        className="font-mono text-sm"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>URL do Webhook (cadastrar na Lynx)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          readOnly
                          value={`${getPanelApiBase().replace(/\/$/, '')}/api/webhooks/lynx/${encodeURIComponent(String(cmsUser?.cmsSlug || formData.cmsSlug || '').trim().toLowerCase() || 'SEU_SLUG')}`}
                          className="font-mono text-sm"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const slug = String(cmsUser?.cmsSlug || formData.cmsSlug || '')
                              .trim()
                              .toLowerCase();
                            if (!slug) {
                              alert('Salve o slug do CMS antes.');
                              return;
                            }
                            const url = `${getPanelApiBase().replace(/\/$/, '')}/api/webhooks/lynx/${encodeURIComponent(slug)}`;
                            try {
                              await navigator.clipboard.writeText(url);
                              alert('URL copiada. Cole no painel da Lynx (webhook).');
                            } catch {
                              prompt('Copie a URL do webhook:', url);
                            }
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Só POST. Abrir no navegador (GET) retorna 404 — isso é normal.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lynxWebhookSecret">Webhook Secret (opcional)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="lynxWebhookSecret"
                          type={showLynxWebhookSecret ? 'text' : 'password'}
                          value={lynxConfig.webhookSecret}
                          onChange={(e) => handleLynxConfigChange('webhookSecret', e.target.value)}
                          placeholder="Cole o secret completo da Lynx (não o valor mascarado)"
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowLynxWebhookSecret((v) => !v)}
                        >
                          {showLynxWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyLynxWebhookSecret}
                          disabled={!lynxConfig.webhookSecret}
                        >
                          {lynxWebhookSecretCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Ao reabrir Configurações o campo fica vazio por segurança — cole de novo para
                        gravar. Com Lynx ativa + API Key o webhook já funciona sem secret (confirmação
                        via API). O secret só reforça a assinatura HMAC.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {canManageCreditSalesUi && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Coins className="h-5 w-5" />
                    Venda automática de créditos (revendedores)
                  </CardTitle>
                  <CardDescription>
                    Defina pacotes: quantidade de créditos e o <strong>preço por crédito</strong> em R$. O valor cobrado
                    no checkout é <strong>créditos × preço unitário</strong> (ex.: 10 créditos a R$ 10/crédito = R$
                    100,00). Revendedores compram pelo menu &quot;Comprar créditos&quot; com Mercado Pago e/ou UpdePix
                    já configurados acima. O pagamento cai nas contas do master; os créditos somam ao revendedor após
                    confirmação.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-0.5">
                      <Label className="text-base font-medium">Ativar venda de créditos no CMS</Label>
                      <p className="text-sm text-muted-foreground">
                        Quando desligado, o menu de compra some e nenhum pedido novo é aceito.
                      </p>
                    </div>
                    <Switch
                      checked={creditSalesConfig.enabled}
                      onCheckedChange={(checked) => handleCreditSalesChange('enabled', checked)}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label>Pacotes (até 30)</Label>
                    <p className="text-sm text-muted-foreground">
                      Em cada linha, o campo em R$ é o valor <strong>por crédito</strong>. O total do pacote aparece
                      abaixo da linha.
                    </p>
                    {(creditSalesConfig.packages || []).map((p, idx) => (
                      <div key={`${p.id}-${idx}`} className="border rounded-md p-3 space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                          <div className="md:col-span-3 space-y-1">
                            <Label className="text-xs">ID</Label>
                            <Input
                              value={p.id}
                              onChange={(e) => {
                                const next = [...(creditSalesConfig.packages || [])];
                                next[idx] = { ...next[idx], id: e.target.value };
                                handleCreditSalesChange('packages', next);
                              }}
                            />
                          </div>
                          <div className="md:col-span-4 space-y-1">
                            <Label className="text-xs">Rótulo</Label>
                            <Input
                              value={p.label}
                              onChange={(e) => {
                                const next = [...(creditSalesConfig.packages || [])];
                                next[idx] = { ...next[idx], label: e.target.value };
                                handleCreditSalesChange('packages', next);
                              }}
                            />
                          </div>
                          <div className="md:col-span-2 space-y-1">
                            <Label className="text-xs">Créditos</Label>
                            <Input
                              type="number"
                              min={1}
                              value={p.credits}
                              onChange={(e) => {
                                const next = [...(creditSalesConfig.packages || [])];
                                next[idx] = { ...next[idx], credits: parseInt(e.target.value, 10) || 0 };
                                handleCreditSalesChange('packages', next);
                              }}
                            />
                          </div>
                          <div className="md:col-span-2 space-y-1">
                            <Label className="text-xs">Preço por crédito (R$)</Label>
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              value={p.priceBrl}
                              onChange={(e) => {
                                const next = [...(creditSalesConfig.packages || [])];
                                next[idx] = { ...next[idx], priceBrl: parseFloat(e.target.value) || 0 };
                                handleCreditSalesChange('packages', next);
                              }}
                            />
                          </div>
                          <div className="md:col-span-1 flex justify-end pb-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const next = [...(creditSalesConfig.packages || [])];
                                next.splice(idx, 1);
                                handleCreditSalesChange('packages', next);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Total do pacote:{' '}
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                            (Number(p.credits) || 0) * (Number(p.priceBrl) || 0)
                          )}
                        </p>
                      </div>
                    ))}
                    {(creditSalesConfig.packages || []).length < 30 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const next = [...(creditSalesConfig.packages || [])];
                          next.push({
                            id: `pkg_${next.length + 1}`,
                            label: 'Novo pacote',
                            credits: 10,
                            priceBrl: 0,
                            active: true
                          });
                          handleCreditSalesChange('packages', next);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar pacote
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* App Promotion Tab */}
        {activeTab === 'appPromotion' && cmsUser?.role !== 'client' && (
          <div className="space-y-6">
            {/* Switch Principal */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Promoções do App
                </CardTitle>
                <CardDescription>
                  Configure as informações de promoção e suporte que serão exibidas no aplicativo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Habilitar Promoções</Label>
                    <p className="text-sm text-muted-foreground">
                      Ative para exibir as caixas de promoção e suporte no aplicativo
                    </p>
                  </div>
                  <Switch
                    checked={formData.appPromotionSettings?.enabled || false}
                    onCheckedChange={(checked) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appPromotionSettings: {
                          ...(prev.appPromotionSettings || {}),
                          enabled: checked
                        }
                      }));
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Caixa de Promoção (lado direito) */}
            <Card>
              <CardHeader>
                <CardTitle>Caixa de Promoção (Lado Direito)</CardTitle>
                <CardDescription>
                  Configure a caixa promocional com informações sobre preço e benefícios
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Título */}
                <div className="space-y-2">
                  <Label htmlFor="promotionTitle">Título</Label>
                  <Input
                    id="promotionTitle"
                    type="text"
                    value={formData.appPromotionSettings?.promotionBox?.title || 'ATENÇÃO!'}
                    onChange={(e) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appPromotionSettings: {
                          ...(prev.appPromotionSettings || {}),
                          promotionBox: {
                            ...(prev.appPromotionSettings?.promotionBox || {}),
                            title: e.target.value
                          }
                        }
                      }));
                    }}
                    placeholder="ATENÇÃO!"
                    maxLength={100}
                  />
                </div>

                {/* Label do Preço */}
                <div className="space-y-2">
                  <Label htmlFor="promotionPriceLabel">Texto antes do Preço</Label>
                  <Input
                    id="promotionPriceLabel"
                    type="text"
                    value={formData.appPromotionSettings?.promotionBox?.priceLabel || 'Nosso app custa apenas:'}
                    onChange={(e) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appPromotionSettings: {
                          ...(prev.appPromotionSettings || {}),
                          promotionBox: {
                            ...(prev.appPromotionSettings?.promotionBox || {}),
                            priceLabel: e.target.value
                          }
                        }
                      }));
                    }}
                    placeholder="Nosso app custa apenas:"
                    maxLength={100}
                  />
                </div>

                {/* Preço */}
                <div className="space-y-2">
                  <Label htmlFor="promotionPrice">Preço</Label>
                  <Input
                    id="promotionPrice"
                    type="text"
                    value={formData.appPromotionSettings?.promotionBox?.price || 'R$19,90/MÊS'}
                    onChange={(e) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appPromotionSettings: {
                          ...(prev.appPromotionSettings || {}),
                          promotionBox: {
                            ...(prev.appPromotionSettings?.promotionBox || {}),
                            price: e.target.value
                          }
                        }
                      }));
                    }}
                    placeholder="R$19,90/MÊS"
                    maxLength={50}
                  />
                  <p className="text-sm text-muted-foreground">
                    Valor exibido em destaque (ex: R$19,90/MÊS, R$29,90/ANO, etc.)
                  </p>
                </div>

                {/* Descrição */}
                <div className="space-y-2">
                  <Label htmlFor="promotionDescription">Descrição</Label>
                  <Input
                    id="promotionDescription"
                    type="text"
                    value={formData.appPromotionSettings?.promotionBox?.description || 'O melhores conteúdos'}
                    onChange={(e) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appPromotionSettings: {
                          ...(prev.appPromotionSettings || {}),
                          promotionBox: {
                            ...(prev.appPromotionSettings?.promotionBox || {}),
                            description: e.target.value
                          }
                        }
                      }));
                    }}
                    placeholder="O melhores conteúdos"
                    maxLength={200}
                  />
                </div>

                {/* Texto Destacado */}
                <div className="space-y-2">
                  <Label htmlFor="promotionHighlight">Texto Destacado</Label>
                  <Input
                    id="promotionHighlight"
                    type="text"
                    value={formData.appPromotionSettings?.promotionBox?.highlightText || 'Sempre Atualizados!'}
                    onChange={(e) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appPromotionSettings: {
                          ...(prev.appPromotionSettings || {}),
                          promotionBox: {
                            ...(prev.appPromotionSettings?.promotionBox || {}),
                            highlightText: e.target.value
                          }
                        }
                      }));
                    }}
                    placeholder="Sempre Atualizados!"
                    maxLength={100}
                  />
                  <p className="text-sm text-muted-foreground">
                    Texto que aparece em destaque (geralmente em amarelo)
                  </p>
                </div>

                {/* Lista de Benefícios */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Benefícios (Lista com Bullets)</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const currentBenefits = formData.appPromotionSettings?.promotionBox?.benefits || [];
                        setFormData((prev: any) => ({
                          ...prev,
                          appPromotionSettings: {
                            ...(prev.appPromotionSettings || {}),
                            promotionBox: {
                              ...(prev.appPromotionSettings?.promotionBox || {}),
                              benefits: [...currentBenefits, '']
                            }
                          }
                        }));
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Benefício
                    </Button>
                  </div>
                  
                  {(formData.appPromotionSettings?.promotionBox?.benefits || []).map((benefit: string, index: number) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={benefit}
                        onChange={(e) => {
                          const currentBenefits = formData.appPromotionSettings?.promotionBox?.benefits || [];
                          const newBenefits = [...currentBenefits];
                          newBenefits[index] = e.target.value;
                          setFormData((prev: any) => ({
                            ...prev,
                            appPromotionSettings: {
                              ...(prev.appPromotionSettings || {}),
                              promotionBox: {
                                ...(prev.appPromotionSettings?.promotionBox || {}),
                                benefits: newBenefits
                              }
                            }
                          }));
                        }}
                        placeholder={`Benefício ${index + 1} (ex: Canais, Filmes e Séries)`}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const currentBenefits = formData.appPromotionSettings?.promotionBox?.benefits || [];
                          const newBenefits = currentBenefits.filter((_: string, i: number) => i !== index);
                          setFormData((prev: any) => ({
                            ...prev,
                            appPromotionSettings: {
                              ...(prev.appPromotionSettings || {}),
                              promotionBox: {
                                ...(prev.appPromotionSettings?.promotionBox || {}),
                                benefits: newBenefits
                              }
                            }
                          }));
                        }}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Texto do Botão */}
                <div className="space-y-2">
                  <Label htmlFor="promotionButtonText">Texto do Botão</Label>
                  <Input
                    id="promotionButtonText"
                    type="text"
                    value={formData.appPromotionSettings?.promotionBox?.buttonText || 'INDIQUE 2 AMIGOS E GANHE 1 MÊS'}
                    onChange={(e) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appPromotionSettings: {
                          ...(prev.appPromotionSettings || {}),
                          promotionBox: {
                            ...(prev.appPromotionSettings?.promotionBox || {}),
                            buttonText: e.target.value
                          }
                        }
                      }));
                    }}
                    placeholder="INDIQUE 2 AMIGOS E GANHE 1 MÊS"
                    maxLength={100}
                  />
                </div>

                {/* Link do Botão */}
                <div className="space-y-2">
                  <Label htmlFor="promotionButtonLink">Link do Botão</Label>
                  <Input
                    id="promotionButtonLink"
                    type="url"
                    value={formData.appPromotionSettings?.promotionBox?.buttonLink || ''}
                    onChange={(e) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appPromotionSettings: {
                          ...(prev.appPromotionSettings || {}),
                          promotionBox: {
                            ...(prev.appPromotionSettings?.promotionBox || {}),
                            buttonLink: e.target.value
                          }
                        }
                      }));
                    }}
                    placeholder="https://exemplo.com/promocao"
                    className="font-mono text-sm"
                  />
                </div>

                <Separator />

                {/* Informações de Contato/WhatsApp */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Informações de Contato (WhatsApp)</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Configure os dados de contato que serão exibidos junto com as informações de promoção. O QR code será gerado automaticamente a partir destes dados.
                    </p>
                  </div>

                  {/* Título do Contato */}
                  <div className="space-y-2">
                    <Label htmlFor="promotionContactTitle">Título do Contato</Label>
                    <Input
                      id="promotionContactTitle"
                      type="text"
                      value={formData.appPromotionSettings?.promotionBox?.contactTitle ?? formData.appPromotionSettings?.supportBox?.title ?? ''}
                      onChange={(e) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          appPromotionSettings: {
                            ...(prev.appPromotionSettings || {}),
                            promotionBox: {
                              ...(prev.appPromotionSettings?.promotionBox || {}),
                              contactTitle: e.target.value
                            }
                          }
                        }));
                      }}
                      placeholder="PARA ATIVAÇÃO DÚVIDAS OU SUPORTE NOS CHAME NO WHATSAPP"
                      maxLength={200}
                    />
                  </div>

                  {/* Número de Telefone */}
                  <div className="space-y-2">
                    <Label htmlFor="promotionPhone">Número de Telefone/WhatsApp</Label>
                    <Input
                      id="promotionPhone"
                      type="text"
                      value={formData.appPromotionSettings?.promotionBox?.phoneNumber ?? formData.appPromotionSettings?.supportBox?.phoneNumber ?? ''}
                      onChange={(e) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          appPromotionSettings: {
                            ...(prev.appPromotionSettings || {}),
                            promotionBox: {
                              ...(prev.appPromotionSettings?.promotionBox || {}),
                              phoneNumber: e.target.value
                            }
                          }
                        }));
                      }}
                      placeholder="+551234567890"
                      maxLength={50}
                    />
                    <p className="text-sm text-muted-foreground">
                      Número de WhatsApp para contato. O QR code será gerado automaticamente a partir deste número.
                    </p>
                  </div>

                  {/* Link do WhatsApp */}
                  <div className="space-y-2">
                    <Label htmlFor="promotionWhatsappLink">Link do WhatsApp (opcional)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="promotionWhatsappLink"
                        type="url"
                        value={formData.appPromotionSettings?.promotionBox?.whatsappLink ?? formData.appPromotionSettings?.supportBox?.whatsappLink ?? ''}
                        onChange={(e) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            appPromotionSettings: {
                              ...(prev.appPromotionSettings || {}),
                              promotionBox: {
                                ...(prev.appPromotionSettings?.promotionBox || {}),
                                whatsappLink: e.target.value
                              }
                            }
                          }));
                        }}
                        placeholder="https://wa.me/551234567890"
                        className="font-mono text-sm flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const phoneNumber = formData.appPromotionSettings?.promotionBox?.phoneNumber || formData.appPromotionSettings?.supportBox?.phoneNumber || '';
                          if (phoneNumber) {
                            // Remover caracteres não numéricos, exceto +
                            const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
                            // Se começar com +, manter, senão adicionar 55 (Brasil)
                            const whatsappNumber = cleanNumber.startsWith('+') ? cleanNumber.substring(1) : (cleanNumber.startsWith('55') ? cleanNumber : '55' + cleanNumber);
                            const whatsappLink = `https://wa.me/${whatsappNumber}`;
                            setFormData((prev: any) => ({
                              ...prev,
                              appPromotionSettings: {
                                ...(prev.appPromotionSettings || {}),
                                promotionBox: {
                                  ...(prev.appPromotionSettings?.promotionBox || {}),
                                  whatsappLink: whatsappLink
                                }
                              }
                            }));
                          }
                        }}
                      >
                        Gerar Link
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Link direto para WhatsApp. Clique em "Gerar Link" para criar automaticamente a partir do número acima, ou deixe em branco para usar apenas o número de telefone.
                    </p>
                  </div>

                  {/* QR Code do WhatsApp */}
                  <div className="space-y-2">
                    <Label>QR Code do WhatsApp</Label>
                    <div className="p-4 border rounded-lg bg-white">
                      <p className="text-sm text-muted-foreground mb-3">
                        QR Code gerado automaticamente a partir do número de telefone configurado acima. O app exibirá este QR code junto com as informações de promoção.
                      </p>
                      {cmsUser?.cmsSlug && ((formData.appPromotionSettings?.promotionBox?.whatsappLink && formData.appPromotionSettings.promotionBox.whatsappLink.trim() !== '') || (formData.appPromotionSettings?.promotionBox?.phoneNumber && formData.appPromotionSettings.promotionBox.phoneNumber.trim() !== '') || (formData.appPromotionSettings?.supportBox?.whatsappLink && formData.appPromotionSettings.supportBox.whatsappLink.trim() !== '') || (formData.appPromotionSettings?.supportBox?.phoneNumber && formData.appPromotionSettings.supportBox.phoneNumber.trim() !== '')) && (
                        <div className="flex flex-col items-center gap-3">
                          <div className="p-3 bg-white rounded-lg border-2 border-gray-200">
                            <img 
                              src={`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api-cms.cms20.online')}/api/cms/${cmsUser.cmsSlug}/qr-code`}
                              alt="QR Code WhatsApp"
                              className="w-48 h-48"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground mb-1">URL do QR Code (para uso no app):</p>
                            <code className="text-xs bg-gray-100 p-2 rounded block break-all">
                              {`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api-cms.cms20.online')}/api/cms/${cmsUser.cmsSlug}/qr-code`}
                            </code>
                            <p className="text-xs text-muted-foreground mt-2">
                              Este QR code será exibido automaticamente no app junto com as informações de promoção.
                            </p>
                          </div>
                        </div>
                      )}
                      {(!cmsUser?.cmsSlug || (!formData.appPromotionSettings?.promotionBox?.whatsappLink && !formData.appPromotionSettings?.promotionBox?.phoneNumber && !formData.appPromotionSettings?.supportBox?.whatsappLink && !formData.appPromotionSettings?.supportBox?.phoneNumber)) && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Configure o número de WhatsApp acima e salve as configurações para gerar o QR Code.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'inAppNotice' && cmsUser?.role !== 'client' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Comunicado no App
              </CardTitle>
              <CardDescription>
                Mensagem exibida no aplicativo Android ao abrir (campo <code className="text-xs">cmsSettings.inAppNotice</code> na API pública).
                Editar o texto e salvar já publica um novo aviso para todos; use &quot;Forçar reexibição&quot; para mostrar de novo o mesmo texto.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium">Habilitar comunicado</Label>
                  <p className="text-sm text-muted-foreground">
                    Quando ativo, o app busca título e mensagem na abertura (login / sessão)
                  </p>
                </div>
                <Switch
                  checked={formData.inAppNotice?.enabled || false}
                  onCheckedChange={(checked) => {
                    setFormData((prev: any) => ({
                      ...prev,
                      inAppNotice: {
                        ...(prev.inAppNotice || {}),
                        enabled: checked
                      }
                    }));
                  }}
                />
              </div>

              {formData.inAppNotice?.enabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="inAppNoticeTitle">Título</Label>
                    <Input
                      id="inAppNoticeTitle"
                      value={formData.inAppNotice?.title || ''}
                      onChange={(e) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          inAppNotice: {
                            ...(prev.inAppNotice || {}),
                            title: e.target.value
                          }
                        }));
                        setInAppNoticeDirty(true);
                      }}
                      placeholder="Ex.: Aviso importante"
                      maxLength={200}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inAppNoticeMessage">Mensagem</Label>
                    <textarea
                      id="inAppNoticeMessage"
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={formData.inAppNotice?.message || ''}
                      onChange={(e) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          inAppNotice: {
                            ...(prev.inAppNotice || {}),
                            message: e.target.value
                          }
                        }));
                        setInAppNoticeDirty(true);
                      }}
                      placeholder="Texto exibido no diálogo do app"
                      maxLength={2000}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Select
                        value={formData.inAppNotice?.severity || 'info'}
                        onValueChange={(value) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            inAppNotice: {
                              ...(prev.inAppNotice || {}),
                              severity: value
                            }
                          }));
                        }}
                      >
                        <SelectTrigger className="h-9 w-full text-sm">
                          <span>{formData.inAppNotice?.severity === 'warning' ? 'Atenção' : formData.inAppNotice?.severity === 'danger' ? 'Urgente' : 'Informação'}</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="info">Informação</SelectItem>
                          <SelectItem value="warning">Atenção</SelectItem>
                          <SelectItem value="danger">Urgente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="inAppNoticeId">ID do aviso</Label>
                      <Input
                        id="inAppNoticeId"
                        value={formData.inAppNotice?.id || ''}
                        readOnly
                        className="bg-muted"
                        placeholder="Gerado ao salvar ou ao forçar reexibição"
                      />
                      {formData.inAppNotice?.updatedAt && (
                        <p className="text-xs text-muted-foreground">
                          Última publicação: {new Date(formData.inAppNotice.updatedAt).toLocaleString('pt-BR')}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setFormData((prev: any) => ({
                          ...prev,
                          inAppNotice: {
                            ...(prev.inAppNotice || {}),
                            enabled: true,
                            title: '',
                            message: '',
                            id: `notice_${Date.now()}`,
                            updatedAt: new Date().toISOString()
                          }
                        }));
                        // ID já foi renovado agora; ao digitar o texto o "dirty" volta a true.
                        setInAppNoticeDirty(false);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Novo comunicado
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setFormData((prev: any) => ({
                          ...prev,
                          inAppNotice: {
                            ...(prev.inAppNotice || {}),
                            id: `notice_${Date.now()}`,
                            updatedAt: new Date().toISOString()
                          }
                        }));
                      }}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Forçar reexibição no app
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Salve as configurações para publicar. Ao editar o texto de um comunicado, um novo ID é gerado
                    automaticamente ao salvar, então o aviso reaparece para todos os clientes (inclusive quem já o tinha visto).
                    Use &quot;Novo comunicado&quot; para começar do zero e &quot;Forçar reexibição&quot; para mostrar de novo o mesmo texto.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Trial Generator Tab */}
        {activeTab === 'appUpdate' && cmsUser?.role !== 'client' && (
          <div className="space-y-6">
                        {/* Proxy global removido: proxy agora é seletivo por dispositivo */}
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Atualização Automática do App
                </CardTitle>
                <CardDescription>
                  Configure as atualizações automáticas do aplicativo Android. Os usuários serão notificados quando uma nova versão estiver disponível.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Switch */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Habilitar Atualização Automática</Label>
                    <p className="text-sm text-muted-foreground">
                      Ative para permitir que o app verifique e baixe atualizações automaticamente
                    </p>
                  </div>
                  <Switch
                    checked={formData.appUpdate?.enabled || false}
                    onCheckedChange={(checked) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        appUpdate: {
                          ...prev.appUpdate!,
                          enabled: checked
                        }
                      }));
                    }}
                  />
                </div>

                {formData.appUpdate?.enabled && (
                  <>
                    {/* Version Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="latestVersionCode">Version Code</Label>
                        <Input
                          id="latestVersionCode"
                          type="number"
                          min="1"
                          value={formData.appUpdate?.latestVersionCode || 1}
                          onChange={(e) => {
                            setFormData((prev: any) => ({
                              ...prev,
                              appUpdate: {
                                ...(prev.appUpdate || {}),
                                latestVersionCode: parseInt(e.target.value) || 1
                              }
                            }));
                          }}
                          placeholder="1"
                        />
                        <p className="text-sm text-muted-foreground">
                          Código numérico da versão (deve ser maior que a versão atual)
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="latestVersionName">Version Name</Label>
                        <Input
                          id="latestVersionName"
                          type="text"
                          value={formData.appUpdate?.latestVersionName || '1.0.0'}
                          onChange={(e) => {
                            setFormData((prev: any) => ({
                              ...prev,
                              appUpdate: {
                                ...(prev.appUpdate || {}),
                                latestVersionName: e.target.value
                              }
                            }));
                          }}
                          placeholder="1.0.0"
                        />
                        <p className="text-sm text-muted-foreground">
                          Nome da versão (ex: 1.0.1, 2.0.0)
                        </p>
                      </div>
                    </div>

                    {/* APK Download URL */}
                    <div className="space-y-2">
                      <Label htmlFor="apkDownloadUrl">URL do APK para Download</Label>
                      <Input
                        id="apkDownloadUrl"
                        type="url"
                        value={formData.appUpdate?.apkDownloadUrl || ''}
                        onChange={(e) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            appUpdate: {
                              ...(prev.appUpdate || {}),
                              apkDownloadUrl: e.target.value
                            }
                          }));
                        }}
                        placeholder="https://seu-servidor.com/apps/FireVision-v1.0.1.apk"
                        className="font-mono text-sm"
                      />
                      <p className="text-sm text-muted-foreground">
                        URL completa do arquivo APK que será baixado pelos usuários. Pode ser qualquer URL HTTP/HTTPS válida que sirva o arquivo APK (não precisa terminar com .apk).
                      </p>
                    </div>

                    {/* Update Message */}
                    <div className="space-y-2">
                      <Label htmlFor="updateMessage">Mensagem de Atualização</Label>
                      <textarea
                        id="updateMessage"
                        value={formData.appUpdate?.updateMessage || ''}
                        onChange={(e) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            appUpdate: {
                              ...(prev.appUpdate || {}),
                              updateMessage: e.target.value
                            }
                          }));
                        }}
                        placeholder="Nova versão disponível! Atualize para ter acesso às últimas funcionalidades."
                        className="w-full p-2 border rounded-md min-h-[100px] resize-y"
                      />
                      <p className="text-sm text-muted-foreground">
                        Mensagem que será exibida aos usuários quando uma atualização estiver disponível
                      </p>
                    </div>

                    {/* Force Update */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label className="text-base font-medium">Atualização Forçada</Label>
                        <p className="text-sm text-muted-foreground">
                          Se ativado, os usuários não poderão cancelar a atualização e precisarão atualizar para continuar usando o app
                        </p>
                      </div>
                      <Switch
                        checked={formData.appUpdate?.forceUpdate || false}
                        onCheckedChange={(checked) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            appUpdate: {
                              ...(prev.appUpdate || {}),
                              forceUpdate: checked
                            }
                          }));
                        }}
                      />
                    </div>

                    {/* Min Supported Version */}
                    <div className="space-y-2">
                      <Label htmlFor="minSupportedVersion">Versão Mínima Suportada</Label>
                      <Input
                        id="minSupportedVersion"
                        type="number"
                        min="1"
                        value={formData.appUpdate?.minSupportedVersion || 1}
                        onChange={(e) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            appUpdate: {
                              ...(prev.appUpdate || {}),
                              minSupportedVersion: parseInt(e.target.value) || 1
                            }
                          }));
                        }}
                        placeholder="1"
                      />
                      <p className="text-sm text-muted-foreground">
                        Versões abaixo desta não funcionarão mais. Use para forçar atualização de versões muito antigas.
                      </p>
                    </div>

                    {/* Info Card */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            Informações Importantes
                          </p>
                          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                            <li>O Version Code deve ser incrementado a cada nova versão</li>
                            <li>Certifique-se de que a URL do APK está acessível publicamente</li>
                            <li>O app verifica atualizações automaticamente ao iniciar</li>
                            <li>Atualização forçada bloqueia o uso do app até atualizar</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'gamesOfDay' && cmsUser?.role !== 'client' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Jogos do Dia
                </CardTitle>
                <CardDescription>
                  Configure a página de jogos do dia que será exibida no aplicativo Android
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Switch para ativar/desativar */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50">
                  <div className="flex-1">
                    <Label className="text-base font-medium">
                      Habilitar Jogos do Dia
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Quando ativado, a página de jogos do dia será exibida no aplicativo
                    </p>
                  </div>
                  <Switch
                    checked={formData.gamesOfDay?.enabled || false}
                    onCheckedChange={(checked) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        gamesOfDay: {
                          ...(prev.gamesOfDay || {}),
                          enabled: checked
                        }
                      }));
                    }}
                  />
                </div>

                {formData.gamesOfDay?.enabled && (
                  <>
                    <Separator />

                    {/* URL da Página de Jogos */}
                    <div className="space-y-2">
                      <Label htmlFor="gamesPageUrl">URL da Página de Jogos</Label>
                      <Input
                        id="gamesPageUrl"
                        type="url"
                        value={formData.gamesOfDay?.pageUrl || ''}
                        onChange={(e) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            gamesOfDay: {
                              ...(prev.gamesOfDay || {}),
                              pageUrl: e.target.value
                            }
                          }));
                        }}
                        placeholder="https://exemplo.com/jogos-do-dia"
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        URL da página web que será exibida no aplicativo. O app carregará esta página em uma WebView, permitindo atualizações sem recompilar o APK.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        <strong>Importante:</strong> Esta página deve ser responsiva e otimizada para mobile/Android TV.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="broadcastHintsUrl">URL do JSON &quot;onde assistir&quot; (opcional)</Label>
                      <Input
                        id="broadcastHintsUrl"
                        type="url"
                        value={formData.gamesOfDay?.broadcastHintsUrl || ''}
                        onChange={(e) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            gamesOfDay: {
                              ...(prev.gamesOfDay || {}),
                              broadcastHintsUrl: e.target.value
                            }
                          }));
                        }}
                        placeholder="https://cdn.exemplo.com/broadcast-hints.json"
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        O painel faz merge deste JSON com os jogos da API e envia o campo <code className="text-xs">broadcastHint</code> para o app. Use HTTPS; o formato está descrito na documentação do painel (<code className="text-xs">broadcast-hints-json.md</code>).
                      </p>
                    </div>

                    {/* Switch: mapeamento automático de canais (cron diária) */}
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-green-50">
                      <div className="flex-1 pr-4">
                        <Label className="text-base font-medium">
                          Atualizar canais automaticamente (1x/dia)
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Quando ativado, o painel associa automaticamente os canais Xtream e P2P aos jogos do dia todos os dias, logo após atualizar a lista de jogos. Desative se preferir associar os canais manualmente.
                        </p>
                      </div>
                      <Switch
                        checked={formData.gamesOfDay?.autoBroadcastChannels !== false}
                        onCheckedChange={(checked) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            gamesOfDay: {
                              ...(prev.gamesOfDay || {}),
                              autoBroadcastChannels: checked
                            }
                          }));
                        }}
                      />
                    </div>

                    {/* URL do Preview Gerado Automaticamente */}
                    {cmsUser?.cmsSlug && formData.gamesOfDay?.enabled && formData.gamesOfDay?.apiKey && (
                      <div className="space-y-2">
                        <Label>URL do Preview Automático</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="text"
                            readOnly
                            value={`${window.location.origin}/${cmsUser.cmsSlug}/games-of-day/preview`}
                            className="font-mono text-sm bg-gray-50"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const previewUrl = `${window.location.origin}/${cmsUser.cmsSlug}/games-of-day/preview`;
                              try {
                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                  await navigator.clipboard.writeText(previewUrl);
                                  setPublicUrlApiCopied(true);
                                  setTimeout(() => setPublicUrlApiCopied(false), 2000);
                                } else {
                                  // Fallback para navegadores sem Clipboard API
                                  const textArea = document.createElement('textarea');
                                  textArea.value = previewUrl;
                                  textArea.style.position = 'fixed';
                                  textArea.style.opacity = '0';
                                  document.body.appendChild(textArea);
                                  textArea.select();
                                  document.execCommand('copy');
                                  // Evita NotFoundError intermitente (o node pode já não estar no DOM)
                                  try {
                                    textArea.remove();
                                  } catch (_) {
                                    if (textArea.parentNode) textArea.parentNode.removeChild(textArea);
                                  }
                                  setPublicUrlApiCopied(true);
                                  setTimeout(() => setPublicUrlApiCopied(false), 2000);
                                }
                              } catch (err) {
                              }
                            }}
                          >
                            {publicUrlApiCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const previewUrl = `${window.location.origin}/${cmsUser.cmsSlug}/games-of-day/preview`;
                              setFormData((prev: any) => ({
                                ...prev,
                                gamesOfDay: {
                                  ...(prev.gamesOfDay || {}),
                                  pageUrl: previewUrl
                                }
                              }));
                            }}
                          >
                            Usar esta URL
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const previewUrl = `${window.location.origin}/${cmsUser.cmsSlug}/games-of-day/preview`;
                              window.open(previewUrl, '_blank');
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Esta URL é gerada automaticamente pelo sistema e mostra os jogos do dia com o mesmo design do preview. Você pode usar esta URL diretamente no campo acima ou criar sua própria página personalizada.
                        </p>
                      </div>
                    )}

                    <Separator />

                    {/* Países Filtrados */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Países para Filtrar</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowCountryInput(true);
                            setNewCountryInput('');
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Adicionar País
                        </Button>
                      </div>
                      
                      {/* Input para adicionar país */}
                      {showCountryInput && (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-background">
                          <Input
                            type="text"
                            placeholder="Digite o nome do país (ex: Brazil, England, Spain, World)"
                            value={newCountryInput}
                            onChange={(e) => setNewCountryInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newCountryInput.trim()) {
                                setFormData((prev: any) => ({
                                  ...prev,
                                  gamesOfDay: {
                                    ...(prev.gamesOfDay || {}),
                                    filteredCountries: [
                                      ...(prev.gamesOfDay?.filteredCountries || []),
                                      newCountryInput.trim()
                                    ]
                                  }
                                }));
                                setNewCountryInput('');
                                setShowCountryInput(false);
                              } else if (e.key === 'Escape') {
                                setShowCountryInput(false);
                                setNewCountryInput('');
                              }
                            }}
                            autoFocus
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (newCountryInput.trim()) {
                                setFormData((prev: any) => ({
                                  ...prev,
                                  gamesOfDay: {
                                    ...(prev.gamesOfDay || {}),
                                    filteredCountries: [
                                      ...(prev.gamesOfDay?.filteredCountries || []),
                                      newCountryInput.trim()
                                    ]
                                  }
                                }));
                                setNewCountryInput('');
                                setShowCountryInput(false);
                              }
                            }}
                          >
                            <Check className="h-4 w-4 text-green-500" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowCountryInput(false);
                              setNewCountryInput('');
                            }}
                          >
                            <X className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground">
                        Configure quais países serão exibidos. Use os nomes exatos como retornados pela API (ex: "Brazil", "England", "Spain", "World" para competições mundiais). Se nenhum país for configurado, serão usados os valores padrão.
                      </p>
                      {formData.gamesOfDay?.filteredCountries && formData.gamesOfDay.filteredCountries.length > 0 ? (
                        <div className="space-y-2">
                          {(formData.gamesOfDay.filteredCountries as string[]).map((country: string, index: number) => (
                            <div key={index} className="flex items-center gap-2 p-2 border rounded-md">
                              <span className="flex-1 text-sm font-medium">{country}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    gamesOfDay: {
                                      ...(prev.gamesOfDay || {}),
                                      filteredCountries: prev.gamesOfDay?.filteredCountries?.filter((_: string, i: number) => i !== index) || []
                                    }
                                  }));
                                }}
                              >
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">
                          Nenhum país configurado. Use o botão acima para adicionar países. Se nenhum país for configurado, serão usados os valores padrão (Brasil + principais países europeus).
                        </p>
                      )}
                    </div>

                    <Separator />

                    {/* Ligas Filtradas */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Ligas/Campeonatos para Filtrar</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowLeagueInput(true);
                            setNewLeagueInput('');
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Adicionar Liga
                        </Button>
                      </div>
                      
                      {/* Input para adicionar liga */}
                      {showLeagueInput && (
                        <div className="flex items-center gap-2 p-2 border rounded-md bg-background">
                          <Input
                            type="text"
                            placeholder="Digite o nome da liga/campeonato"
                            value={newLeagueInput}
                            onChange={(e) => setNewLeagueInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newLeagueInput.trim()) {
                                setFormData((prev: any) => ({
                                  ...prev,
                                  gamesOfDay: {
                                    ...(prev.gamesOfDay || {}),
                                    filteredLeagues: [
                                      ...(prev.gamesOfDay?.filteredLeagues || []),
                                      newLeagueInput.trim()
                                    ]
                                  }
                                }));
                                setNewLeagueInput('');
                                setShowLeagueInput(false);
                              } else if (e.key === 'Escape') {
                                setShowLeagueInput(false);
                                setNewLeagueInput('');
                              }
                            }}
                            autoFocus
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (newLeagueInput.trim()) {
                                setFormData((prev: any) => ({
                                  ...prev,
                                  gamesOfDay: {
                                    ...(prev.gamesOfDay || {}),
                                    filteredLeagues: [
                                      ...(prev.gamesOfDay?.filteredLeagues || []),
                                      newLeagueInput.trim()
                                    ]
                                  }
                                }));
                                setNewLeagueInput('');
                                setShowLeagueInput(false);
                              }
                            }}
                          >
                            <Check className="h-4 w-4 text-green-500" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowLeagueInput(false);
                              setNewLeagueInput('');
                            }}
                          >
                            <X className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground">
                        Configure quais ligas/campeonatos serão exibidos. Os jogos do Brasil aparecerão primeiro, seguidos pelos grandes campeonatos europeus.
                      </p>
                      {formData.gamesOfDay?.filteredLeagues && formData.gamesOfDay.filteredLeagues.length > 0 ? (
                        <div className="space-y-2">
                          {(formData.gamesOfDay.filteredLeagues as string[]).map((league: string, index: number) => (
                            <div key={index} className="flex items-center gap-2 p-2 border rounded-md">
                              <span className="flex-1 text-sm">{league}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    gamesOfDay: {
                                      ...(prev.gamesOfDay || {}),
                                      filteredLeagues: prev.gamesOfDay?.filteredLeagues?.filter((_: string, i: number) => i !== index) || []
                                    }
                                  }));
                                }}
                              >
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">
                          Nenhuma liga configurada. Use o botão acima para adicionar ligas.
                        </p>
                      )}
                    </div>

                    <Separator />

                    {/* API Key */}
                    <div className="space-y-2">
                      <Label htmlFor="gamesApiKey">API Key do API-Football</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="gamesApiKey"
                          type={showMercadoPagoKeys.publicKey ? 'text' : 'password'}
                          value={formData.gamesOfDay?.apiKey || ''}
                          onChange={(e) => {
                            setFormData((prev: any) => ({
                              ...prev,
                              gamesOfDay: {
                                ...(prev.gamesOfDay || {}),
                                apiKey: e.target.value
                              }
                            }));
                          }}
                          placeholder="Sua chave da API-Football v3"
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowMercadoPagoKeys(prev => ({ ...prev, publicKey: !prev.publicKey }))}
                        >
                          {showMercadoPagoKeys.publicKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        API Key usada pela página web para buscar os jogos. A página gerada automaticamente usará esta chave para fazer as requisições à API-Football v3.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        A API v3 usa autenticação via header <code className="bg-gray-100 px-1 rounded">X-RapidAPI-Key</code> ou <code className="bg-gray-100 px-1 rounded">x-apisports-key</code>
                      </p>
                    </div>

                    {/* Info sobre filtro automático */}
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-green-900 dark:text-green-100">
                            Otimização Ativa
                          </p>
                          <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                            O sistema busca todos os jogos do dia em uma única requisição e filtra automaticamente para exibir apenas jogos dos <strong>principais campeonatos</strong>: UEFA Champions League, Premier League, LaLiga, Serie A (Itália), Ligue 1, Bundesliga, UEFA Europa League, UEFA Conference League, FIFA Intercontinental, Copa Betano Brasil e Campeonato Brasileiro (Série A, B, C). Isso economiza requisições da API.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Country ID - Mantido para compatibilidade, mas não é mais usado no filtro */}
                    <div className="space-y-2">
                      <Label htmlFor="gamesCountryId">ID do País (Legado)</Label>
                      <Input
                        id="gamesCountryId"
                        type="text"
                        value={formData.gamesOfDay?.countryId || 'BR'}
                        onChange={(e) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            gamesOfDay: {
                              ...(prev.gamesOfDay || {}),
                              countryId: e.target.value
                            }
                          }));
                        }}
                        placeholder="BR"
                        disabled
                      />
                      <p className="text-xs text-muted-foreground">
                        Campo mantido para compatibilidade. O filtro agora é automático (Brasil + Europa).
                      </p>
                    </div>

                    {/* Country Name */}
                    <div className="space-y-2">
                      <Label htmlFor="gamesCountryName">Nome do País (Legado)</Label>
                      <Input
                        id="gamesCountryName"
                        type="text"
                        value={formData.gamesOfDay?.countryName || 'Principais Ligas'}
                        onChange={(e) => {
                          setFormData((prev: any) => ({
                            ...prev,
                            gamesOfDay: {
                              ...(prev.gamesOfDay || {}),
                              countryName: e.target.value
                            }
                          }));
                        }}
                        placeholder="Principais Ligas"
                        disabled
                      />
                      <p className="text-xs text-muted-foreground">
                        Campo mantido para compatibilidade. O app exibirá "Principais Ligas" automaticamente.
                      </p>
                    </div>

                    {/* Cache Management */}
                    <Separator />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-semibold">Gerenciamento de Cache</h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            O sistema usa cache para economizar requisições da API (limite de 100/dia). O cache é atualizado automaticamente a cada 5 horas.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              // Buscar token de múltiplas fontes
                              let authToken = token;
                              if (!authToken) {
                                try {
                                  const authStorage = localStorage.getItem('auth-storage');
                                  if (authStorage) {
                                    const authData = JSON.parse(authStorage);
                                    authToken = authData.state?.token || authData.token;
                                  }
                                  if (!authToken) {
                                    const legacyStorage = localStorage.getItem('_pv001');
                                    if (legacyStorage) {
                                      const legacyData = JSON.parse(legacyStorage);
                                      authToken = legacyData.t;
                                    }
                                  }
                                  if (!authToken) {
                                    authToken = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
                                  }
                                } catch (e) {
                                }
                              }
                              
                              if (!authToken) {
                                window.alert('Token de autenticação não encontrado. Por favor, faça login novamente.');
                                redirectToLogin();
                                return;
                              }
                              
                              const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api-cms.cms20.online');
                              const response = await fetch(`${apiUrl}/api/cms/${cmsUser.cmsSlug}/games-of-day/cache/update`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${authToken}`
                                }
                              });
                              
                              if (!response.ok) {
                                const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
                                throw new Error(errorData.error || `HTTP ${response.status}`);
                              }
                              
                              const data = await response.json();
                              if (data.success) {
                                window.alert(`Cache atualizado com sucesso! ${data.gamesCount} jogos encontrados.`);
                              } else {
                                window.alert(`Erro ao atualizar cache: ${data.error || 'Erro desconhecido'}`);
                              }
                            } catch (error: any) {
                              const errorMessage = error.message || error.response?.data?.error || 'Erro desconhecido';
                              window.alert(`Erro ao atualizar cache: ${errorMessage}`);
                            }
                          }}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Forçar Atualização
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm('Tem certeza que deseja limpar o cache? O sistema precisará buscar os dados da API novamente na próxima requisição.')) {
                              return;
                            }
                            try {
                              // Buscar token de múltiplas fontes
                              let authToken = token;
                              if (!authToken) {
                                try {
                                  const authStorage = localStorage.getItem('auth-storage');
                                  if (authStorage) {
                                    const authData = JSON.parse(authStorage);
                                    authToken = authData.state?.token || authData.token;
                                  }
                                  if (!authToken) {
                                    const legacyStorage = localStorage.getItem('_pv001');
                                    if (legacyStorage) {
                                      const legacyData = JSON.parse(legacyStorage);
                                      authToken = legacyData.t;
                                    }
                                  }
                                  if (!authToken) {
                                    authToken = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
                                  }
                                } catch (e) {
                                }
                              }
                              
                              if (!authToken) {
                                window.alert('Token de autenticação não encontrado. Por favor, faça login novamente.');
                                redirectToLogin();
                                return;
                              }
                              
                              const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api-cms.cms20.online');
                              const response = await fetch(`${apiUrl}/api/cms/${cmsUser.cmsSlug}/games-of-day/cache`, {
                                method: 'DELETE',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${authToken}`
                                }
                              });
                              
                              if (!response.ok) {
                                const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
                                throw new Error(errorData.error || `HTTP ${response.status}`);
                              }
                              
                              const data = await response.json();
                              if (data.success) {
                                window.alert('Cache limpo com sucesso!');
                              } else {
                                window.alert(`Erro ao limpar cache: ${data.error || 'Erro desconhecido'}`);
                              }
                            } catch (error: any) {
                              const errorMessage = error.message || error.response?.data?.error || 'Erro desconhecido';
                              window.alert(`Erro ao limpar cache: ${errorMessage}`);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Limpar Cache
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              // Buscar token de múltiplas fontes
                              let authToken = token;
                              if (!authToken) {
                                try {
                                  const authStorage = localStorage.getItem('auth-storage');
                                  if (authStorage) {
                                    const authData = JSON.parse(authStorage);
                                    authToken = authData.state?.token || authData.token;
                                  }
                                  if (!authToken) {
                                    const legacyStorage = localStorage.getItem('_pv001');
                                    if (legacyStorage) {
                                      const legacyData = JSON.parse(legacyStorage);
                                      authToken = legacyData.t;
                                    }
                                  }
                                  if (!authToken) {
                                    authToken = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
                                  }
                                } catch (e) {
                                }
                              }
                              
                              if (!authToken) {
                                window.alert('Token de autenticação não encontrado. Por favor, faça login novamente.');
                                redirectToLogin();
                                return;
                              }
                              
                              const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api-cms.cms20.online');
                              const response = await fetch(`${apiUrl}/api/cms/${cmsUser.cmsSlug}/games-of-day/cache/status`, {
                                method: 'GET',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${authToken}`
                                }
                              });
                              
                              if (!response.ok) {
                                const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
                                throw new Error(errorData.error || `HTTP ${response.status}`);
                              }
                              
                              const data = await response.json();
                              if (data.success) {
                                if (data.exists) {
                                  window.alert(`Status do Cache:\n\nVálido: ${data.isValid ? 'Sim' : 'Não'}\nJogos: ${data.gamesCount}\nÚltima atualização: ${new Date(data.lastUpdated).toLocaleString('pt-BR')}\nPróxima atualização: ${new Date(data.nextUpdate).toLocaleString('pt-BR')}\nTempo até expiração: ${data.timeUntilExpiryFormatted}\nRequisições servidas: ${data.requestsServed}`);
                                } else {
                                  window.alert('Cache não existe ainda. Faça uma requisição ou force a atualização para criar o cache.');
                                }
                              } else {
                                window.alert(`Erro ao buscar status: ${data.error || 'Erro desconhecido'}`);
                              }
                            } catch (error: any) {
                              const errorMessage = error.message || error.response?.data?.error || 'Erro desconhecido';
                              window.alert(`Erro ao buscar status: ${errorMessage}`);
                            }
                          }}
                        >
                          <Database className="h-4 w-4 mr-2" />
                          Ver Status
                        </Button>
                      </div>
                    </div>

                    {isAdmin() && (
                      <>
                        <Separator />
                        <div className="space-y-4 rounded-lg border border-amber-200/70 bg-amber-50/50 p-4 dark:border-amber-800/50 dark:bg-amber-950/25">
                          <div className="space-y-1">
                            <h3 className="text-sm font-semibold text-amber-950 dark:text-amber-100">
                              Transmissão dos jogos (curadoria)
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              Associe <strong>stream_id IPTV (Xtream)</strong> e, se usar P2P, o <strong>ID P2P</strong> aos jogos.
                              Apps antigos só usam IPTV; o APK novo escolhe conforme o sistema do cliente. Pode usar hints
                              automáticos ou <strong>editar manualmente</strong> na tabela. Disponível apenas para administrador (mesma regra da API). Certifique-se de que a
                              rota{' '}
                              <code className="rounded bg-white/60 px-1 text-[11px] dark:bg-black/30">
                                /api/games-broadcast-map
                              </code>{' '}
                              está ativa no servidor da API.
                            </p>
                          </div>

                          {broadcastError && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
                              {broadcastError}
                            </div>
                          )}

                          <div className="grid gap-3 sm:grid-cols-1 md:grid-cols-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="gbc-xtream-server">URL do servidor Xtream</Label>
                              <Input
                                id="gbc-xtream-server"
                                type="url"
                                className="font-mono text-sm"
                                placeholder="Preenchido pelo DNS do slug"
                                value={xtreamServer}
                                onChange={(e) => setXtreamServer(e.target.value)}
                                autoComplete="off"
                              />
                              <p className="text-[11px] text-muted-foreground">
                                Automático: domínio do slug/admin (pode sobrescrever).
                              </p>
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="gbc-xtream-user">Usuário (linha)</Label>
                              <Input
                                id="gbc-xtream-user"
                                className="font-mono text-sm"
                                placeholder="channelsday"
                                value={xtreamUsername}
                                onChange={(e) => setXtreamUsername(e.target.value)}
                                autoComplete="off"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="gbc-xtream-pass">Senha (linha)</Label>
                              <Input
                                id="gbc-xtream-pass"
                                type="password"
                                className="font-mono text-sm"
                                placeholder="daychannels"
                                value={xtreamPassword}
                                onChange={(e) => setXtreamPassword(e.target.value)}
                                autoComplete="new-password"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Padrão Xtream: usuário <code className="rounded bg-white/60 px-1 dark:bg-black/30">channelsday</code> / senha{' '}
                            <code className="rounded bg-white/60 px-1 dark:bg-black/30">daychannels</code>. A API completa os campos se estiverem vazios.
                          </p>

                          <div className="grid gap-3 sm:grid-cols-1 md:grid-cols-2">
                            <div className="space-y-1.5">
                              <Label htmlFor="gbc-p2p-user">Usuário P2P (BinStream)</Label>
                              <Input
                                id="gbc-p2p-user"
                                className="font-mono text-sm"
                                placeholder="linha P2P válida (não channelsday)"
                                value={p2pUsername}
                                onChange={(e) => setP2pUsername(e.target.value)}
                                autoComplete="off"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="gbc-p2p-pass">Senha P2P</Label>
                              <Input
                                id="gbc-p2p-pass"
                                type="password"
                                className="font-mono text-sm"
                                placeholder="senha da linha P2P"
                                value={p2pPassword}
                                onChange={(e) => setP2pPassword(e.target.value)}
                                autoComplete="new-password"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Hints P2P autenticam em{' '}
                            <code className="rounded bg-white/60 px-1 text-[10px] dark:bg-black/30">settings.binstreamApiUrl</code>{' '}
                            (ou api1.hojevai.com). <strong>Não</strong> use a linha Xtream channelsday — ela não existe no BinStream.
                          </p>

                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={!!broadcastBusy}
                              onClick={async () => {
                                setBroadcastError(null);
                                const auth = resolveAuthToken();
                                if (!auth) {
                                  redirectToLogin();
                                  return;
                                }
                                setBroadcastBusy('today');
                                try {
                                  const base = getPanelApiBase();
                                  const res = await fetch(`${base}/api/games-broadcast-map/today`, {
                                    method: 'GET',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    }
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    throw new Error(
                                      (data as { error?: string }).error ||
                                        `HTTP ${res.status}: ${res.statusText}`
                                    );
                                  }
                                  const g = Array.isArray((data as { games?: unknown }).games)
                                    ? (data as { games: BroadcastGameRow[] }).games
                                    : [];
                                  setBroadcastGames(g);
                                  setBroadcastCacheHint(
                                    (data as { message?: string }).message ||
                                      ((data as { cache?: { lastUpdated?: string } }).cache?.lastUpdated
                                        ? `Cache: ${new Date(
                                            String((data as { cache: { lastUpdated: string } }).cache.lastUpdated)
                                          ).toLocaleString('pt-BR')}`
                                        : null)
                                  );
                                  window.alert(
                                    `Lista carregada: ${g.length} jogo(s) no painel. ${(data as { enabled?: boolean }).enabled === false ? 'Jogos do dia desativados no CMS.' : ''}`
                                  );
                                } catch (e: unknown) {
                                  const msg = e instanceof Error ? e.message : 'Erro ao carregar jogos.';
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                } finally {
                                  setBroadcastBusy(null);
                                }
                              }}
                            >
                              {broadcastBusy === 'today' ? (
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Play className="mr-2 h-4 w-4" />
                              )}
                              Carregar jogos (cache)
                            </Button>

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!!broadcastBusy}
                              onClick={async () => {
                                setBroadcastError(null);
                                const auth = resolveAuthToken();
                                if (!auth) {
                                  redirectToLogin();
                                  return;
                                }
                                if (!xtreamServer.trim() || !xtreamUsername.trim() || !xtreamPassword) {
                                  // API completa com DNS do slug + channelsday/daychannels
                                  if (!xtreamUsername.trim()) setXtreamUsername('channelsday');
                                  if (!xtreamPassword) setXtreamPassword('daychannels');
                                }
                                setBroadcastBusy('streams');
                                try {
                                  const base = getPanelApiBase();
                                  const res = await fetch(`${base}/api/games-broadcast-map/xtream-streams`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    },
                                    body: JSON.stringify({
                                      server: xtreamServer.trim(),
                                      username: xtreamUsername.trim() || 'channelsday',
                                      password: xtreamPassword || 'daychannels',
                                      maxRows: 20000
                                    })
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    throw new Error(
                                      (data as { error?: string }).error ||
                                        `HTTP ${res.status}: ${res.statusText}`
                                    );
                                  }
                                  const n = Number((data as { count?: number }).count);
                                  setXtreamStreamsCount(Number.isFinite(n) ? n : 0);
                                  window.alert(
                                    `Grade Xtream: ${Number.isFinite(n) ? n : 0} canais carregados (resposta da API).`
                                  );
                                } catch (e: unknown) {
                                  const msg = e instanceof Error ? e.message : 'Erro ao carregar canais.';
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                } finally {
                                  setBroadcastBusy(null);
                                }
                              }}
                            >
                              {broadcastBusy === 'streams' ? (
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Database className="mr-2 h-4 w-4" />
                              )}
                              Carregar canais (Xtream)
                            </Button>

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!!broadcastBusy}
                              onClick={async () => {
                                setBroadcastError(null);
                                const auth = resolveAuthToken();
                                if (!auth) {
                                  redirectToLogin();
                                  return;
                                }
                                if (!xtreamUsername.trim()) setXtreamUsername('channelsday');
                                if (!xtreamPassword) setXtreamPassword('daychannels');
                                setBroadcastBusy('hints');
                                try {
                                  const base = getPanelApiBase();
                                  const hintsUrl = String(formData.gamesOfDay?.broadcastHintsUrl || '').trim();
                                  const res = await fetch(`${base}/api/games-broadcast-map/hints-fill`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    },
                                    body: JSON.stringify({
                                      xtream: {
                                        server: xtreamServer.trim(),
                                        username: xtreamUsername.trim() || 'channelsday',
                                        password: xtreamPassword || 'daychannels'
                                      },
                                      apply: false,
                                      ...(hintsUrl && /^https?:\/\//i.test(hintsUrl)
                                        ? { broadcastHintsUrl: hintsUrl }
                                        : {})
                                    })
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    throw new Error(
                                      (data as { error?: string }).error ||
                                        `HTTP ${res.status}: ${res.statusText}`
                                    );
                                  }
                                  const cnt = Number((data as { count?: number }).count);
                                  window.alert(
                                    `Prévia por hints: ${Number.isFinite(cnt) ? cnt : 0} sugestão(ões). Use «Aplicar hints» para gravar no MongoDB.`
                                  );
                                } catch (e: unknown) {
                                  const msg = e instanceof Error ? e.message : 'Erro no hints-fill.';
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                } finally {
                                  setBroadcastBusy(null);
                                }
                              }}
                            >
                              {broadcastBusy === 'hints' ? (
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Zap className="mr-2 h-4 w-4" />
                              )}
                              Carregar com hints (prévia)
                            </Button>

                            <Button
                              type="button"
                              variant="default"
                              size="sm"
                              className="bg-emerald-700 hover:bg-emerald-800"
                              disabled={!!broadcastBusy}
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    'Gravar sugestões por hints na base (vínculos jogo → canais)?'
                                  )
                                ) {
                                  return;
                                }
                                setBroadcastError(null);
                                const auth = resolveAuthToken();
                                if (!auth) {
                                  redirectToLogin();
                                  return;
                                }
                                if (!xtreamUsername.trim()) setXtreamUsername('channelsday');
                                if (!xtreamPassword) setXtreamPassword('daychannels');
                                setBroadcastBusy('hintsApply');
                                try {
                                  const base = getPanelApiBase();
                                  const hintsUrl = String(formData.gamesOfDay?.broadcastHintsUrl || '').trim();
                                  const res = await fetch(`${base}/api/games-broadcast-map/hints-fill`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    },
                                    body: JSON.stringify({
                                      xtream: {
                                        server: xtreamServer.trim(),
                                        username: xtreamUsername.trim() || 'channelsday',
                                        password: xtreamPassword || 'daychannels'
                                      },
                                      apply: true,
                                      ...(hintsUrl && /^https?:\/\//i.test(hintsUrl)
                                        ? { broadcastHintsUrl: hintsUrl }
                                        : {})
                                    })
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    throw new Error(
                                      (data as { error?: string }).error ||
                                        `HTTP ${res.status}: ${res.statusText}`
                                    );
                                  }
                                  const saved = Number((data as { savedCount?: number }).savedCount);
                                  window.alert(
                                    `Aplicado. Registros gravados: ${Number.isFinite(saved) ? saved : 0}.`
                                  );
                                  const resToday = await fetch(`${base}/api/games-broadcast-map/today`, {
                                    method: 'GET',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    }
                                  });
                                  const todayData = await resToday.json().catch(() => ({}));
                                  if (resToday.ok && Array.isArray((todayData as { games?: unknown }).games)) {
                                    setBroadcastGames((todayData as { games: BroadcastGameRow[] }).games);
                                  }
                                } catch (e: unknown) {
                                  const msg = e instanceof Error ? e.message : 'Erro ao aplicar hints.';
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                } finally {
                                  setBroadcastBusy(null);
                                }
                              }}
                            >
                              {broadcastBusy === 'hintsApply' ? (
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="mr-2 h-4 w-4" />
                              )}
                              Aplicar hints (gravar)
                            </Button>

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!!broadcastBusy}
                              onClick={async () => {
                                setBroadcastError(null);
                                const auth = resolveAuthToken();
                                if (!auth) {
                                  redirectToLogin();
                                  return;
                                }
                                const p2pUser = p2pUsername.trim();
                                const p2pPass = p2pPassword;
                                if (!p2pUser || !p2pPass) {
                                  const msg =
                                    'Informe usuário e senha P2P (BinStream). Não use channelsday — essa linha é só Xtream.';
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                  return;
                                }
                                setBroadcastBusy('p2pStreams');
                                try {
                                  const base = getPanelApiBase();
                                  const p2pBase = String(formData.settings?.binstreamApiUrl || '').trim();
                                  const res = await fetch(`${base}/api/games-broadcast-map/p2p-streams`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    },
                                    body: JSON.stringify({
                                      baseUrl: p2pBase || undefined,
                                      username: p2pUser,
                                      password: p2pPass,
                                      maxRows: 30000,
                                      force: true
                                    })
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    throw new Error(
                                      (data as { error?: string }).error ||
                                        (res.status === 502
                                          ? 'Falha na API P2P (502). Verifique a linha BinStream e tente de novo.'
                                          : `HTTP ${res.status}: ${res.statusText}`)
                                    );
                                  }
                                  const n = Number((data as { count?: number }).count);
                                  setP2pStreamsCount(Number.isFinite(n) ? n : 0);
                                  window.alert(
                                    `Grade P2P: ${Number.isFinite(n) ? n : 0} canais (chid). A linha precisa existir no BinStream.`
                                  );
                                } catch (e: unknown) {
                                  const raw = e instanceof Error ? e.message : 'Erro ao carregar canais P2P.';
                                  const msg =
                                    raw === 'Failed to fetch'
                                      ? 'Falha de rede/CORS ao falar com a API. Confirme que a linha P2P existe no BinStream (não use channelsday).'
                                      : raw;
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                } finally {
                                  setBroadcastBusy(null);
                                }
                              }}
                            >
                              {broadcastBusy === 'p2pStreams' ? (
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Database className="mr-2 h-4 w-4" />
                              )}
                              Carregar canais (P2P)
                            </Button>

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!!broadcastBusy}
                              onClick={async () => {
                                setBroadcastError(null);
                                const auth = resolveAuthToken();
                                if (!auth) {
                                  redirectToLogin();
                                  return;
                                }
                                const p2pUser = p2pUsername.trim();
                                const p2pPass = p2pPassword;
                                if (!p2pUser || !p2pPass) {
                                  const msg =
                                    'Informe usuário e senha P2P (BinStream). Não use channelsday — essa linha é só Xtream.';
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                  return;
                                }
                                setBroadcastBusy('p2pHints');
                                try {
                                  const base = getPanelApiBase();
                                  const hintsUrl = String(formData.gamesOfDay?.broadcastHintsUrl || '').trim();
                                  const p2pBase = String(formData.settings?.binstreamApiUrl || '').trim();
                                  const res = await fetch(`${base}/api/games-broadcast-map/hints-fill-p2p`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    },
                                    body: JSON.stringify({
                                      baseUrl: p2pBase || undefined,
                                      username: p2pUser,
                                      password: p2pPass,
                                      apply: false,
                                      force: true,
                                      ...(hintsUrl && /^https?:\/\//i.test(hintsUrl)
                                        ? { broadcastHintsUrl: hintsUrl }
                                        : {})
                                    })
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    throw new Error(
                                      (data as { error?: string }).error ||
                                        `HTTP ${res.status}: ${res.statusText}`
                                    );
                                  }
                                  const cnt = Number((data as { count?: number }).count);
                                  const catalogN = Number((data as { p2pStreamsCount?: number }).p2pStreamsCount);
                                  if (Number.isFinite(catalogN)) setP2pStreamsCount(catalogN);
                                  window.alert(
                                    `Prévia P2P por hints: ${Number.isFinite(cnt) ? cnt : 0} sugestão(ões). Use «Aplicar hints P2P» para gravar.`
                                  );
                                } catch (e: unknown) {
                                  const raw = e instanceof Error ? e.message : 'Erro no hints-fill P2P.';
                                  const msg =
                                    raw === 'Failed to fetch'
                                      ? 'Falha de rede/CORS ao falar com a API. Use uma linha P2P válida no BinStream.'
                                      : raw;
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                } finally {
                                  setBroadcastBusy(null);
                                }
                              }}
                            >
                              {broadcastBusy === 'p2pHints' ? (
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              ) : null}
                              Carregar com hints P2P (prévia)
                            </Button>

                            <Button
                              type="button"
                              size="sm"
                              className="bg-emerald-700 hover:bg-emerald-800 text-white"
                              disabled={!!broadcastBusy}
                              onClick={async () => {
                                setBroadcastError(null);
                                const auth = resolveAuthToken();
                                if (!auth) {
                                  redirectToLogin();
                                  return;
                                }
                                const p2pUser = p2pUsername.trim();
                                const p2pPass = p2pPassword;
                                if (!p2pUser || !p2pPass) {
                                  const msg =
                                    'Informe usuário e senha P2P (BinStream). Não use channelsday — essa linha é só Xtream.';
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                  return;
                                }
                                if (
                                  !window.confirm(
                                    'Gravar sugestões P2P (p2pStreamIds) no mapa global? Os IDs IPTV não são alterados.'
                                  )
                                ) {
                                  return;
                                }
                                setBroadcastBusy('p2pHintsApply');
                                try {
                                  const base = getPanelApiBase();
                                  const hintsUrl = String(formData.gamesOfDay?.broadcastHintsUrl || '').trim();
                                  const p2pBase = String(formData.settings?.binstreamApiUrl || '').trim();
                                  const res = await fetch(`${base}/api/games-broadcast-map/hints-fill-p2p`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    },
                                    body: JSON.stringify({
                                      baseUrl: p2pBase || undefined,
                                      username: p2pUser,
                                      password: p2pPass,
                                      apply: true,
                                      force: true,
                                      ...(hintsUrl && /^https?:\/\//i.test(hintsUrl)
                                        ? { broadcastHintsUrl: hintsUrl }
                                        : {})
                                    })
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    throw new Error(
                                      (data as { error?: string }).error ||
                                        `HTTP ${res.status}: ${res.statusText}`
                                    );
                                  }
                                  const saved = Number((data as { savedCount?: number }).savedCount);
                                  window.alert(
                                    `P2P aplicado. Registros gravados: ${Number.isFinite(saved) ? saved : 0}.`
                                  );
                                  const resToday = await fetch(`${base}/api/games-broadcast-map/today`, {
                                    method: 'GET',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    }
                                  });
                                  const todayData = await resToday.json().catch(() => ({}));
                                  if (resToday.ok && Array.isArray((todayData as { games?: unknown }).games)) {
                                    setBroadcastGames((todayData as { games: BroadcastGameRow[] }).games);
                                  }
                                } catch (e: unknown) {
                                  const raw = e instanceof Error ? e.message : 'Erro ao aplicar hints P2P.';
                                  const msg =
                                    raw === 'Failed to fetch'
                                      ? 'Falha de rede/CORS ao falar com a API. Use uma linha P2P válida no BinStream.'
                                      : raw;
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                } finally {
                                  setBroadcastBusy(null);
                                }
                              }}
                            >
                              {broadcastBusy === 'p2pHintsApply' ? (
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="mr-2 h-4 w-4" />
                              )}
                              Aplicar hints P2P (gravar)
                            </Button>

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!!broadcastBusy || broadcastGames.length === 0}
                              onClick={async () => {
                                setBroadcastError(null);
                                const auth = resolveAuthToken();
                                if (!auth) {
                                  redirectToLogin();
                                  return;
                                }
                                if (!xtreamUsername.trim()) setXtreamUsername('channelsday');
                                if (!xtreamPassword) setXtreamPassword('daychannels');
                                const fixtures = broadcastGames
                                  .filter(
                                    (g) =>
                                      g.fixtureId != null &&
                                      g.teams?.home?.name &&
                                      g.teams?.away?.name
                                  )
                                  .slice(0, 40)
                                  .map((g) => ({
                                    fixtureId: g.fixtureId,
                                    home: String(g.teams?.home?.name || ''),
                                    away: String(g.teams?.away?.name || ''),
                                    league: String(g.league?.name || ''),
                                    broadcastHint: String(g.broadcastHint || '')
                                  }));
                                if (!fixtures.length) {
                                  window.alert('Carregue primeiro «Carregar jogos (cache)».');
                                  return;
                                }
                                if (
                                  !window.confirm(
                                    `Sugerir canais via EPG curto para ${fixtures.length} jogo(s)? Pode demorar.`
                                  )
                                ) {
                                  return;
                                }
                                setBroadcastBusy('epg');
                                try {
                                  const base = getPanelApiBase();
                                  const res = await fetch(`${base}/api/games-broadcast-map/epg-suggest`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${auth}`,
                                      ...(cmsUser?.cmsSlug ? { 'X-CMS-Slug': cmsUser.cmsSlug } : {})
                                    },
                                    body: JSON.stringify({
                                      fixtures,
                                      xtream: {
                                        server: xtreamServer.trim(),
                                        username: xtreamUsername.trim() || 'channelsday',
                                        password: xtreamPassword || 'daychannels'
                                      },
                                      topK: 14,
                                      epgLimit: 12,
                                      maxConcurrency: 4
                                    })
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    throw new Error(
                                      (data as { error?: string }).error ||
                                        `HTTP ${res.status}: ${res.statusText}`
                                    );
                                  }
                                  const cnt = Number((data as { count?: number }).count);
                                  window.alert(
                                    `EPG: ${Number.isFinite(cnt) ? cnt : 0} sugestão(ões). Confira a API para aplicar ao mapa (PUT por fixture) se necessário.`
                                  );
                                } catch (e: unknown) {
                                  const msg = e instanceof Error ? e.message : 'Erro no EPG.';
                                  setBroadcastError(msg);
                                  window.alert(msg);
                                } finally {
                                  setBroadcastBusy(null);
                                }
                              }}
                            >
                              {broadcastBusy === 'epg' ? (
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Clock className="mr-2 h-4 w-4" />
                              )}
                              Sugerir via EPG (lento)
                            </Button>
                          </div>

                          {xtreamStreamsCount != null && (
                            <p className="text-xs text-muted-foreground">
                              Última contagem de canais na grade Xtream: <strong>{xtreamStreamsCount}</strong>
                            </p>
                          )}
                          {p2pStreamsCount != null && (
                            <p className="text-xs text-muted-foreground">
                              Última contagem de canais na grade P2P: <strong>{p2pStreamsCount}</strong>
                            </p>
                          )}
                          <p className="text-[11px] text-muted-foreground">
                            Hints P2P usam os campos <strong>Usuário/Senha P2P</strong> acima e a API em{' '}
                            <code className="text-[10px]">settings.binstreamApiUrl</code> (ou api1.hojevai.com).
                            Grava só <code className="text-[10px]">p2pStreamIds</code> — não altera IPTV.
                          </p>
                          {broadcastCacheHint && (
                            <p className="text-xs text-muted-foreground">{broadcastCacheHint}</p>
                          )}

                          <div className="flex flex-col gap-2 rounded-md border border-dashed bg-background/60 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={broadcastSaveTargetGlobal}
                                onCheckedChange={(v) => setBroadcastSaveTargetGlobal(!!v)}
                              />
                              <span className="text-xs font-normal leading-snug">
                                Gravar edição manual no <strong>mapa global</strong> (todos os CMS com jogos do dia). Desligue
                                para gravar só no slug deste painel (override local).
                              </span>
                            </div>
                          </div>

                          {broadcastGames.length > 0 && (
                            <div className="max-h-[28rem] overflow-auto rounded-md border bg-background/80">
                              <table className="w-full min-w-[980px] text-left text-xs">
                                <thead className="sticky top-0 z-[1] bg-muted/95 backdrop-blur-sm">
                                  <tr>
                                    <th className="p-2 w-16">ID</th>
                                    <th className="p-2 min-w-[140px]">Jogo</th>
                                    <th className="p-2 min-w-[100px]">Hint</th>
                                    <th className="p-2 min-w-[110px]">IPTV stream_id</th>
                                    <th className="p-2 min-w-[100px]">Rótulo IPTV</th>
                                    <th className="p-2 min-w-[110px]">P2P stream_id</th>
                                    <th className="p-2 min-w-[100px]">Rótulo P2P</th>
                                    <th className="p-2 min-w-[90px]">Notas</th>
                                    <th className="p-2 w-[140px]">Ações</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {broadcastGames.map((row) => {
                                    const fid = row.fixtureId;
                                    const hid = row.teams?.home?.name || '';
                                    const aid = row.teams?.away?.name || '';
                                    const k = fid != null ? String(fid) : '';
                                    const draft =
                                      k && broadcastDrafts[k]
                                        ? broadcastDrafts[k]
                                        : { ids: '', labels: '', p2pIds: '', p2pLabels: '', notes: '' };
                                    const day =
                                      row.fixtureDate != null
                                        ? String(row.fixtureDate).slice(0, 10)
                                        : '';
                                    return (
                                      <tr key={k || `row-${hid}-${aid}`} className="border-t align-top">
                                        <td className="p-2 font-mono">{fid ?? '—'}</td>
                                        <td className="p-2">
                                          <span className="font-medium">
                                            {hid} <span className="text-muted-foreground">x</span> {aid}
                                          </span>
                                          {row.league?.name ? (
                                            <div className="mt-0.5 text-[10px] text-muted-foreground">
                                              {row.league.name}
                                            </div>
                                          ) : null}
                                        </td>
                                        <td className="p-2 text-[11px] text-muted-foreground max-w-[140px] break-words">
                                          {row.broadcastHint || '—'}
                                        </td>
                                        <td className="p-1">
                                          <Input
                                            className="h-8 font-mono text-[11px]"
                                            placeholder="ex: 1201, 1202"
                                            value={draft.ids}
                                            disabled={fid == null || broadcastFixtureBusy === fid}
                                            onChange={(e) => {
                                              if (fid == null) return;
                                              const key = String(fid);
                                              setBroadcastDrafts((prev) => ({
                                                ...prev,
                                                [key]: {
                                                  ...(prev[key] || { ids: '', labels: '', p2pIds: '', p2pLabels: '', notes: '' }),
                                                  ids: e.target.value
                                                }
                                              }));
                                            }}
                                          />
                                        </td>
                                        <td className="p-1">
                                          <Input
                                            className="h-8 text-[11px]"
                                            placeholder="rótulos, mesma ordem (vírgula)"
                                            value={draft.labels}
                                            disabled={fid == null || broadcastFixtureBusy === fid}
                                            onChange={(e) => {
                                              if (fid == null) return;
                                              const key = String(fid);
                                              setBroadcastDrafts((prev) => ({
                                                ...prev,
                                                [key]: {
                                                  ...(prev[key] || { ids: '', labels: '', p2pIds: '', p2pLabels: '', notes: '' }),
                                                  labels: e.target.value
                                                }
                                              }));
                                            }}
                                          />
                                        </td>
                                        <td className="p-1">
                                          <Input
                                            className="h-8 font-mono text-[11px]"
                                            placeholder="ID P2P"
                                            value={draft.p2pIds || ''}
                                            disabled={fid == null || broadcastFixtureBusy === fid}
                                            onChange={(e) => {
                                              if (fid == null) return;
                                              const key = String(fid);
                                              setBroadcastDrafts((prev) => ({
                                                ...prev,
                                                [key]: {
                                                  ...(prev[key] || { ids: '', labels: '', p2pIds: '', p2pLabels: '', notes: '' }),
                                                  p2pIds: e.target.value
                                                }
                                              }));
                                            }}
                                          />
                                        </td>
                                        <td className="p-1">
                                          <Input
                                            className="h-8 text-[11px]"
                                            placeholder="rótulo P2P"
                                            value={draft.p2pLabels || ''}
                                            disabled={fid == null || broadcastFixtureBusy === fid}
                                            onChange={(e) => {
                                              if (fid == null) return;
                                              const key = String(fid);
                                              setBroadcastDrafts((prev) => ({
                                                ...prev,
                                                [key]: {
                                                  ...(prev[key] || { ids: '', labels: '', p2pIds: '', p2pLabels: '', notes: '' }),
                                                  p2pLabels: e.target.value
                                                }
                                              }));
                                            }}
                                          />
                                        </td>
                                        <td className="p-1">
                                          <Input
                                            className="h-8 text-[11px]"
                                            placeholder="opcional"
                                            value={draft.notes}
                                            disabled={fid == null || broadcastFixtureBusy === fid}
                                            onChange={(e) => {
                                              if (fid == null) return;
                                              const key = String(fid);
                                              setBroadcastDrafts((prev) => ({
                                                ...prev,
                                                [key]: {
                                                  ...(prev[key] || { ids: '', labels: '', p2pIds: '', p2pLabels: '', notes: '' }),
                                                  notes: e.target.value
                                                }
                                              }));
                                            }}
                                          />
                                        </td>
                                        <td className="p-1 space-y-1">
                                          {fid != null ? (
                                            <>
                                              <Button
                                                type="button"
                                                size="sm"
                                                className="h-7 w-full text-[11px]"
                                                disabled={!!broadcastBusy || broadcastFixtureBusy === fid}
                                                onClick={async () => {
                                                  setBroadcastError(null);
                                                  const auth = resolveAuthToken();
                                                  if (!auth) {
                                                    redirectToLogin();
                                                    return;
                                                  }
                                                  const streamIds = parseBroadcastStreamIds(draft.ids);
                                                  const labelParts = String(draft.labels || '')
                                                    .split(',')
                                                    .map((s) => s.trim());
                                                  const streamLabels =
                                                    labelParts.length && streamIds.length
                                                      ? streamIds.map((_, i) => labelParts[i] || '')
                                                      : [];
                                                  setBroadcastFixtureBusy(fid);
                                                  try {
                                                    const base = getPanelApiBase();
                                                    const res = await fetch(
                                                      `${base}/api/games-broadcast-map/${fid}`,
                                                      {
                                                        method: 'PUT',
                                                        headers: {
                                                          'Content-Type': 'application/json',
                                                          Authorization: `Bearer ${auth}`,
                                                          ...(cmsUser?.cmsSlug
                                                            ? { 'X-CMS-Slug': cmsUser.cmsSlug }
                                                            : {})
                                                        },
                                                        body: JSON.stringify({
                                                          streamIds,
                                                          streamLabels,
                                                          p2pStreamIds: parseBroadcastStreamIds(draft.p2pIds || ''),
                                                          p2pStreamLabels: (() => {
                                                            const p2pIds = parseBroadcastStreamIds(draft.p2pIds || '');
                                                            const parts = String(draft.p2pLabels || '')
                                                              .split(',')
                                                              .map((s) => s.trim());
                                                            return parts.length && p2pIds.length
                                                              ? p2pIds.map((_, i) => parts[i] || '')
                                                              : [];
                                                          })(),
                                                          notes: draft.notes || '',
                                                          day: day || undefined,
                                                          saveToGlobalMap: broadcastSaveTargetGlobal
                                                        })
                                                      }
                                                    );
                                                    const data = await res.json().catch(() => ({}));
                                                    if (!res.ok) {
                                                      throw new Error(
                                                        (data as { error?: string }).error ||
                                                          `HTTP ${res.status}`
                                                      );
                                                    }
                                                    const item = (data as { item?: BroadcastGameRow & { streamIds?: number[]; streamLabels?: string[]; notes?: string } }).item;
                                                    setBroadcastGames((prev) =>
                                                      prev.map((r) =>
                                                        r.fixtureId === fid
                                                          ? {
                                                              ...r,
                                                              streamIds: item?.streamIds ?? streamIds,
                                                              streamLabels: item?.streamLabels ?? streamLabels,
                                                              p2pStreamIds: item?.p2pStreamIds ?? parseBroadcastStreamIds(draft.p2pIds || ''),
                                                              p2pStreamLabels: item?.p2pStreamLabels ?? [],
                                                              notes: item?.notes ?? draft.notes
                                                            }
                                                          : r
                                                      )
                                                    );
                                                    window.alert('Vínculo gravado com sucesso.');
                                                  } catch (e: unknown) {
                                                    const msg =
                                                      e instanceof Error ? e.message : 'Erro ao gravar.';
                                                    setBroadcastError(msg);
                                                    window.alert(msg);
                                                  } finally {
                                                    setBroadcastFixtureBusy(null);
                                                  }
                                                }}
                                              >
                                                {broadcastFixtureBusy === fid ? (
                                                  <RefreshCw className="h-3 w-3 animate-spin inline mr-1" />
                                                ) : null}
                                                Gravar
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-7 w-full text-[11px]"
                                                disabled={!!broadcastBusy || broadcastFixtureBusy === fid}
                                                onClick={async () => {
                                                  if (
                                                    !window.confirm(
                                                      'Remover o vínculo deste jogo (stream_ids) na base?'
                                                    )
                                                  ) {
                                                    return;
                                                  }
                                                  setBroadcastError(null);
                                                  const auth = resolveAuthToken();
                                                  if (!auth) {
                                                    redirectToLogin();
                                                    return;
                                                  }
                                                  setBroadcastFixtureBusy(fid);
                                                  try {
                                                    const base = getPanelApiBase();
                                                    const q = broadcastSaveTargetGlobal ? '?global=1' : '';
                                                    const res = await fetch(
                                                      `${base}/api/games-broadcast-map/${fid}${q}`,
                                                      {
                                                        method: 'DELETE',
                                                        headers: {
                                                          'Content-Type': 'application/json',
                                                          Authorization: `Bearer ${auth}`,
                                                          ...(cmsUser?.cmsSlug
                                                            ? { 'X-CMS-Slug': cmsUser.cmsSlug }
                                                            : {})
                                                        }
                                                      }
                                                    );
                                                    const data = await res.json().catch(() => ({}));
                                                    if (!res.ok) {
                                                      throw new Error(
                                                        (data as { error?: string }).error ||
                                                          `HTTP ${res.status}`
                                                      );
                                                    }
                                                    setBroadcastGames((prev) =>
                                                      prev.map((r) =>
                                                        r.fixtureId === fid
                                                          ? {
                                                              ...r,
                                                              streamIds: [],
                                                              streamLabels: [],
                                                              p2pStreamIds: [],
                                                              p2pStreamLabels: [],
                                                              notes: ''
                                                            }
                                                          : r
                                                      )
                                                    );
                                                    window.alert('Vínculo removido.');
                                                  } catch (e: unknown) {
                                                    const msg =
                                                      e instanceof Error ? e.message : 'Erro ao remover.';
                                                    setBroadcastError(msg);
                                                    window.alert(msg);
                                                  } finally {
                                                    setBroadcastFixtureBusy(null);
                                                  }
                                                }}
                                              >
                                                Limpar
                                              </Button>
                                            </>
                                          ) : (
                                            <span className="text-muted-foreground">—</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Info Card */}
                    <Separator />
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            Informações Importantes
                          </p>
                          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                            <li>A API Key é fornecida pelo <a href="https://www.api-football.com/dashboard" target="_blank" rel="noopener noreferrer" className="underline font-medium">API-Football v3</a></li>
                            <li><strong>Otimização:</strong> Buscamos TODOS os jogos do dia em uma única requisição e filtramos localmente</li>
                            <li>Os filtros de <strong>países e ligas</strong> são configuráveis pelo CMS acima</li>
                            <li>Se nenhum filtro for configurado, serão usados os valores padrão (Brasil + principais países europeus + principais ligas)</li>
                            <li>A API v3 usa autenticação via headers <code className="bg-blue-100 px-1 rounded text-xs">X-RapidAPI-Key</code> ou <code className="bg-blue-100 px-1 rounded text-xs">x-apisports-key</code></li>
                            <li>Os jogos são exibidos automaticamente no dia atual</li>
                            <li>A página será acessível através do menu do aplicativo</li>
                            <li>Endpoint utilizado: <code className="bg-blue-100 px-1 rounded text-xs">https://v3.football.api-sports.io/fixtures?date=YYYY-MM-DD</code> (sem filtro de país)</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'trial' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TestTube className="h-5 w-5" />
                  Gerador de Testes Automático
                </CardTitle>
                <CardDescription>
                  Configure seu token para gerar testes automaticamente
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Switch para ativar/desativar trial generator no app */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50">
                  <div className="flex-1">
                    <Label className="text-base font-medium">
                      Exibir botão de teste no aplicativo
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Quando ativado, o botão de teste automático aparecerá na tela de login do app
                    </p>
                  </div>
                  <Switch
                    checked={trialSettings.trialGeneratorEnabled}
                    onCheckedChange={(checked) => handleTrialSettingsChange('trialGeneratorEnabled', checked)}
                  />
                </div>
                
                <Separator />
                
                {/* Alert sobre necessidade de token */}
                {!trialSettings.hasToken && (
                  <div className="p-4 border-2 border-yellow-200 bg-yellow-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-yellow-900 mb-1">Token Necessário</h4>
                        <p className="text-sm text-yellow-800 mb-2">
                          Para usar o gerador de testes, você precisa configurar seu token abaixo.
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <Ticket className="h-4 w-4 text-yellow-700" />
                          <p className="text-xs text-yellow-700 font-medium">
                            Não tem um token? Abra um ticket solicitando seu token de acesso.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Configuração do Token */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="xuiApiUrl">URL do servidor</Label>
                    <Input
                      id="xuiApiUrl"
                      type="url"
                      value={trialSettings.xuiApiUrl}
                      onChange={(e) => handleTrialSettingsChange('xuiApiUrl', e.target.value)}
                      placeholder="http://seuservidor.com"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      URL completa do servidor (fornecida pelo suporte)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="xuiApiToken">Token de Autenticação</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="xuiApiToken"
                        type={showTrialToken ? 'text' : 'password'}
                        value={trialSettings.xuiApiToken}
                        onChange={(e) => handleTrialSettingsChange('xuiApiToken', e.target.value)}
                        placeholder="seu-token-xui-aqui"
                        className="font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowTrialToken(!showTrialToken)}
                      >
                        {showTrialToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Token de autenticação fornecido pelo suporte
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="xuiTrialPackageId">Pacote para Testes (XUI)</Label>
                    {trialSettings.xuiIndependentEnabled && xuiRemotePackages.length > 0 ? (
                      <Select
                        value={String(trialSettings.xuiTrialPackageId || '')}
                        onValueChange={(v) => handleTrialSettingsChange('xuiTrialPackageId', parseInt(v, 10))}
                      >
                        <SelectTrigger className="max-w-md">
                          <SelectValue placeholder="Selecione o pacote de teste" />
                        </SelectTrigger>
                        <SelectContent>
                          {xuiRemotePackages.map((pkg) => (
                            <SelectItem key={pkg.id} value={String(pkg.id)}>
                              {pkg.display_name || pkg.package_name || pkg.name || `Pacote #${pkg.id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="xuiTrialPackageId"
                        type="number"
                        value={trialSettings.xuiTrialPackageId}
                        onChange={(e) => handleTrialSettingsChange('xuiTrialPackageId', parseInt(e.target.value))}
                        placeholder="194"
                        className="max-w-xs"
                      />
                    )}
                    <p className="text-xs text-muted-foreground">
                      {trialSettings.xuiIndependentEnabled
                        ? 'No modo independente, escolha um pacote do seu XUI com duração de teste (trial_duration) maior que zero.'
                        : 'ID Teste com adultos (194) ou ID Teste sem adultos (195)'}
                    </p>
                  </div>

                  {/* Modo independente: usar o XUI próprio deste revendedor */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-purple-50">
                    <div className="flex-1 pr-4">
                      <Label className="text-base font-medium">
                        Usar meu próprio painel XUI (independente)
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Quando ativado, os testes são criados no XUI configurado acima (URL, token e pacote deste revendedor),
                        sem depender do painel principal. Quando desativado, usa o painel padrão do sistema.
                      </p>
                    </div>
                    <Switch
                      checked={trialSettings.xuiIndependentEnabled}
                      onCheckedChange={(checked) => handleTrialSettingsChange('xuiIndependentEnabled', checked)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="appDownloadUrl">Link do Aplicativo para Download</Label>
                    <Input
                      id="appDownloadUrl"
                      type="url"
                      value={trialSettings.appDownloadUrl}
                      onChange={(e) => handleTrialSettingsChange('appDownloadUrl', e.target.value)}
                      placeholder="https://play.google.com/store/apps/... ou https://seusite.com/app.apk"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Link que será exibido no botão "Baixar Aplicativo" da página de teste (Google Play, link direto APK, etc)
                    </p>
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      variant="outline"
                      onClick={handleValidateToken}
                      disabled={validatingToken || !trialSettings.xuiApiUrl || !trialSettings.xuiApiToken}
                    >
                      {validatingToken ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Validando...
                        </>
                      ) : (
                        <>
                          <Shield className="h-4 w-4 mr-2" />
                          Validar Token
                        </>
                      )}
                    </Button>

                    <Button
                      variant="secondary"
                      onClick={handleSaveTrialSettings}
                      disabled={saving || !trialSettings.xuiApiUrl || !trialSettings.xuiApiToken}
                    >
                      {saving ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salvar Configurações
                        </>
                      )}
                    </Button>

                    {tokenValid !== null && (
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
                        tokenValid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {tokenValid ? (
                          <>
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-sm font-medium">Token Válido</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Token Inválido</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Links Públicos para Compartilhamento */}
                {trialSettings.hasToken && trialSettings.publicTrialUrlApi && (
                  <div className="space-y-4">
                    <div className="p-4 border-2 border-indigo-200 bg-indigo-50 rounded-lg">
                      <div className="flex items-start gap-3 mb-4">
                        <Link className="h-6 w-6 text-indigo-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-medium text-indigo-900 mb-1">Links Públicos para Geração de Testes</h4>
                          <p className="text-sm text-indigo-700">
                            Compartilhe estes links com clientes e apps. Cada acesso gera credenciais únicas automaticamente.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRegeneratePublicToken}
                          disabled={saving}
                          className="flex-shrink-0"
                          title="Regenerar ambos os links"
                        >
                          <RefreshCw className={`h-5 w-5 ${saving ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>

                      <div className="space-y-4">
                        {/* Link para Página Interativa */}
                        <div className="space-y-2">
                          <Label className="text-indigo-900 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              🌐 Página Web
                            </span>
                            Link para Clientes (Interface Visual)
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              value={trialSettings.publicTrialUrlPage}
                              readOnly
                              className="font-mono text-xs bg-white border-indigo-300"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={copyPublicUrlPage}
                              className="flex-shrink-0"
                            >
                              {publicUrlPageCopied ? (
                                <Check className="h-5 w-5 text-green-600" />
                              ) : (
                                <Copy className="h-5 w-5" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-indigo-700">
                            Cliente clica e vê uma página bonita com username/senha para copiar. Ideal para WhatsApp, sites e redes sociais.
                          </p>
                        </div>

                        <Separator className="my-3" />

                        {/* Link para API JSON */}
                        <div className="space-y-2">
                          <Label className="text-indigo-900 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                              📱 API JSON
                            </span>
                            Link para Apps e Integrações
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              value={trialSettings.publicTrialUrlApi}
                              readOnly
                              className="font-mono text-xs bg-white border-indigo-300"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={copyPublicUrlApi}
                              className="flex-shrink-0"
                            >
                              {publicUrlApiCopied ? (
                                <Check className="h-5 w-5 text-green-600" />
                              ) : (
                                <Copy className="h-5 w-5" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-indigo-700">
                            Apps fazem GET request e recebem JSON com username/senha. Ideal para apps mobile, TV e integrações automáticas.
                          </p>
                        </div>
                      </div>

                      {/* Exemplo de Resposta API */}
                      <div className="mt-4 p-3 bg-gray-800 rounded-md">
                        <p className="text-xs text-gray-300 mb-1">📱 Resposta JSON da API:</p>
                        <pre className="text-xs text-green-400 font-mono overflow-x-auto">
{`{
  "success": true,
  "username": "aB3xK9mP",
  "password": "4f7a2c8e",
  "expDate": "2025-11-08 14:30",
  "duration": 24
}`}
                        </pre>
                      </div>

                      {/* Dica de Regeneração */}
                      <div className="mt-3 text-xs text-indigo-600">
                        💡 <strong>Regenerar links:</strong> Clique no botão 🔄 no topo para gerar novos links (invalida os anteriores)
                      </div>
                    </div>
                  </div>
                )}

                <Separator />

                {/* Gerador de Teste */}
                {trialSettings.hasToken ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900 mb-1">Gerar Teste Agora</h4>
                        <p className="text-sm text-blue-700">
                          Gera automaticamente um teste de 24 horas com credenciais aleatórias
                        </p>
                      </div>
                      <Button
                        size="lg"
                        onClick={handleGenerateTrial}
                        disabled={generatingTrial}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                      >
                        {generatingTrial ? (
                          <>
                            <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                            Gerando...
                          </>
                        ) : (
                          <>
                            <Zap className="h-5 w-5 mr-2" />
                            Gerar Teste
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Último Teste Gerado */}
                    {lastGeneratedTrial && (
                      <div className="p-4 border-2 border-green-200 bg-green-50 rounded-lg">
                        <h4 className="font-medium text-green-900 mb-3 flex items-center gap-2">
                          <CheckCircle className="h-5 w-5" />
                          Último Teste Gerado com Sucesso
                        </h4>
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-green-700">Username</Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  value={lastGeneratedTrial.username}
                                  readOnly
                                  className="font-mono text-sm bg-white"
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => copyTrialCredentials(lastGeneratedTrial.username)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-green-700">Password</Label>
                              <div className="flex items-center gap-2">
                                <Input
                                  value={lastGeneratedTrial.password}
                                  readOnly
                                  className="font-mono text-sm bg-white"
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => copyTrialCredentials(lastGeneratedTrial.password)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-green-700 space-y-1">
                            <p><strong>Duração:</strong> {lastGeneratedTrial.duration} horas</p>
                            <p><strong>Expira em:</strong> {lastGeneratedTrial.expDate}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-6 text-center border-2 border-dashed border-gray-300 rounded-lg">
                    <Ticket className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h4 className="font-medium text-gray-700 mb-2">Configure seu token primeiro</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Preencha os campos acima e salve para começar a gerar testes
                    </p>
                    <p className="text-xs text-gray-500">
                      💡 Não tem token? Abra um ticket no suporte!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* WhatsApp Automation Tab */}
        {activeTab === 'whatsapp' && cmsUser.role !== 'client' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-amber-600" />
                  Aviso Importante
                </CardTitle>
                <CardDescription>
                  Integração utilizando a biblioteca não-oficial Baileys. Utilize por sua conta e risco.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg space-y-2">
                  <p className="text-sm text-amber-800 font-medium">
                    Antes de ativar o disparo automático, esteja ciente:
                  </p>
                  <ul className="list-disc pl-5 text-sm text-amber-800 space-y-1">
                    <li>O WhatsApp pode bloquear o número sem aviso ao detectar automações.</li>
                    <li>Prefira um chip exclusivo para cobranças e notificações.</li>
                    <li>Envie mensagens apenas para contatos que autorizaram o recebimento.</li>
                  </ul>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ao continuar você confirma que entende os riscos e aceita possíveis bloqueios do WhatsApp.
                </p>
              </CardContent>
            </Card>

            {/* Componente de Automação WhatsApp Simplificado */}
            <WhatsappAutomation isConnected={whatsappConnected} />
          </div>
        )}

        {/* System/Maintenance Tab - APENAS ADMINS */}
        {activeTab === 'system' && cmsUser.role === 'admin' && (
          <div className="space-y-6">
            {/* Limpeza Automática de Linhas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5" />
                  Limpeza Automática de Linhas
                </CardTitle>
                <CardDescription>
                  Remove automaticamente linhas com username ou password vazios do banco IPTV
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Switch */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Ativar Limpeza Automática</Label>
                    <p className="text-sm text-muted-foreground">
                      Executa limpeza periodicamente conforme agendamento
                    </p>
                  </div>
                  <Switch
                    checked={lineCleanupSettings.enabled}
                    onCheckedChange={handleLineCleanupToggle}
                    disabled={saving}
                  />
                </div>

                {lineCleanupSettings.enabled && (
                  <>
                    {/* Interval Selection - Apenas Botões */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-medium">Selecione o Intervalo de Execução</Label>
                        {lineCleanupSettings.is_running && (
                          <Badge variant="default" className="bg-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Ativo
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Button
                          variant={lineCleanupSettings.cron_expression === '*/1 * * * *' ? 'default' : 'outline'}
                          onClick={() => handleCronExpressionChange('*/1 * * * *')}
                          disabled={saving}
                          className="w-full h-20 flex flex-col items-center justify-center"
                        >
                          <Clock className="h-6 w-6 mb-2" />
                          <span className="font-semibold">1 Minuto</span>
                          <span className="text-xs opacity-80">Para testes</span>
                        </Button>
                        <Button
                          variant={lineCleanupSettings.cron_expression === '*/10 * * * *' ? 'default' : 'outline'}
                          onClick={() => handleCronExpressionChange('*/10 * * * *')}
                          disabled={saving}
                          className="w-full h-20 flex flex-col items-center justify-center"
                        >
                          <Clock className="h-6 w-6 mb-2" />
                          <span className="font-semibold">10 Minutos</span>
                          <span className="text-xs opacity-80">Limpeza rápida</span>
                        </Button>
                        <Button
                          variant={lineCleanupSettings.cron_expression === '*/30 * * * *' ? 'default' : 'outline'}
                          onClick={() => handleCronExpressionChange('*/30 * * * *')}
                          disabled={saving}
                          className="w-full h-20 flex flex-col items-center justify-center"
                        >
                          <Clock className="h-6 w-6 mb-2" />
                          <span className="font-semibold">30 Minutos</span>
                          <span className="text-xs opacity-80">Balanceado</span>
                        </Button>
                        <Button
                          variant={lineCleanupSettings.cron_expression === '0 */1 * * *' ? 'default' : 'outline'}
                          onClick={() => handleCronExpressionChange('0 */1 * * *')}
                          disabled={saving}
                          className="w-full h-20 flex flex-col items-center justify-center"
                        >
                          <Clock className="h-6 w-6 mb-2" />
                          <span className="font-semibold">1 Hora</span>
                          <span className="text-xs opacity-80">Recomendado</span>
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {/* Informação do Intervalo */}
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-900">
                            <strong>Intervalo atual:</strong> {
                              lineCleanupSettings.cron_expression === '*/1 * * * *' ? 'A cada 1 minuto' :
                              lineCleanupSettings.cron_expression === '*/10 * * * *' ? 'A cada 10 minutos' :
                              lineCleanupSettings.cron_expression === '*/30 * * * *' ? 'A cada 30 minutos' :
                              lineCleanupSettings.cron_expression === '0 */1 * * *' ? 'A cada 1 hora' :
                              'Personalizado'
                            }
                          </p>
                        </div>

                        {/* Countdown para Próxima Execução */}
                        {lineCleanupSettings.is_running && (
                          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="relative">
                                  <Clock className="h-5 w-5 text-green-600 animate-pulse" />
                                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-green-900">Próxima Execução em:</p>
                                  <p className="text-xs text-green-700">Job ativo e monitorando</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-3xl font-bold font-mono text-green-700 tracking-wider">
                                  {nextExecutionCountdown}
                                </div>
                                <p className="text-xs text-green-600 mt-1">HH:MM:SS</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Current Statistics */}
                <div className="space-y-2">
                  <Label>Estatísticas Atuais</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 border rounded-lg text-center">
                      <div className="text-2xl font-bold text-red-600">{lineCleanupStats.total_empty_lines}</div>
                      <div className="text-xs text-muted-foreground mt-1">Total Vazias</div>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-600">{lineCleanupStats.empty_username}</div>
                      <div className="text-xs text-muted-foreground mt-1">Username Vazio</div>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-600">{lineCleanupStats.empty_password}</div>
                      <div className="text-xs text-muted-foreground mt-1">Password Vazio</div>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <div className="text-2xl font-bold text-red-700">{lineCleanupStats.empty_both}</div>
                      <div className="text-xs text-muted-foreground mt-1">Ambos Vazios</div>
                    </div>
                  </div>
                </div>

                {/* Manual Execution */}
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900">Executar Limpeza Manual</h4>
                    <p className="text-sm text-blue-700">
                      Remove linhas vazias imediatamente sem esperar o agendamento
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleManualCleanup}
                    disabled={executingCleanup || lineCleanupStats.total_empty_lines === 0}
                  >
                    {executingCleanup ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Executando...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Executar Agora
                      </>
                    )}
                  </Button>
                </div>

                {/* Execution History */}
                {lineCleanupSettings.last_run && (
                  <div className="space-y-2">
                    <Label>Última Execução</Label>
                    <div className="p-4 border rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Data e Hora:</span>
                        <span className="text-sm text-muted-foreground">
                          {lineCleanupSettings.last_run.toLocaleString('pt-BR')}
                        </span>
                      </div>
                      {lineCleanupSettings.last_run_result && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Status:</span>
                            <span className={`text-sm font-medium ${
                              lineCleanupSettings.last_run_result.success ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {lineCleanupSettings.last_run_result.success ? 'Sucesso' : 'Falha'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Linhas Removidas:</span>
                            <span className="text-sm font-bold text-red-600">
                              {lineCleanupSettings.last_run_result.removed_count || 0}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-2">
                            {lineCleanupSettings.last_run_result.message}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Total Statistics */}
                <div className="space-y-2">
                  <Label>Estatísticas Totais</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="text-xl font-bold">{lineCleanupSettings.stats.total_runs}</div>
                      <div className="text-sm text-muted-foreground">Total de Execuções</div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="text-xl font-bold text-red-600">{lineCleanupSettings.stats.total_removed}</div>
                      <div className="text-sm text-muted-foreground">Total de Linhas Removidas</div>
                    </div>
                  </div>
                </div>

                {/* Refresh Button */}
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={fetchLineCleanupSettings}
                    disabled={loadingLineCleanup}
                  >
                    {loadingLineCleanup ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Atualizando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Atualizar Dados
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      </div>


      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg" variant="secondary">
          {saving ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Salvar Configurações
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Settings;
